import PaginatedReportTable from "./PaginatedReportTable";
import ReportExclusionFilters, { restoreExclusions } from "./ReportExclusionFilters";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus,
  PieChart,
  LineChart,
  Table,
  Filter,
  Pencil,
  Copy,
  Trash2,
  Download,
  Settings,
  Mail,
  GripVertical,
  ArrowUp,
  ArrowDown,
  ChevronsUpDown,
  ChevronDown,
  RefreshCw,
} from "lucide-react";
import { FaTimes } from "react-icons/fa";
import { Responsive, useContainerWidth } from "react-grid-layout";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toPng } from "html-to-image";
import server from "../../../api/server.js";
import ModernTimeDropdown from "../../layouts/modernDropdown.jsx";
import CustomerAdvancedFiltersPanel from "../../layouts/CustomerAdvancedFiltersPanel.jsx";
import { useToast } from "../../../context/ToastContext.jsx";
import { PieChart2 } from "../../layouts/ChartLayout.jsx";
import CustomerRecords from "../customers/CustomerRecords.jsx";
import ConversationRecords from "../calls/ConversationRecords.jsx";
import CallAdvancedFiltersPanel from "../../layouts/CallAdvancedFiltersPanel.jsx";
import ModernDateRangePicker from "../../layouts/ModernDateRangePicker.jsx";
import { reportCountField, conversationReportFilters, getConversationReportDefaults,
  conversationReportApiFilters, conversationReportDrilldownFilters, conversationReportFiltersFromPayload,
  isConversationCountDrilldown, isAgentFeedbackDrilldownScore,
  preserveConversationReportQuickFilters } from "./customReportLevel";
import {
  CONVERSATION_RECORD_TYPE_OPTIONS,
  CONVERSATION_TIME_FILTER_OPTIONS,
  mergeConversationFilterDetails,
} from "../../../utils/conversationFilterModel";
import {
  buildAdditionalTagFilterConditions,
  buildDashboardFilterPayload,
  extractLastInteractionForDrilldown,
  extractFirstInteractionForDrilldown,
  extractCrmLeadCreatedAtForDrilldown,
} from "../../../utils/filterPayloadBuilder";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import RestrictedSqlMeasuresEditor from "./RestrictedSqlMeasuresEditor.jsx";
import MeasureDisplayModeControls from "./MeasureDisplayModeControls.jsx";
import MultidimensionalPivotTable from "./MultidimensionalPivotTable.jsx";
import PeriodComparisonSettings from "./PeriodComparisonSettings.jsx";
import PeriodComparisonTable from "./PeriodComparisonTable.jsx";
import PivotShelvesEditor from "./PivotShelvesEditor.jsx";
import TimeSeriesSettings from "./TimeSeriesSettings.jsx";
import TimeSeriesVisualization from "./TimeSeriesVisualization.jsx";
import {
  buildRestrictedSqlFieldOptions,
  createRestrictedSqlMeasure,
  extractRestrictedSqlApiErrors,
  formatRestrictedSqlValue,
  getRestrictedSqlErrorMessage,
  hasRestrictedSqlMeasures,
  MAX_RESTRICTED_SQL_GROUPING_PARAMETERS,
  getGroupingParameterLimit,
  normalizeLogicalFieldName,
  normalizeRestrictedSqlMeasures,
  validateRestrictedSqlMeasures,
} from "./customReportMeasures.js";
import {
  buildDefaultPivotConfig,
  ensureTimeSeriesRangeFilter,
  getDefaultPeriodComparisonConfig,
  getPeriodComparisonAxes,
  getPivotConfig,
  isLegacyPeriodComparisonConfig,
  isTimeSeriesReportLevel,
  normalizePeriodComparisonConfig,
  normalizeTimeSeriesConfig,
  MAX_PERIOD_COMPARISON_DIMENSIONS,
  movePeriodComparisonPair,
  PERIOD_COMPARISON_TABLE_VARIANT,
  reconcilePivotConfig,
  validatePeriodComparisonVisualization,
  validatePivotConfig,
  validateTimeSeriesVisualization,
} from "./customReportVisualizationConfig.js";
import {
  DISPLAY_VALUES,
  PERCENTAGE_OF_COLUMN,
  PERCENTAGE_OF_ROW,
  formatDisplayedMeasure,
  getAxisTotal,
  getDisplayModeSettings,
  getDisplayNumericValue,
  getGrandTotal,
  getLegacyStandardAggregationSemantics,
  hasPercentageEligibleMeasure,
  mergeMeasureColumnMetadata,
  sumRawMeasure,
} from "./customReportValueDisplay.js";

const getDefaultAdvancedFilters = (reportLevel = "customers") => reportLevel === "calls" ? getConversationReportDefaults() : ({
  customer_id: "",
  employee_id: "",
  team_name: "",
  firstinteraction: "",
  firstinteraction_start: "",
  firstinteraction_end: "",
  lastinteraction: "Last 7 Days",
  lastinteraction_start: "",
  lastinteraction_end: "",
  refusalCategory: "",
  customerPotential: "",
  customer_outcome: "",
  openConcerns: "",
  id: "",
  interactions: "",
  concernCategory: "",
  concernSubcategory: "",
  queryCategory: "",
  querySubcategory: "",
  business_insights_multiple: [],
  disposition: "",
  subdisposition: "",
  lead_source: "",
  tag: "",
  tag2: "",
  tag3: "",
  tag4: "",
  tag5: "",
  tag6: "",
  tag7: "",
  tag8: "",
  tag9: "",
  tag10: "",
  tag11: "",
  tag12: "",
  tag13: "",
  tag14: "",
  tag15: "",
  tag16: "",
  tag17: "",
  tag18: "",
  tag19: "",
  tag20: "",
  tag21: "",
  tag22: "",
  tag23: "",
  tag24: "",
  tag25: "",
  tag26: "",
  tag27: "",
  tag28: "",
  tag29: "",
  tag30: "",
  campaign_id: "",
  customer_phone_number: "",
  crm_location: "",
  lead_sub_source: "",
  crm_lead_stage: "",
  crm_lead_sub_stage: "",
  crm_lead_created_at: "",
  crm_lead_created_at_start: "",
  crm_lead_created_at_end: "",
  custom_module_parameter_name: "",
  custom_module_parameter_answer: "",
  custom_module_multiple: [],
  primary_language: '',
});

const BASE_PARAMETER_OPTIONS = [
  { label: "Lead Stage", value: "crm_lead_stage", type: "categorical_single" },
  { label: "Lead Sub Stage", value: "crm_lead_sub_stage", type: "categorical_single" },
  { label: "Lead Source", value: "lead_source", type: "categorical_single" },
  { label: "Tag", value: "tag", type: "categorical_single" },
  { label: "Interactions", value: "interactions", type: "number" },
];
const METADATA_BASE_PARAMETER_OPTIONS = [
  { label: "Customer ID", value: "customer_id", type: "categorical_single", reportLevels: ["customers", "calls"] },
  { label: "Conversation ID", value: "conversation_id", type: "categorical_single", reportLevels: ["calls"] },
  { label: "Campaign Batch ID", value: "campaign_batch_id", type: "categorical_single", reportLevels: ["calls"] },
  { label: "Conversation Type", value: "conversation_type", type: "categorical_single", reportLevels: ["calls"] },
  { label: "Conversation Type Reason", value: "conversation_type_reason", type: "categorical_single", reportLevels: ["calls"] },
  { label: "Conversation Direction", value: "conversation_direction", type: "categorical_single", reportLevels: ["calls"] },
  { label: "Actor Type", value: "actor_type", type: "categorical_single", reportLevels: ["calls"] },
  { label: "Record Type", value: "record_type", type: "categorical_single", reportLevels: ["calls"] },
  { label: "CRM Lead Stage", value: "crm_lead_stage", type: "categorical_single" },
  { label: "CRM Lead Sub Stage", value: "crm_lead_sub_stage", type: "categorical_single" },
  { label: "Tag", value: "tag", type: "categorical_single" },
  { label: "Tag 2", value: "tag2", type: "categorical_single" },
  { label: "Tag 3", value: "tag3", type: "categorical_single" },
  { label: "Tag 4", value: "tag4", type: "categorical_single" },
  { label: "Tag 5", value: "tag5", type: "categorical_single" },
  { label: "Tag 6", value: "tag6", type: "categorical_single" },
  { label: "Tag 7", value: "tag7", type: "categorical_single" },
  { label: "Tag 8", value: "tag8", type: "categorical_single" },
  { label: "Tag 9", value: "tag9", type: "categorical_single" },
  { label: "Tag 10", value: "tag10", type: "categorical_single" },
  { label: "Tag 11", value: "tag11", type: "categorical_single" },
  { label: "Tag 12", value: "tag12", type: "categorical_single" },
  { label: "Tag 13", value: "tag13", type: "categorical_single" },
  { label: "Tag 14", value: "tag14", type: "categorical_single" },
  { label: "Tag 15", value: "tag15", type: "categorical_single" },
  { label: "Tag 16", value: "tag16", type: "categorical_single" },
  { label: "Tag 17", value: "tag17", type: "categorical_single" },
  { label: "Tag 18", value: "tag18", type: "categorical_single" },
  { label: "Tag 19", value: "tag19", type: "categorical_single" },
  { label: "Tag 20", value: "tag20", type: "categorical_single" },
  { label: "Tag 21", value: "tag21", type: "categorical_single" },
  { label: "Tag 22", value: "tag22", type: "categorical_single" },
  { label: "Tag 23", value: "tag23", type: "categorical_single" },
  { label: "Tag 24", value: "tag24", type: "categorical_single" },
  { label: "Tag 25", value: "tag25", type: "categorical_single" },
  { label: "Tag 26", value: "tag26", type: "categorical_single" },
  { label: "Tag 27", value: "tag27", type: "categorical_single" },
  { label: "Tag 28", value: "tag28", type: "categorical_single" },
  { label: "Tag 29", value: "tag29", type: "categorical_single" },
  { label: "Tag 30", value: "tag30", type: "categorical_single" },
  { label: "Lead Source", value: "lead_source", type: "categorical_single" },
  { label: "Lead Sub Source", value: "lead_sub_source", type: "categorical_single" },
  { label: "Employee ID", value: "employee_id", type: "categorical_single" },
  { label: "Campaign ID", value: "campaign_id", type: "categorical_single" },
  { label: "Primary Language", value: "primary_language", type: "categorical_single" },
  { label: "Secondary Languages", value: "secondary_languages", type: "categorical_single" },
  { label: "Interactions", value: "interactions", type: "number", reportLevels: ["customers"] },
  { label: "CRM Lead Created At", value: "crm_lead_created_at", type: "datetime", reportLevels: ["customers"] },
  { label: "First Interaction Time", value: "first_interaction_time", type: "datetime", reportLevels: ["customers"] },
  { label: "Last Interaction Time", value: "last_interaction_time", type: "datetime", reportLevels: ["customers"] },
  { label: "CRM Follow-up Time", value: "crm_followup_time", type: "datetime", reportLevels: ["customers"] },
  { label: "Customer Phone Number", value: "customer_phone_number", type: "categorical_single" },
  { label: "CRM Location", value: "crm_location", type: "categorical_single" },
];
const LEAD_QUALITY_BASE_PARAMETER_OPTIONS = [
  { label: "Customer Potential", value: "customerPotential", type: "categorical_single" },
];

/** Persisted `parameters` keys (API-friendly) → internal baseModule */
const PARAMETERS_KEY_TO_MODULE = {
  agent_feedback: "agent_feedback",
  metadata: "metadata",
  lead_quality: "lead_quality",
  business_insights: "business_insight",
  custom_module: "custom_module",
};
const PARAMETERS_OBJECT_KEYS = {
  agent_feedback: "agent_feedback",
  metadata: "metadata",
  lead_quality: "lead_quality",
  business_insight: "business_insights",
  custom_module: "custom_module",
};
const PARAMETERS_BUCKET_ORDER = ["metadata", "lead_quality", "business_insights", "custom_module", "agent_feedback"];

const orderedPairsToParametersObject = (pairs) => {
  const bucket = { metadata: [], lead_quality: [], business_insights: [], custom_module: [], agent_feedback: [] };
  (pairs || []).forEach(({ module, value }) => {
    const k = PARAMETERS_OBJECT_KEYS[module];
    if (k && value) bucket[k].push(value);
  });
  return Object.fromEntries(Object.entries(bucket).filter(([, arr]) => arr.length > 0));
};

const parametersObjectToOrderedPairs = (params) => {
  if (!params || typeof params !== "object") return [];
  const out = [];
  PARAMETERS_BUCKET_ORDER.forEach((storageKey) => {
    const module = PARAMETERS_KEY_TO_MODULE[storageKey];
    const arr = params[storageKey];
    if (!module || !Array.isArray(arr)) return;
    arr.forEach((v) => {
      const s = v === undefined || v === null ? "" : String(v).trim();
      if (s !== "") out.push({ module, value: s });
    });
  });
  return out;
};

const visualizationToOrderedPairs = (viz) => {
  const explicit = viz?.orderedParameterPairs ?? viz?.ordered_parameter_pairs;
  if (Array.isArray(explicit) && explicit.length > 0) {
    const cleaned = explicit
      .map((p) => {
        if (!p || !p.module) return null;
        const value = p.value === undefined || p.value === null ? "" : String(p.value).trim();
        if (!value) return null;
        return { module: p.module, value };
      })
      .filter(Boolean);
    if (cleaned.length) return cleaned;
  }
  if (viz?.parameters && typeof viz.parameters === "object") {
    const fromObj = parametersObjectToOrderedPairs(viz.parameters);
    if (fromObj.length) return fromObj;
  }
  const m = viz?.baseModule || viz?.base_module || "metadata";
  const raw = viz?.baseParameters !== undefined ? viz?.baseParameters : viz?.base_parameters;
  if (m === "metadata" || m === "lead_quality") {
    const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
    return list.filter((x) => x !== undefined && x !== null && String(x).trim() !== "").map((val) => ({
      module: m,
      value: String(val),
    }));
  }
  const arr = Array.isArray(raw) ? raw : raw ? [raw] : [];
  return arr
    .filter((x) => x !== undefined && x !== null && String(x).trim() !== "")
    .map((val) => ({ module: m, value: String(val) }));
};

const deriveLegacyBaseFieldsFromPairs = (pairs) => {
  if (pairs.length === 0) {
    return { baseModule: "metadata", baseMode: "single", baseParameters: "" };
  }
  const modules = [...new Set(pairs.map((p) => p.module))];
  const firstMod = pairs[0].module;
  if (modules.length === 1) {
    const vals = pairs.map((p) => p.value);
    const isMs = firstMod === "metadata" || firstMod === "lead_quality";
    if (vals.length === 1) {
      return {
        baseModule: firstMod,
        baseMode: isMs ? "single" : "multi",
        baseParameters: vals[0],
      };
    }
    return {
      baseModule: firstMod,
      baseMode: "multi",
      baseParameters: vals,
    };
  }
  return {
    baseModule: firstMod,
    baseMode: "multi",
    baseParameters: pairs.map((p) => p.value),
  };
};

const derivePersistBaseParameters = (pairs) => {
  if (pairs.length === 0) return "";
  const same = pairs.every((p) => p.module === pairs[0].module);
  if (!same) return pairs.map((p) => p.value);
  const m = pairs[0].module;
  if (m === "metadata" || m === "lead_quality") {
    return pairs.length === 1 ? pairs[0].value : pairs.map((p) => p.value);
  }
  return pairs.map((p) => p.value);
};

const inferModuleForParameterKey = (key, businessInsightKeys = [], customModuleKeys = []) => {
  const k = String(key || "");
  if (METADATA_BASE_PARAMETER_OPTIONS.some((x) => x.value === k)) return "metadata";
  if (LEAD_QUALITY_BASE_PARAMETER_OPTIONS.some((x) => x.value === k)) return "lead_quality";
  if (businessInsightKeys.some((x) => String(x) === k)) return "business_insight";
  if (customModuleKeys.some((x) => String(x) === k)) return "custom_module";
  return "metadata";
};

const normalizeDrilldownModule = (module) =>
  module === "business_insights" ? "business_insight" : module;

export const routeNamespacedReportDrilldownFilters = (items = [], reportLevel = "customers") => {
  const agentFeedback = [];
  const businessInsights = [];
  const customModules = [];
  const flatFilters = [];
  (Array.isArray(items) ? items : []).forEach((item) => {
    const module = normalizeDrilldownModule(item?.module);
    if (module === "business_insight") {
      businessInsights.push({
        business_insights_parameter: item.key,
        business_insights_parameter_value: item.value,
      });
    } else if (module === "custom_module") {
      customModules.push({
        custom_module_parameter_name: item.key,
        custom_module_parameter_answer: item.value,
      });
    } else if (module === "agent_feedback" && reportLevel === "calls") {
      const score = Number(item.value);
      if (isAgentFeedbackDrilldownScore(item.value)) {
        agentFeedback.push({ agent_feedback_parameter: item.key,
          agent_feedback_parameter_score_lower: score, agent_feedback_parameter_score_upper: score });
      }
    } else if (module === "lead_quality") {
      flatFilters.push({ key: reportLevel === "calls" ? "conversationPotential" : "customerPotential", value: item.value });
    } else {
      flatFilters.push({ key: item.key, value: item.value });
    }
  });
  return { businessInsights, customModules, flatFilters, agentFeedback };
};

/** For table headers when API sends only parameter ids (e.g. patient_city). */
const resolveParameterKeyToDisplayLabel = (parameterKey) => {
  const k = String(parameterKey || "").trim();
  if (!k) return null;
  const meta = METADATA_BASE_PARAMETER_OPTIONS.find((o) => o.value === k);
  if (meta) return meta.label;
  const lq = LEAD_QUALITY_BASE_PARAMETER_OPTIONS.find((o) => o.value === k);
  if (lq) return lq.label;
  const base = BASE_PARAMETER_OPTIONS.find((o) => o.value === k);
  if (base) return base.label;
  return null;
};

/**
 * Execution API should declare which report parameter each dimension slot (`col_1`, `col_4`, …) uses:
 * - Per column: `parameter_key` | `parameterKey` | `field` | `binding`
 * - Or on `data`: `dimension_parameter_keys` | `dimensionParameterKeys` — same length and order as non-count entries in `columns`
 * Pie charts: same `dimension_parameter_keys` (single segment = one entry) for drilldown.
 * If absent, falls back to visualization ordered pairs (legacy).
 */
const resolveDimensionParameterIds = (vizData, dimensionColumns, fallbackIds) =>
  dimensionColumns.map((column, index) => {
    const fromCol =
      column?.parameter_key ??
      column?.parameterKey ??
      column?.field ??
      column?.binding;
    if (fromCol !== undefined && fromCol !== null && String(fromCol).trim() !== "") {
      return String(fromCol).trim();
    }
    const data = vizData || {};
    const list = data.dimension_parameter_keys ?? data.dimensionParameterKeys;
    if (
      Array.isArray(list) &&
      list.length === dimensionColumns.length &&
      list[index] !== undefined &&
      list[index] !== null &&
      String(list[index]).trim() !== ""
    ) {
      return String(list[index]).trim();
    }
    return fallbackIds[index] ?? column?.key;
  });

const PIVOT_TABLE_VARIANT = "pivot_table";

export const usesMultidimensionalPivotRenderer = (visualization) =>
  Boolean(
    visualization?.pivotConfig &&
      typeof visualization.pivotConfig === "object" &&
      !Array.isArray(visualization.pivotConfig)
  );

const getVisualizationTypeForPairCount = (pairCount) => (pairCount > 1 ? "table" : "pie_chart");

export const getCustomReportVisualizationOptions = (pairCount, reportLevel) => {
  const standardType = getVisualizationTypeForPairCount(pairCount);
  return [
    { value: standardType, label: standardType === "table" ? "Table" : "Pie Chart" },
    ...(pairCount >= 2 && pairCount <= MAX_RESTRICTED_SQL_GROUPING_PARAMETERS
      ? [{ value: PIVOT_TABLE_VARIANT, label: "Pivot Table" }]
      : []),
    ...(isTimeSeriesReportLevel(reportLevel)
      ? [{ value: "time_series", label: "Time Series" }]
      : []),
    { value: PERIOD_COMPARISON_TABLE_VARIANT, label: "Period Comparison Table" },
  ];
};

const getEffectiveVisualizationType = (viz = {}) => {
  if (
    viz?.visualizationType === "table" &&
    [PIVOT_TABLE_VARIANT, PERIOD_COMPARISON_TABLE_VARIANT].includes(viz?.variant)
  ) {
    return viz.variant;
  }
  return viz?.visualizationType;
};

const PIE_SAMPLE = [
  { label: "Hot", value: 7, color: "#6366f1" },
  { label: "Warm", value: 5, color: "#06b6d4" },
  { label: "Cold", value: 3, color: "#f59e0b" },
];
const PIE_COLORS = ["#6366f1", "#06b6d4", "#f59e0b", "#22c55e", "#a855f7", "#ef4444", "#14b8a6", "#f97316"];

const GRID_COLUMNS = 3;
const GRID_UNIT = 4;
const DEFAULT_LAYOUT_HEIGHT = 16;
const HEADER_LAYOUT_HEIGHT = 4;
const HEADER_MIN_H = 2;
const HEADER_MAX_H = 48;
const LAYOUT_BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
const LAYOUT_COLS = { lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 };
const ResponsiveReactGridLayout = Responsive;

const getDefaultLayout = (indexLikePosition) => {
  const normalized = Number(indexLikePosition) >= 0 ? Number(indexLikePosition) : 0;
  return {
    x: (normalized % GRID_COLUMNS) * GRID_UNIT,
    y: Math.floor(normalized / GRID_COLUMNS) * DEFAULT_LAYOUT_HEIGHT,
    w: GRID_UNIT,
    h: DEFAULT_LAYOUT_HEIGHT,
    minW: 1,
    minH: 4,
  };
};

const getHeaderLayout = (indexLikePosition = 0, existingY = null, existingH = null) => {
  const fallback = getDefaultLayout(indexLikePosition);
  const h =
    Number.isFinite(Number(existingH)) && Number(existingH) > 0
      ? Math.min(HEADER_MAX_H, Math.max(HEADER_MIN_H, Number(existingH)))
      : HEADER_LAYOUT_HEIGHT;
  return {
    x: 0,
    y: Number.isFinite(Number(existingY)) && Number(existingY) >= 0 ? Number(existingY) : fallback.y,
    w: LAYOUT_COLS.lg,
    h,
    minW: LAYOUT_COLS.lg,
    maxW: LAYOUT_COLS.lg,
    minH: HEADER_MIN_H,
    maxH: HEADER_MAX_H,
    resizeHandles: ["s"],
  };
};

/** Execution API nests saved fields under `config`; flatten for shared normalization/helpers. */
const unwrapVisualizationForUi = (viz = {}) => {
  const nested =
    viz?.config && typeof viz.config === "object" && !Array.isArray(viz.config) ? viz.config : null;
  if (!nested) return { ...viz };
  return {
    ...nested,
    ...viz,
    id: viz.id ?? nested.id,
    title: viz.title ?? nested.title,
    visualizationType: viz.visualizationType ?? nested.visualizationType,
    variant: viz.variant !== undefined ? viz.variant : nested.variant,
    data: viz.data ?? nested.data,
    drilldown: viz.drilldown ?? nested.drilldown,
    layout: viz.layout ?? nested.layout,
    gridRow: viz.gridRow ?? nested.gridRow,
    gridColumn: viz.gridColumn ?? nested.gridColumn,
    orderedParameterPairs:
      viz.orderedParameterPairs ??
      viz.ordered_parameter_pairs ??
      nested.orderedParameterPairs ??
      nested.ordered_parameter_pairs,
    parameters: viz.parameters ?? nested.parameters,
    advancedFilters: viz.advancedFilters ?? nested.advancedFilters,
    advancedFiltersPayload: viz.advancedFiltersPayload ?? nested.advancedFiltersPayload,
    baseModule: viz.baseModule ?? viz.base_module ?? nested.baseModule ?? nested.base_module,
    baseParameters:
      viz.baseParameters ?? viz.base_parameters ?? nested.baseParameters ?? nested.base_parameters,
    aggregationParameter: viz.aggregationParameter ?? nested.aggregationParameter,
    aggregationOperator: viz.aggregationOperator ?? nested.aggregationOperator,
    measures: viz.measures ?? nested.measures,
    displayMeasureId: viz.displayMeasureId ?? viz.display_measure_id ?? nested.displayMeasureId ?? nested.display_measure_id,
    timeSeries: viz.timeSeries ?? viz.time_series ?? nested.timeSeries ?? nested.time_series,
    pivotConfig: viz.pivotConfig ?? viz.pivot_config ?? nested.pivotConfig ?? nested.pivot_config,
    periodComparison:
      viz.periodComparison ??
      viz.period_comparison ??
      nested.periodComparison ??
      nested.period_comparison,
  };
};

const normalizeVisualizationLayout = (viz = {}, index = 0) => {
  if (viz?.visualizationType === "header") {
    const source = viz?.layout || {};
    const y = Number(source?.y);
    const h = Number(source?.h);
    return getHeaderLayout(
      index,
      Number.isFinite(y) && y >= 0 ? y : undefined,
      Number.isFinite(h) && h > 0 ? h : undefined
    );
  }
  const fallback = getDefaultLayout(index);
  const legacyRow = Number(viz?.gridRow);
  const legacyColumn = Number(viz?.gridColumn);
  const legacyFallback =
    Number.isFinite(legacyRow) && legacyRow > 0 && Number.isFinite(legacyColumn) && legacyColumn > 0
      ? {
          x: (legacyColumn - 1) * GRID_UNIT,
          y: (legacyRow - 1) * DEFAULT_LAYOUT_HEIGHT,
          w: GRID_UNIT,
          h: DEFAULT_LAYOUT_HEIGHT,
          minW: 1,
          minH: 4,
        }
      : null;
  const source = viz?.layout || legacyFallback || fallback;
  const x = Number(source?.x);
  const y = Number(source?.y);
  const w = Number(source?.w);
  const h = Number(source?.h);
  return {
    x: Number.isFinite(x) && x >= 0 ? x : fallback.x,
    y: Number.isFinite(y) && y >= 0 ? y : fallback.y,
    w: Number.isFinite(w) && w > 0 ? Math.min(w, LAYOUT_COLS.lg) : fallback.w,
    h: Number.isFinite(h) && h > 0 ? h : fallback.h,
    minW: fallback.minW,
    minH: fallback.minH,
  };
};

const normalizeVisualizationsWithLayout = (visualizations = [], reportLevel = "customers") =>
  (Array.isArray(visualizations) ? visualizations : []).map((rawViz, index) => {
    const viz = unwrapVisualizationForUi(rawViz);
    const isPeriodComparison =
      getEffectiveVisualizationType(viz) === PERIOD_COMPARISON_TABLE_VARIANT;
    const normalizedPeriodComparison = isPeriodComparison
      ? normalizePeriodComparisonConfig(viz?.periodComparison, reportLevel)
      : null;
    const isLegacyPeriodComparison =
      isPeriodComparison && isLegacyPeriodComparisonConfig(normalizedPeriodComparison);
    const pairs = isLegacyPeriodComparison ? [] : visualizationToOrderedPairs(viz);
    const derived = deriveLegacyBaseFieldsFromPairs(pairs);
    const isSingleSelectBase =
      pairs.length === 1 &&
      (pairs[0].module === "metadata" || pairs[0].module === "lead_quality");
    const reconstructedAdvancedFilters = reportLevel === "calls"
      ? conversationReportFiltersFromPayload(viz.advancedFiltersPayload)
      : reconstructAdvancedFiltersFromPayload(viz);
    const normalizedAdvancedFilters = {
      ...getDefaultAdvancedFilters(reportLevel),
      ...reconstructedAdvancedFilters,
      reportExclusions: restoreExclusions(viz.advancedFiltersPayload),
      ...(viz?.advancedFilters || {}),
    };
    const normalizedTimeSeries =
      viz?.visualizationType === "time_series" ? normalizeTimeSeriesConfig(viz?.timeSeries) : null;
    const hasPersistedLastInteraction =
      Object.prototype.hasOwnProperty.call(viz?.advancedFilters || {}, "lastinteraction") ||
      Object.prototype.hasOwnProperty.call(reconstructedAdvancedFilters, "lastinteraction");
    if (isPeriodComparison && !hasPersistedLastInteraction) {
      normalizedAdvancedFilters.lastinteraction = "";
    }
    if (
      normalizedTimeSeries &&
      normalizedTimeSeries.field !== "last_interaction_time" &&
      !hasPersistedLastInteraction
    ) {
      normalizedAdvancedFilters.lastinteraction = "";
    }
    if (
      reportLevel !== "calls" &&
      !normalizedAdvancedFilters.lastinteraction &&
      !normalizedPeriodComparison &&
      (!normalizedTimeSeries || normalizedTimeSeries.field === "last_interaction_time")
    ) {
      normalizedAdvancedFilters.lastinteraction =
        normalizedAdvancedFilters.last_interaction_time || "Last 7 Days";
    }
    if (!normalizedAdvancedFilters.lastinteraction_start && normalizedAdvancedFilters.last_interaction_time_start) {
      normalizedAdvancedFilters.lastinteraction_start = normalizedAdvancedFilters.last_interaction_time_start;
    }
    if (!normalizedAdvancedFilters.lastinteraction_end && normalizedAdvancedFilters.last_interaction_time_end) {
      normalizedAdvancedFilters.lastinteraction_end = normalizedAdvancedFilters.last_interaction_time_end;
    }
    if (!normalizedAdvancedFilters.firstinteraction && normalizedAdvancedFilters.first_interaction_time) {
      normalizedAdvancedFilters.firstinteraction = normalizedAdvancedFilters.first_interaction_time;
    }
    if (!normalizedAdvancedFilters.firstinteraction_start && normalizedAdvancedFilters.first_interaction_time_start) {
      normalizedAdvancedFilters.firstinteraction_start = normalizedAdvancedFilters.first_interaction_time_start;
    }
    if (!normalizedAdvancedFilters.firstinteraction_end && normalizedAdvancedFilters.first_interaction_time_end) {
      normalizedAdvancedFilters.firstinteraction_end = normalizedAdvancedFilters.first_interaction_time_end;
    }
    const parameters =
      isLegacyPeriodComparison
        ? {}
        : pairs.length > 0
        ? orderedPairsToParametersObject(pairs)
        : viz?.parameters && typeof viz.parameters === "object"
          ? viz.parameters
          : {};
    return {
      ...viz,
      ...(hasRestrictedSqlMeasures(viz)
        ? {
            measures: normalizeRestrictedSqlMeasures(viz.measures),
            displayMeasureId:
              viz.displayMeasureId || normalizeRestrictedSqlMeasures(viz.measures)[0]?.id,
          }
        : {}),
      ...(normalizedTimeSeries ? { timeSeries: normalizedTimeSeries } : {}),
      ...(normalizedPeriodComparison ? { periodComparison: normalizedPeriodComparison } : {}),
      ...(isPeriodComparison
        ? {
            pivotConfig: undefined,
            timeSeries: undefined,
          }
        : {}),
      ...(isLegacyPeriodComparison
        ? {
            orderedParameterPairs: [],
            measures: undefined,
            displayMeasureId: undefined,
          }
        : {}),
      parameters,
      baseModule: derived.baseModule,
      baseMode: isSingleSelectBase ? "single" : "multi",
      baseParameters: derived.baseParameters,
      advancedFilters: reportLevel === "calls" ? conversationReportFilters(normalizedAdvancedFilters) : normalizedAdvancedFilters,
      layout: normalizeVisualizationLayout(viz, index),
    };
  });

const applyLayoutToVisualizations = (visualizations = [], layoutItems = [], reportLevel = "customers") => {
  const layoutById = new Map(
    (Array.isArray(layoutItems) ? layoutItems : []).map((item) => [String(item?.i), item])
  );
  return normalizeVisualizationsWithLayout(visualizations, reportLevel).map((viz, index) => {
    const matched = layoutById.get(String(viz.id));
    if (!matched) return { ...viz, layout: normalizeVisualizationLayout(viz, index) };
    return {
      ...viz,
      layout: normalizeVisualizationLayout(
        {
          ...viz,
          layout: {
            ...viz.layout,
            x: matched.x,
            y: matched.y,
            w: matched.w,
            h: matched.h,
          },
        },
        index
      ),
    };
  });
};

const toComparableLayoutItems = (layoutItems = []) =>
  (Array.isArray(layoutItems) ? layoutItems : [])
    .map((item) => ({
      i: String(item?.i ?? ""),
      x: Number(item?.x) || 0,
      y: Number(item?.y) || 0,
      w: Math.max(1, Number(item?.w) || 1),
      h: Math.max(1, Number(item?.h) || 1),
    }))
    .sort((a, b) => String(a.i).localeCompare(String(b.i)));

const areLayoutItemsEqual = (a = [], b = []) => {
  const left = toComparableLayoutItems(a);
  const right = toComparableLayoutItems(b);
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    const l = left[index];
    const r = right[index];
    if (l.i !== r.i || l.x !== r.x || l.y !== r.y || l.w !== r.w || l.h !== r.h) return false;
  }
  return true;
};

const buildReportsPathWithId = (pathname = "", targetReportId = "") => {
  const safeId = encodeURIComponent(String(targetReportId || ""));
  if (!safeId) return pathname;
  if (/\/custom-reports\/[^/]+$/.test(pathname)) {
    return pathname.replace(/\/custom-reports\/[^/]+$/, `/custom-reports/${safeId}`);
  }
  if (/\/custom-reports$/.test(pathname)) {
    return `${pathname}/${safeId}`;
  }
  return pathname;
};

const isEmptyValue = (value) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

const normalizeFieldKey = (value) =>
  String(value || "")
    .trim()
    .replace(/([A-Z])/g, "_$1")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();

const buildModuleParameterFilters = (advancedFilters = {}) => {
  const moduleFilters = [...buildAdditionalTagFilterConditions(advancedFilters)];

  const pushFilter = (condition) => {
    if (!condition || isEmptyValue(condition.field) || isEmptyValue(condition.value)) return;
    const value = condition.value;
    if (["business_insight", "custom_module"].includes(condition.module) &&
        value && typeof value === "object" && !Array.isArray(value) &&
        ["lower", "upper", "start", "end"].some((key) => key in value)) {
      const lower = value.lower ?? value.start;
      const upper = value.upper ?? value.end;
      condition = { ...condition, operator: "between", value: {
        lower: isEmptyValue(lower) ? null : Number(lower),
        upper: isEmptyValue(upper) ? null : Number(upper),
      } };
    }
    moduleFilters.push(condition);
  };

  const businessList = Array.isArray(advancedFilters.business_insights_multiple)
    ? advancedFilters.business_insights_multiple
    : [];
  businessList.forEach((item) => {
    const field = normalizeFieldKey(
      item?.business_insights_parameter || item?.businessInsightParameter || item?.field
    );
    const value =
      item?.business_insights_parameter_value ??
      item?.businessInsightValue ??
      item?.value;
    if (!field || isEmptyValue(value)) return;
    pushFilter({
      field,
      operator: Array.isArray(value) ? "in" : "eq",
      value,
      module: "business_insight",
    });
  });

  const customList = Array.isArray(advancedFilters.custom_module_multiple)
    ? advancedFilters.custom_module_multiple
    : [];
  customList.forEach((item) => {
    const field = normalizeFieldKey(item?.custom_module_parameter_name || item?.field);
    const value = item?.custom_module_parameter_answer ?? item?.value;
    if (!field || isEmptyValue(value)) return;
    pushFilter({
      field,
      operator: Array.isArray(value) ? "in" : "eq",
      value,
      module: "custom_module",
    });
  });

  const agentFeedbackList = Array.isArray(advancedFilters.agent_feedback_multiple)
    ? advancedFilters.agent_feedback_multiple
    : [];
  agentFeedbackList.forEach((item) => {
    const field = normalizeFieldKey(item?.agent_feedback_parameter || item?.field);
    const lower = item?.agent_feedback_parameter_score_lower ?? item?.lower;
    const upper = item?.agent_feedback_parameter_score_upper ?? item?.upper;
    if (!field || (isEmptyValue(lower) && isEmptyValue(upper))) return;
    pushFilter({
      field,
      operator: "between",
      value: {
        lower: isEmptyValue(lower) ? 0 : Number(lower),
        upper: isEmptyValue(upper) ? 100 : Number(upper),
      },
      module: "agent_feedback",
    });
  });

  if (
    !isEmptyValue(advancedFilters.custom_module_parameter_name) &&
    !isEmptyValue(advancedFilters.custom_module_parameter_answer)
  ) {
    pushFilter({
      field: normalizeFieldKey(advancedFilters.custom_module_parameter_name),
      operator: Array.isArray(advancedFilters.custom_module_parameter_answer) ? "in" : "eq",
      value: advancedFilters.custom_module_parameter_answer,
      module: "custom_module",
    });
  }

  if (
    !isEmptyValue(advancedFilters.businessInsightParameter) &&
    !isEmptyValue(advancedFilters.businessInsightValue)
  ) {
    pushFilter({
      field: normalizeFieldKey(advancedFilters.businessInsightParameter),
      operator: Array.isArray(advancedFilters.businessInsightValue) ? "in" : "eq",
      value: advancedFilters.businessInsightValue,
      module: "business_insight",
    });
  }

  return moduleFilters;
};

/** Shapes consumed by buildModuleParameterFilters — omit from flat filter pass-through. */
const REPORT_ADVANCED_FILTER_OMIT = [
  "reportExclusions",
  "business_insights_multiple",
  "custom_module_multiple",
  "agent_feedback_multiple",
  "businessInsightParameter",
  "businessInsightValue",
  "custom_module_parameter_name",
  "custom_module_parameter_answer",
];

/** Drop empty values so saved report JSON only carries meaningful filter keys. */
const pruneAdvancedFiltersForPersistence = (filters = {}) => {
  const out = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (key === "reportExclusions") return;
    if (value === undefined || value === null) return;
    if (value === "") return;
    if (Array.isArray(value) && value.length === 0) return;
    out[key] = value;
  });
  return out;
};

/**
 * Older configs only stored `advancedFiltersPayload`. Recover last-interaction UI fields
 * when `advancedFilters` is missing or partial.
 */
const reconstructAdvancedFiltersFromPayload = (viz = {}) => {
  const payload = unwrapVisualizationForUi(viz)?.advancedFiltersPayload;
  if (!payload || typeof payload !== "object") return {};
  const next = { reportExclusions: restoreExclusions(payload) };
  const timeInfo = extractLastInteractionForDrilldown(payload);
  if (timeInfo) {
    next.lastinteraction = timeInfo.lastinteraction;
    if (timeInfo.lastinteraction === "Custom Range") {
      next.lastinteraction_start = timeInfo.lastInteractionStart || "";
      next.lastinteraction_end = timeInfo.lastInteractionEnd || "";
      if (timeInfo.lastinteraction_range_type) {
        next.lastinteraction_range_type = timeInfo.lastinteraction_range_type;
      }
    }
  }
  const firstInfo = extractFirstInteractionForDrilldown(payload);
  if (firstInfo) {
    next.firstinteraction = firstInfo.firstinteraction;
    if (firstInfo.firstinteraction === "Custom Range") {
      next.firstinteraction_start = firstInfo.firstInteractionStart || "";
      next.firstinteraction_end = firstInfo.firstInteractionEnd || "";
      if (firstInfo.firstinteraction_range_type) {
        next.firstinteraction_range_type = firstInfo.firstinteraction_range_type;
      }
    }
  }
  const crmInfo = extractCrmLeadCreatedAtForDrilldown(payload);
  if (crmInfo) {
    next.crm_lead_created_at = crmInfo.crm_lead_created_at;
    if (crmInfo.crm_lead_created_at === "Custom Range") {
      next.crm_lead_created_at_start = crmInfo.crm_lead_created_at_start || "";
      next.crm_lead_created_at_end = crmInfo.crm_lead_created_at_end || "";
      if (crmInfo.crm_lead_created_at_range_type) {
        next.crm_lead_created_at_range_type = crmInfo.crm_lead_created_at_range_type;
      }
    }
  }
  const and = Array.isArray(payload.filter?.and) ? payload.filter.and : [];
  const callTime = and.find((condition) => condition?.field === "call_time");
  if (callTime?.operator === "date_preset") next.call_time = callTime.value;
  if (callTime?.operator === "between") {
    next.call_time = "Custom Range";
    next.call_time_start = callTime.value?.start ?? callTime.value?.lower ?? "";
    next.call_time_end = callTime.value?.end ?? callTime.value?.upper ?? "";
    next.call_time_range_type = "between";
  }
  if (["after", "gte", "gt"].includes(callTime?.operator)) {
    next.call_time = "Custom Range";
    next.call_time_start = callTime.value;
    next.call_time_range_type = "after";
  }
  const employeeCond = and.find(
    (c) => c && ["eq", "in"].includes(c.operator) && c.field === "employee_id" && c.value !== undefined && c.value !== null && c.value !== ""
  );
  if (employeeCond && (next.employee_id === undefined || next.employee_id === null || next.employee_id === "")) {
    next.employee_id = employeeCond.value;
  }
  return next;
};

const buildAdvancedFiltersPayload = (advancedFilters = {}, { suppressDefaultLastInteraction = false, reportLevel = "customers" } = {}) => {
  const module_parameter_filters = buildModuleParameterFilters(advancedFilters);
  const payload = { ...advancedFilters };
  REPORT_ADVANCED_FILTER_OMIT.forEach((key) => {
    delete payload[key];
  });
  if (module_parameter_filters.length > 0) {
    payload.module_parameter_filters = module_parameter_filters;
  }
  if (!payload.lastinteraction && !suppressDefaultLastInteraction && reportLevel !== "calls") {
    payload.lastinteraction = "Last 7 Days";
  }
  const built = buildDashboardFilterPayload(reportLevel === "calls" ? conversationReportApiFilters(payload) : payload);
  const lastinteraction = advancedFilters.lastinteraction || payload.lastinteraction || "";
  const result = {
    pagination: built.pagination,
    filter: { ...built.filter, and: [...(built.filter?.and || []), ...(advancedFilters.reportExclusions || [])] },
  };
  if (lastinteraction && reportLevel !== "calls") result.lastinteraction = lastinteraction;
  return result;
};

const buildPersistableVisualizations = (visualizations = [], reportLevel = "customers") =>
  normalizeVisualizationsWithLayout(visualizations, reportLevel).map((viz, index) => {
    const {
      gridRow,
      gridColumn,
      advancedFilters,
      data: _data,
      measures: rawMeasures,
      displayMeasureId: rawDisplayMeasureId,
      periodComparison: rawPeriodComparison,
      period_comparison: _legacyPeriodComparison,
      ...restViz
    } = viz || {};
    const isPeriodComparison =
      getEffectiveVisualizationType(viz) === PERIOD_COMPARISON_TABLE_VARIANT;
    const normalizedPeriodComparison = isPeriodComparison
      ? normalizePeriodComparisonConfig(rawPeriodComparison, reportLevel)
      : null;
    const isLegacyPeriodComparison =
      isPeriodComparison && isLegacyPeriodComparisonConfig(normalizedPeriodComparison);
    const isGenericPeriodComparison = isPeriodComparison && !isLegacyPeriodComparison;
    const selectedPairs = isLegacyPeriodComparison ? [] : visualizationToOrderedPairs(viz);
    const filters = { ...(advancedFilters || {}) };
    if (isGenericPeriodComparison && reportLevel === "calls") {
      delete filters.call_time;
      delete filters.call_time_start;
      delete filters.call_time_end;
      delete filters.call_time_range_type;
    }
    const isPivotTable = getEffectiveVisualizationType(viz) === PIVOT_TABLE_VARIANT;
    const hasExplicitPivotConfig =
      isPivotTable && viz?.pivotConfig && typeof viz.pivotConfig === "object" && !Array.isArray(viz.pivotConfig);
    const normalizedPivotConfig = hasExplicitPivotConfig ? getPivotConfig(viz, selectedPairs) : null;
    const pairs = normalizedPivotConfig
      ? [...normalizedPivotConfig.rows, ...normalizedPivotConfig.columns]
      : selectedPairs;
    const baseModule = pairs[0]?.module || viz?.baseModule || "metadata";
    const normalizedTimeSeries =
      viz?.visualizationType === "time_series" ? normalizeTimeSeriesConfig(viz?.timeSeries) : null;
    const persistablePeriodComparison = normalizedPeriodComparison
      ? isLegacyPeriodComparison
        ? {
            ...normalizedPeriodComparison,
            areas: normalizedPeriodComparison.areas.map((area) => ({
              ...area,
              label: area.label.trim(),
            })),
          }
        : normalizedPeriodComparison
      : null;
    const suppressDefaultLastInteraction =
      isPeriodComparison ||
      (normalizedTimeSeries && normalizedTimeSeries.field !== "last_interaction_time");
    const restrictedSqlMeasures = normalizeRestrictedSqlMeasures(rawMeasures);
    const usesRestrictedSql = restrictedSqlMeasures.length > 0;
    const isBoundedStandardVariant =
      !usesRestrictedSql &&
      Boolean(normalizedTimeSeries || normalizedPivotConfig || isGenericPeriodComparison);
    const displayMeasureId = restrictedSqlMeasures.some(
      (measure) => String(measure.id) === String(rawDisplayMeasureId)
    )
      ? rawDisplayMeasureId
      : restrictedSqlMeasures[0]?.id;
    return {
      ...restViz,
      ...(usesRestrictedSql ? { measures: restrictedSqlMeasures, displayMeasureId } : {}),
      ...(normalizedTimeSeries ? { timeSeries: normalizedTimeSeries } : {}),
      ...(persistablePeriodComparison
        ? { periodComparison: persistablePeriodComparison }
        : {}),
      ...(normalizedPivotConfig
        ? { pivotConfig: { rows: normalizedPivotConfig.rows, columns: normalizedPivotConfig.columns } }
        : {}),
      ...(isLegacyPeriodComparison || (isGenericPeriodComparison && usesRestrictedSql)
        ? { aggregationParameter: undefined, aggregationOperator: undefined }
        : isBoundedStandardVariant
        ? { aggregationParameter: reportCountField(reportLevel), aggregationOperator: "count" }
        : {}),
      visualizationType:
        isPivotTable || isPeriodComparison ? "table" : restViz.visualizationType,
      variant: isPivotTable
        ? PIVOT_TABLE_VARIANT
        : isPeriodComparison
          ? PERIOD_COMPARISON_TABLE_VARIANT
          : undefined,
      orderedParameterPairs: pairs.map((p) => ({ module: p.module, value: p.value })),
      parameters: orderedPairsToParametersObject(pairs),
      base_module: baseModule,
      base_parameters: derivePersistBaseParameters(pairs),
      layout: normalizeVisualizationLayout(viz, index),
      advancedFilters: pruneAdvancedFiltersForPersistence(filters),
      advancedFiltersPayload: buildAdvancedFiltersPayload(filters, { suppressDefaultLastInteraction, reportLevel }),
    };
  });

const getDefaultVisualization = (id, index = 0, reportLevel = "customers") => ({
  id,
  title: `Visualization ${id}`,
  advancedFilters: getDefaultAdvancedFilters(reportLevel),
  parameters: {},
  baseModule: "metadata",
  baseMode: "single",
  baseParameters: "",
  aggregationParameter: reportLevel === "calls" ? "id" : "",
  aggregationOperator: "count",
  visualizationType: "pie_chart",
  layout: getDefaultLayout(index),
});

const REPORTS_MENU = "custom-reports";
const REPORTS_SUBMENU = "reports";
const REPORT_PROCESSING_MESSAGE = "Report is currently processing. Please refresh in sometime.";

const humanizeFilterName = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const formatFilterSummaryValue = (value) => {
  if (Array.isArray(value)) {
    const values = value
      .filter((item) => item !== null && item !== undefined && item !== "")
      .map(String);
    if (values.length <= 2) return values.join(", ");
    return `${values.slice(0, 2).join(", ")} +${values.length - 2} more`;
  }
  if (value && typeof value === "object") {
    const lower = value.lower ?? value.start;
    const upper = value.upper ?? value.end;
    if (lower !== undefined || upper !== undefined) {
      return [lower, upper]
        .filter((item) => item !== undefined && item !== null && item !== "")
        .join(" to ");
    }
    return JSON.stringify(value);
  }
  return String(value ?? "");
};

const parseConfiguration = (rawConfig = {}) => {
  const config =
    typeof rawConfig === "string"
      ? (() => {
          try {
            return JSON.parse(rawConfig);
          } catch (error) {
            return {};
          }
        })()
      : rawConfig || {};

  const name = config.name || config.reportName || config.title || "Untitled Report";
  const reportLevel = config.reportLevel || "customers";
  const visualizations = normalizeVisualizationsWithLayout(config.visualizations || [], reportLevel);

  return { name, reportLevel, visualizations };
};

const toReportListItem = (item = {}, index = 0) => {
  const configuration = parseConfiguration(item.report_configuration);
  const reportId = item.report_id || `report-${index + 1}`;
  const persistedOrder = Number(item.display_order);
  return {
    id: reportId,
    report_id: reportId,
    order:
      Number.isInteger(persistedOrder) && persistedOrder > 0
        ? persistedOrder
        : index + 1,
    name: configuration.name,
    reportLevel: configuration.reportLevel,
    visualizations: configuration.visualizations,
  };
};

const ReportsDashboard = ({ serviceID, clientID }) => {
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const { reportId } = useParams();
  const [email, setEmail] = useState(null);
  // Sidebar updates this cookie before changing the selected client.
  const reportUser = getCookie("dashboard_login_user_creds");
  const reportRole = reportUser ? JSON.parse(reportUser).role : "admin";
  const isEmployee = reportRole === "employee";
  const canManageReports = !isEmployee;
  const [service_id, setServiceId] = useState(null);
  const [client_id, setClientId] = useState(null);

  const [reports, setReports] = useState([]);
  const [executedReportsById, setExecutedReportsById] = useState({});
  const [activeReportId, setActiveReportId] = useState(null);
  const [reportDataLoading, setReportDataLoading] = useState(false);
  const [reportExecutionStates, setReportExecutionStates] = useState({});
  const [isSegmentModalOpen, setIsSegmentModalOpen] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [editingReportId, setEditingReportId] = useState(null);
  const [editingReportName, setEditingReportName] = useState("");

  const [showCreateReportModal, setShowCreateReportModal] = useState(false);
  const [showManageReportsModal, setShowManageReportsModal] = useState(false);
  const [managedReports, setManagedReports] = useState([]);
  const [draggedReportId, setDraggedReportId] = useState(null);
  const [isSavingReportOrder, setIsSavingReportOrder] = useState(false);
  const [reportPendingDelete, setReportPendingDelete] = useState(null);
  const [isDeletingReport, setIsDeletingReport] = useState(false);
  const [reportModalMode, setReportModalMode] = useState("create");
  const [editingReportConfigId, setEditingReportConfigId] = useState(null);
  const [activeVisualizationTabId, setActiveVisualizationTabId] = useState(null);
  const [filtersPanelVisualizationId, setFiltersPanelVisualizationId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [builderPreview, setBuilderPreview] = useState(null);
  const [restrictedSqlErrors, setRestrictedSqlErrors] = useState([]);
  const [serverFieldCatalog, setServerFieldCatalog] = useState([]);
  const [isHardRefreshing, setIsHardRefreshing] = useState(false);
  const fieldCatalogRequestRef = useRef(0);
  const previewGenerationRef = useRef(0);
  const previewRequestIdRef = useRef(0);
  const reportExecutionRequestsRef = useRef(new Map());
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [reportShareEmail, setReportShareEmail] = useState('');
  const [isSendingReportEmail, setIsSendingReportEmail] = useState(false);
  const [reportShareFrequency, setReportShareFrequency] = useState('');
  const [reportShareStatus, setReportShareStatus] = useState('');
  const [loadingEmailConfiguration, setLoadingEmailConfiguration] = useState(false);
  const [pivotDisplaySettings, setPivotDisplaySettings] = useState({});
  const [reportTableSortSettings, setReportTableSortSettings] = useState({});
  const [pivotSortSettings, setPivotSortSettings] = useState({});

  const [multiSelectFields, setMultiSelectFields] = useState([]);
  const [dummyAdvancedFilterData, setDummyAdvancedFilterData] = useState({
    refusal_category: [],
    open_concerns_and_queries: ["Yes", "No"],
    customer_outcome: [],
    customer_potential: [],
    business_insight_parameters: [],
    concern_categories: [],
    concern_subcategories: [],
    query_categories: [],
    query_subcategories: [],
    disposition: [],
    subdisposition: [],
    lead_sources: [],
    tags: [],
    tag2: [],
    tag3: [],
    tag4: [],
    tag5: [],
    tag6: [],
    tag7: [],
    tag8: [],
    tag9: [],
    tag10: [],
    tag11: [],
    tag12: [],
    tag13: [],
    tag14: [],
    tag15: [],
    tag16: [],
    tag17: [],
    tag18: [],
    tag19: [],
    tag20: [],
    tag21: [],
    tag22: [],
    tag23: [],
    tag24: [],
    tag25: [],
    tag26: [],
    tag27: [],
    tag28: [],
    tag29: [],
    tag30: [],
    lead_stages: [],
    lead_sub_stages: [],
    custom_module: [],
    primary_languages: [],
    secondary_languages: [],
    team_names: [],
    lead_sub_sources: [],
    campaign_ids: []
  });

  const [expandedSections, setExpandedSections] = useState({
    businessInsights: false,
    concernsAndQueries: false,
    dispositionAndSubdisposition: false,
    customModule: false,
  });
  const [formState, setFormState] = useState({
    reportName: "",
    reportLevel: "customers",
    visualizations: [getDefaultVisualization(1)],
  });

  const activeReport = useMemo(
    () => reports.find((item) => item.id === activeReportId) || reports[0] || null,
    [reports, activeReportId]
  );
  const activeExecutedReport = useMemo(
    () => (activeReportId ? executedReportsById[activeReportId] || null : null),
    [executedReportsById, activeReportId]
  );
  const activeReportExecutionState = activeReportId
    ? reportExecutionStates[activeReportId] || null
    : null;
  const isReportOrderDirty = useMemo(
    () =>
      reports.length !== managedReports.length ||
      managedReports.some(
        (report, index) => String(report.id) !== String(reports[index]?.id)
      ),
    [managedReports, reports]
  );
  const reportVisualizations = useMemo(() => {
    const configured = normalizeVisualizationsWithLayout(activeReport?.visualizations || [], activeReport?.reportLevel);
    const executed = Array.isArray(activeExecutedReport?.visualizations)
      ? activeExecutedReport.visualizations
      : [];
    if (!configured.length) {
      return normalizeVisualizationsWithLayout(
        executed.map((viz, index) => ({
          ...unwrapVisualizationForUi(viz),
          id: viz?.id ?? `viz-${index + 1}`,
        })), activeReport?.reportLevel
      ).map((viz, index) => ({
        ...viz,
        layout: normalizeVisualizationLayout(viz, index),
      }));
    }
    if (!executed.length) return configured;
    const executedById = new Map(
      executed
        .filter((viz) => viz?.id !== undefined && viz?.id !== null)
        .map((viz) => [String(viz.id), viz])
    );
    const mergedRaw = configured.map((viz, index) => {
      const executionData = executedById.get(String(viz.id)) || executed[index] || {};
      return unwrapVisualizationForUi({
        ...executionData,
        ...viz,
        id: viz.id,
        data: executionData.data ?? viz.data,
        drilldown: executionData.drilldown ?? viz.drilldown,
        advancedFiltersPayload:
          executionData.config?.advancedFiltersPayload ??
          executionData.advancedFiltersPayload ??
          viz.advancedFiltersPayload,
      });
    });
    return normalizeVisualizationsWithLayout(mergedRaw, activeReport?.reportLevel).map((viz, index) => ({
      ...viz,
      layout: normalizeVisualizationLayout(configured[index] || viz, index),
    }));
  }, [activeReport, activeExecutedReport]);
  const [reportLayouts, setReportLayouts] = useState({ lg: [] });
  const [pendingReportLayouts, setPendingReportLayouts] = useState({ lg: [] });
  const [isLayoutEditMode, setIsLayoutEditMode] = useState(false);
  const [currentBreakpoint, setCurrentBreakpoint] = useState("lg");
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
  const [tableDownloadMenuId, setTableDownloadMenuId] = useState(null);
  const settingsHideTimeoutRef = useRef(null);
  const downloadMenuHideTimeoutRef = useRef(null);
  const { width: reportGridWidth, containerRef: reportGridContainerRef, mounted: reportGridMounted } =
    useContainerWidth({ measureBeforeMount: false, initialWidth: 0.777*window.innerWidth });

  function getCookie(name) {
    function escape(value) {
      return value.replace(/([.*+?\^$(){}|\[\]\/\\])/g, "\\$1");
    }
    const match = document.cookie.match(RegExp(`(?:^|;\\s*)${escape(name)}=([^;]*)`));
    return match ? match[1] : null;
  }

  const fetchFilterDetails = async (effectiveClientId, effectiveServiceId, effectiveEmail, reportLevel, signal) => {
    try {
      const getFilters = reportLevel === "calls" ? server.getAdvancedFilterDetailsAgentFeedback : server.getCustomerFilters;
      const response = await getFilters(
        effectiveClientId,
        effectiveServiceId,
        effectiveEmail,
        "home",
        undefined,
        signal
      );
      if (response.status !== 200 || signal?.aborted) return;
      const data = await response.json();
      if (signal?.aborted) return;
      setMultiSelectFields(Array.isArray(data.multiselects_list) ? data.multiselects_list : []);
      setDummyAdvancedFilterData((prev) => reportLevel === "calls"
        ? mergeConversationFilterDetails(prev, data)
        : ({
        ...prev,
        employees: data.employee_id || [],
        team_names: data.team_names || data.team_name || data.teams || [],
        customer_outcome: data.customer_outcome || [],
        refusal_category: data.refusal_category || data.refusal_category_list || [],
        customer_potential: data.customer_potential || [],
        conversation_potential: data.conversation_potential || [],
        conversation_type_reason: data.conversation_type_reason || [],
        conversation_outcome: data.conversation_outcome_list || [],
        agent_feedback_parameters: data.agent_feedback_parameters || [],
        business_insights_parameters: data.business_insights || [],
        campaign_batch_ids: data.campaign_batch_ids || [],
        business_insight_parameters: data.business_insights || [],
        concern_categories: data.concerns_category || [],
        concern_subcategories: data.concerns_subcategory || [],
        query_categories: data.query_category || [],
        query_subcategories: data.query_subcategory || [],
        disposition: data.disposition || [],
        subdisposition: data.subdisposition || [],
        lead_sources: data.lead_sources || [],
        lead_sub_sources: data.lead_sub_sources || [],
        tags: data.tags || [],
        tag2: data.tags2 || data.tag2 || data.tags || [],
        tag3: data.tags3 || data.tag3 || data.tags || [],
        tag4: data.tags4 || data.tag4 || data.tags || [],
        tag5: data.tags5 || data.tag5 || data.tags || [],
        tag6: data.tags6 || data.tag6 || data.tags || [],
        tag7: data.tags7 || data.tag7 || data.tags || [],
        tag8: data.tags8 || data.tag8 || data.tags || [],
        tag9: data.tags9 || data.tag9 || data.tags || [],
        tag10: data.tags10 || data.tag10 || data.tags || [],
        tag11: data.tags11 || data.tag11 || data.tags || [],
        tag12: data.tags12 || data.tag12 || data.tags || [],
        tag13: data.tags13 || data.tag13 || data.tags || [],
        tag14: data.tags14 || data.tag14 || data.tags || [],
        tag15: data.tags15 || data.tag15 || data.tags || [],
        tag16: data.tags16 || data.tag16 || data.tags || [],
        tag17: data.tags17 || data.tag17 || data.tags || [],
        tag18: data.tags18 || data.tag18 || data.tags || [],
        tag19: data.tags19 || data.tag19 || data.tags || [],
        tag20: data.tags20 || data.tag20 || data.tags || [],
        tag21: data.tags21 || data.tag21 || data.tags || [],
        tag22: data.tags22 || data.tag22 || data.tags || [],
        tag23: data.tags23 || data.tag23 || data.tags || [],
        tag24: data.tags24 || data.tag24 || data.tags || [],
        tag25: data.tags25 || data.tag25 || data.tags || [],
        tag26: data.tags26 || data.tag26 || data.tags || [],
        tag27: data.tags27 || data.tag27 || data.tags || [],
        tag28: data.tags28 || data.tag28 || data.tags || [],
        tag29: data.tags29 || data.tag29 || data.tags || [],
        tag30: data.tags30 || data.tag30 || data.tags || [],
        lead_stages: data.lead_stages || [],
        lead_sub_stages: data.lead_sub_stages || [],
        campaign_ids: data.campaign_ids || [],
        custom_module: data.custom_module || [],
        primary_languages: data.primary_languages || data.primary_language || [],
        secondary_languages: data.secondary_languages || []
      }));
    } catch (error) {
      if (!signal?.aborted) showToast("Unable to load filters for report builder", "error");
    }
  };

  const loadCustomReportsList = async (effectiveClientId, effectiveServiceId, effectiveEmail) => {
    try {
      setReportDataLoading(true);
      const response = await server.get_custom_reports_list(
        effectiveClientId,
        effectiveServiceId,
        effectiveEmail,
        REPORTS_MENU,
        REPORTS_SUBMENU
      );
      if (response.status !== 200) {
        showToast("Unable to load custom reports", "error");
        return;
      }
      const payload = await response.json();
      const nextReports = Array.isArray(payload?.data)
        ? payload.data.map((item, index) => toReportListItem(item, index))
        : [];
      setReports(nextReports);
      setExecutedReportsById((prev) => {
        const next = {};
        nextReports.forEach((item) => {
          if (prev[item.id]) next[item.id] = prev[item.id];
        });
        return next;
      });
      setActiveReportId((prev) => {
        if (nextReports.length === 0) return null;
        if (prev && nextReports.some((item) => String(item.id) === String(prev))) return prev;
        if (reportId && nextReports.some((item) => String(item.id) === String(reportId))) {
          return reportId;
        }
        return nextReports[0].id;
      });
      return nextReports;
    } catch (error) {
      showToast("Unable to load custom reports", "error");
      return null;
    } finally {
      setReportDataLoading(false);
    }
  };

  const loadCustomReportDetails = async (effectiveReportId) => {
    if (!effectiveReportId || !client_id || !service_id || !email) return;
    try {
      const response = await server.get_custom_report_details(
        client_id,
        service_id,
        email,
        effectiveReportId,
        REPORTS_MENU,
        REPORTS_SUBMENU
      );
      if (response.status !== 200) return;
      const payload = await response.json();
      const parsed = parseConfiguration(payload?.data || {});
      setReports((prev) =>
        prev.map((item) =>
          item.id === effectiveReportId
            ? {
                ...item,
                name: parsed.name || item.name,
                reportLevel: parsed.reportLevel || item.reportLevel,
                visualizations: parsed.visualizations,
              }
            : item
        )
      );
    } catch (error) {
      showToast("Unable to load selected report details", "error");
    }
  };

  const triggerReportExecution = async (effectiveReportId, isRefresh = false) => {
    if (!effectiveReportId || !client_id || !service_id) return false;
    const requestKey = String(effectiveReportId);
    const existingRequest = reportExecutionRequestsRef.current.get(requestKey);
    if (existingRequest) return existingRequest;

    const executionPromise = (async () => {
      setReportExecutionStates((prev) => ({
        ...prev,
        [effectiveReportId]: {
          status: isRefresh ? "refreshing" : "loading",
          message: isRefresh ? "Refreshing report..." : "Loading report...",
        },
      }));
      try {
      const response = await server.custom_report_execution({
        client_id,
        service_id,
        report_id: effectiveReportId,
        menu: REPORTS_MENU,
        submenu: REPORTS_SUBMENU,
        email: email,
        is_refresh: isRefresh
      });
      if (response.status !== 200) {
        let errorPayload = {};
        try {
          errorPayload = await response.json();
        } catch (error) {
          errorPayload = {};
        }
        const processingErrors = new Set([
          "execution_in_progress",
          "preview_in_progress",
          "refresh_in_progress",
          "report_capacity_busy",
        ]);
        if (processingErrors.has(errorPayload?.error)) {
          const message = REPORT_PROCESSING_MESSAGE;
          setReportExecutionStates((prev) => ({
            ...prev,
            [effectiveReportId]: { status: "processing", message },
          }));
          showToast(message, "info");
          return false;
        }
        const message = getRestrictedSqlErrorMessage(errorPayload, "Unable to execute report");
        setReportExecutionStates((prev) => ({
          ...prev,
          [effectiveReportId]: { status: "error", message },
        }));
        showToast(message, "error");
        return false;
      }
      const payload = await response.json();
      const result = payload?.result;
      if (!result || !Array.isArray(result.visualizations)) {
        setReportExecutionStates((prev) => ({
          ...prev,
          [effectiveReportId]: {
            status: "error",
            message: "Report execution returned an invalid response.",
          },
        }));
        return false;
      }
      setExecutedReportsById((prev) => ({
        ...prev,
        [effectiveReportId]: result,
      }));
      setReportExecutionStates((prev) => ({
        ...prev,
        [effectiveReportId]: { status: "ready", message: "" },
      }));
      return true;
    } catch (error) {
      setReportExecutionStates((prev) => ({
        ...prev,
        [effectiveReportId]: { status: "error", message: "Unable to execute report" },
      }));
      showToast("Unable to execute report", "error");
      return false;
      } finally {
        reportExecutionRequestsRef.current.delete(requestKey);
      }
    })();
    reportExecutionRequestsRef.current.set(requestKey, executionPromise);
    return executionPromise;
  };

  useEffect(() => {
    const userCookie = getCookie("dashboard_login_user_creds")
      ? JSON.parse(getCookie("dashboard_login_user_creds"))
      : null;
    if (userCookie?.email) setEmail(userCookie.email);
  }, []);

  useEffect(() => {
    if (serviceID) setServiceId(serviceID);
    if (clientID) setClientId(clientID);
    // Do not carry an editor or drilldown from the previous access scope.
    setShowCreateReportModal(false);
    setShowManageReportsModal(false);
    setShowEmailModal(false);
    setReportPendingDelete(null);
    setFiltersPanelVisualizationId(null);
    setIsLayoutEditMode(false);
    setIsSegmentModalOpen(false);
    setSelectedSegment(null);
  }, [serviceID, clientID]);

  useEffect(() => {
    setServerFieldCatalog([]);
  }, [client_id, service_id]);

  useEffect(() => {
    if (client_id && service_id && email) {
      loadCustomReportsList(client_id, service_id, email);

    }
  }, [client_id, service_id, email]);

  const filterReportLevel = showCreateReportModal ? formState.reportLevel : (activeReport?.reportLevel || "customers");
  const ReportFiltersPanel = formState.reportLevel === "calls" ? CallAdvancedFiltersPanel : CustomerAdvancedFiltersPanel;
  const ReportRecords = activeReport?.reportLevel === "calls" ? ConversationRecords : CustomerRecords;
  useEffect(() => {
    const controller = new AbortController();
    fieldCatalogRequestRef.current += 1;
    setServerFieldCatalog([]);
    setDummyAdvancedFilterData((prev) => ({ ...prev,
      business_insight_parameters: [], business_insights_parameters: [], custom_module: [],
      agent_feedback_parameters: [],
    }));
    if (client_id && service_id && email) {
      fetchFilterDetails(client_id, service_id, email, filterReportLevel, controller.signal);
      if (showCreateReportModal) {
        loadRestrictedSqlFieldCatalog(filterReportLevel);
      }
    }
    return () => controller.abort();
  }, [client_id, service_id, email, filterReportLevel, showCreateReportModal]);

  useEffect(() => {
    if (activeReportId && client_id && service_id && email) {
      loadCustomReportDetails(activeReportId);
      triggerReportExecution(activeReportId);
    }
  }, [activeReportId, client_id, service_id, email]);

  useEffect(() => {
    if (!reportId || !reports.length) return;
    const urlReportExists = reports.some((item) => String(item.id) === String(reportId));
    if (!urlReportExists) return;
    const activeExists = activeReportId
      ? reports.some((item) => String(item.id) === String(activeReportId))
      : false;
    if (!activeExists) {
      setActiveReportId(reportId);
    }
  }, [reportId, reports, activeReportId]);

  useEffect(() => {
    if (!activeReportId) return;
    const nextPath = buildReportsPathWithId(location.pathname, activeReportId);
    if (nextPath !== location.pathname) {
      navigate(`${nextPath}${location.search || ""}`, { replace: true });
    }
  }, [activeReportId, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!showCreateReportModal) return;
    const onEsc = (event) => {
      if (event.key === "Escape") {
        setShowCreateReportModal(false);
        setFiltersPanelVisualizationId(null);
        setEditingReportConfigId(null);
      }
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [showCreateReportModal]);

  useEffect(() => {
    return () => {
      if (settingsHideTimeoutRef.current) {
        clearTimeout(settingsHideTimeoutRef.current);
      }
      if (downloadMenuHideTimeoutRef.current) {
        clearTimeout(downloadMenuHideTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!showCreateReportModal) return;
    if (!formState.visualizations.length) {
      setActiveVisualizationTabId(null);
      return;
    }
    const hasActive = formState.visualizations.some((item) => item.id === activeVisualizationTabId);
    if (!hasActive) {
      setActiveVisualizationTabId(formState.visualizations[0].id);
    }
  }, [showCreateReportModal, formState.visualizations, activeVisualizationTabId]);

  const getBusinessInsightValues = (selectedParameter) => {
    if (!selectedParameter || !dummyAdvancedFilterData.business_insight_parameters) return [];
    if (
      typeof dummyAdvancedFilterData.business_insight_parameters === "object" &&
      !Array.isArray(dummyAdvancedFilterData.business_insight_parameters)
    ) {
      const keyFormat = selectedParameter.toLowerCase().replace(/ /g, "_");
      return dummyAdvancedFilterData.business_insight_parameters[keyFormat] || [];
    }
    if (Array.isArray(dummyAdvancedFilterData.business_insight_parameters)) {
      const selectedInsight = dummyAdvancedFilterData.business_insight_parameters.find(
        ([key]) => key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()) === selectedParameter
      );
      return selectedInsight ? selectedInsight[1] : [];
    }
    return [];
  };

  const getBusinessInsightKeys = () => {
    if (!dummyAdvancedFilterData.business_insight_parameters) return [];
    if (
      typeof dummyAdvancedFilterData.business_insight_parameters === "object" &&
      !Array.isArray(dummyAdvancedFilterData.business_insight_parameters)
    ) {
      return Object.keys(dummyAdvancedFilterData.business_insight_parameters).map((key) =>
        key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
      );
    }
    if (Array.isArray(dummyAdvancedFilterData.business_insight_parameters)) {
      return dummyAdvancedFilterData.business_insight_parameters.map(([key]) =>
        key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
      );
    }
    return [];
  };

  const getDispositionKeys = () => {
    if (
      !dummyAdvancedFilterData.subdisposition ||
      typeof dummyAdvancedFilterData.subdisposition !== "object"
    ) {
      return dummyAdvancedFilterData.disposition || [];
    }
    return Object.keys(dummyAdvancedFilterData.subdisposition);
  };

  const getSubdispositionsForDisposition = (selectedDisposition) => {
    if (!dummyAdvancedFilterData.subdisposition) return [];
    if (!selectedDisposition) {
      if (
        typeof dummyAdvancedFilterData.subdisposition === "object" &&
        !Array.isArray(dummyAdvancedFilterData.subdisposition)
      ) {
        return Object.values(dummyAdvancedFilterData.subdisposition).flat();
      }
      return dummyAdvancedFilterData.subdisposition || [];
    }
    if (
      typeof dummyAdvancedFilterData.subdisposition === "object" &&
      !Array.isArray(dummyAdvancedFilterData.subdisposition)
    ) {
      return dummyAdvancedFilterData.subdisposition[selectedDisposition] || [];
    }
    return dummyAdvancedFilterData.subdisposition || [];
  };

  const getConcernCategoryKeys = () => {
    if (
      !dummyAdvancedFilterData.concern_subcategories ||
      typeof dummyAdvancedFilterData.concern_subcategories !== "object"
    ) {
      return dummyAdvancedFilterData.concern_categories || [];
    }
    return Object.keys(dummyAdvancedFilterData.concern_subcategories);
  };

  const getConcernSubcategoriesForCategory = (selectedConcernCategory) => {
    if (!dummyAdvancedFilterData.concern_subcategories) return [];
    if (!selectedConcernCategory) {
      if (
        typeof dummyAdvancedFilterData.concern_subcategories === "object" &&
        !Array.isArray(dummyAdvancedFilterData.concern_subcategories)
      ) {
        return Object.values(dummyAdvancedFilterData.concern_subcategories).flat();
      }
      return dummyAdvancedFilterData.concern_subcategories || [];
    }
    if (
      typeof dummyAdvancedFilterData.concern_subcategories === "object" &&
      !Array.isArray(dummyAdvancedFilterData.concern_subcategories)
    ) {
      return dummyAdvancedFilterData.concern_subcategories[selectedConcernCategory] || [];
    }
    return dummyAdvancedFilterData.concern_subcategories || [];
  };

  const getQueryCategoryKeys = () => {
    if (
      !dummyAdvancedFilterData.query_subcategories ||
      typeof dummyAdvancedFilterData.query_subcategories !== "object"
    ) {
      return dummyAdvancedFilterData.query_categories || [];
    }
    return Object.keys(dummyAdvancedFilterData.query_subcategories);
  };

  const getQuerySubcategoriesForCategory = (selectedQueryCategory) => {
    if (!dummyAdvancedFilterData.query_subcategories) return [];
    if (!selectedQueryCategory) {
      if (
        typeof dummyAdvancedFilterData.query_subcategories === "object" &&
        !Array.isArray(dummyAdvancedFilterData.query_subcategories)
      ) {
        return Object.values(dummyAdvancedFilterData.query_subcategories).flat();
      }
      return dummyAdvancedFilterData.query_subcategories || [];
    }
    if (
      typeof dummyAdvancedFilterData.query_subcategories === "object" &&
      !Array.isArray(dummyAdvancedFilterData.query_subcategories)
    ) {
      return dummyAdvancedFilterData.query_subcategories[selectedQueryCategory] || [];
    }
    return dummyAdvancedFilterData.query_subcategories || [];
  };

  const getCustomModuleKeys = () => {
    if (!Array.isArray(dummyAdvancedFilterData.custom_module)) return [];
    return dummyAdvancedFilterData.custom_module.map(([key]) => key);
  };

  const getCustomModuleValues = (selectedParameter) => {
    if (!Array.isArray(dummyAdvancedFilterData.custom_module) || !selectedParameter) return [];
    const match = dummyAdvancedFilterData.custom_module.find(([key]) => key === selectedParameter);
    return match ? (Array.isArray(match[1]) ? match[1] : []) : [];
  };

  const getParameterType = (paramValue) => {
    const metadataType = METADATA_BASE_PARAMETER_OPTIONS.find((item) => item.value === paramValue)?.type;
    const fallbackType = BASE_PARAMETER_OPTIONS.find((item) => item.value === paramValue)?.type;
    return metadataType || fallbackType || "categorical_single";
  };

  const getAggregationOptions = (paramValue) =>
    getParameterType(paramValue) === "number" ? ["min", "max", "avg", "count"] : ["count"];

  const getParameterValues = (visualization) => {
    return visualizationToOrderedPairs(visualization).map((p) => p.value);
  };

  const reorderParameterPairsAt = (pairs, fromIndex, toIndex) => {
    if (!Array.isArray(pairs) || fromIndex === toIndex) return pairs;
    if (fromIndex < 0 || toIndex < 0 || fromIndex >= pairs.length || toIndex >= pairs.length) return pairs;
    const next = [...pairs];
    const [removed] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, removed);
    return next;
  };

  const getAppliedFilterCount = (advancedFilters) => {
    const filters = advancedFilters || {};
    const excluded = new Set([
      "business_insights_multiple",
      "custom_module_multiple",
      "custom_module_parameter_name",
      "custom_module_parameter_answer",
    ]);
    let count = 0;
    Object.entries(filters).forEach(([key, value]) => {
      if (excluded.has(key)) return;
      if (Array.isArray(value) && value.length > 0) count += 1;
      else if (value !== null && value !== undefined && value !== "") count += 1;
    });
    if (Array.isArray(filters.business_insights_multiple) && filters.business_insights_multiple.length > 0)
      count += filters.business_insights_multiple.length;
    if (Array.isArray(filters.custom_module_multiple) && filters.custom_module_multiple.length > 0)
      count += filters.custom_module_multiple.length;
    return count;
  };

  const invalidateBuilderPreview = () => {
    previewGenerationRef.current += 1;
    previewRequestIdRef.current += 1;
    setIsPreviewing(false);
    setBuilderPreview(null);
  };

  const updateVisualization = (visualizationId, patch) => {
    setRestrictedSqlErrors((prev) =>
      prev.filter(
        (error) =>
          error?.visualizationId !== null &&
          error?.visualizationId !== undefined &&
          String(error.visualizationId) !== String(visualizationId)
      )
    );
    invalidateBuilderPreview();
    setFormState((prev) => ({
      ...prev,
      visualizations: prev.visualizations.map((item) =>
        item.id === visualizationId ? { ...item, ...patch } : item
      ),
    }));
  };

  const updateAdvancedFilters = (visualizationId, nextValue) => {
    invalidateBuilderPreview();
    setFormState((prev) => ({
      ...prev,
      visualizations: prev.visualizations.map((item) =>
        item.id === visualizationId ? { ...item, advancedFilters: nextValue } : item
      ),
    }));
  };

  const updatePivotDisplaySetting = (visualizationId, patch) => {
    setPivotDisplaySettings((prev) => ({
      ...prev,
      [visualizationId]: {
        mode: DISPLAY_VALUES,
        percentageOf: PERCENTAGE_OF_COLUMN,
        ...(prev[visualizationId] || {}),
        ...patch,
      },
    }));
  };

  const addVisualization = () => {
    if(formState.visualizations.length >= 15) {
      showToast("You can only add up to 15 visualizations", "error");
      return;
    }
    const nextId = Date.now();
    const nextOrdinal = formState.visualizations.length + 1;
    const nextVisualization = {
      ...getDefaultVisualization(nextId, nextOrdinal - 1, formState.reportLevel),
      title: `Visualization ${nextOrdinal}`,
    };
    invalidateBuilderPreview();
    setFormState((prev) => ({
      ...prev,
      visualizations: normalizeVisualizationsWithLayout([...prev.visualizations, nextVisualization], prev.reportLevel),
    }));
    setActiveVisualizationTabId(nextId);
    showToast("New visualization section added", "success");
  };

  const removeVisualization = (visualizationId) => {
    invalidateBuilderPreview();
    setFormState((prev) => {
      if (prev.visualizations.length <= 1) {
        showToast("At least one visualization is required", "error");
        return prev;
      }
      const nextVisualizations = prev.visualizations.filter((item) => item.id !== visualizationId);
      const nextActive = nextVisualizations[0]?.id || null;
      setActiveVisualizationTabId(nextActive);
      return {
        ...prev,
        visualizations: normalizeVisualizationsWithLayout(nextVisualizations, prev.reportLevel),
      };
    });
    if (filtersPanelVisualizationId === visualizationId) {
      setFiltersPanelVisualizationId(null);
    }
  };

  const duplicateVisualization = (visualizationId) => {
    if (formState.visualizations.length >= 15) {
      showToast("You can only add up to 15 visualizations", "error");
      return;
    }
    const source = formState.visualizations.find((item) => item.id === visualizationId);
    if (!source) return;
    const nextId = Date.now();
    const sourceClone =
      typeof structuredClone === "function"
        ? structuredClone(source)
        : JSON.parse(JSON.stringify(source));
    const duplicatedVisualization = {
      ...sourceClone,
      id: nextId,
      title: `${source.title || "Visualization"} -- copy`,
      layout: getDefaultLayout(formState.visualizations.length),
    };
    invalidateBuilderPreview();
    setFormState((prev) => ({
      ...prev,
      visualizations: normalizeVisualizationsWithLayout([
        ...prev.visualizations,
        duplicatedVisualization,
      ], prev.reportLevel),
    }));
    setActiveVisualizationTabId(nextId);
    showToast("Visualization duplicated", "success");
  };

  const loadRestrictedSqlFieldCatalog = async (reportLevel = "customers") => {
    if (!client_id || !service_id || !email) return;
    const requestId = ++fieldCatalogRequestRef.current;
    try {
      const response = await server.custom_report_execution({
        client_id,
        service_id,
        email,
        menu: REPORTS_MENU,
        submenu: REPORTS_SUBMENU,
        catalog_only: true,
        preview: true,
        reportLevel,
      });
      if (response.status !== 200) return;
      const payload = await response.json();
      if (requestId !== fieldCatalogRequestRef.current) return;
      const catalog = Array.isArray(payload?.field_catalog) ? payload.field_catalog : [];
      setServerFieldCatalog(
        catalog
          .map((field) => ({
            value: String(field?.name || field?.logical_name || "").trim(),
            label: String(field?.label || field?.name || field?.logical_name || "").trim(),
            dataType: field?.dataType || field?.data_type || null,
          }))
          .filter((field) => field.value)
      );
    } catch (error) {
      // The backend remains authoritative at validation time; the editor keeps its safe fallback suggestions.
    }
  };

  const openCreateReportModal = () => {
    invalidateBuilderPreview();
    setReportModalMode("create");
    setEditingReportConfigId(null);
    setFiltersPanelVisualizationId(null);
    const visualizations = [getDefaultVisualization(1)];
    setFormState({
      reportName: "",
      reportLevel: "customers",
      visualizations: normalizeVisualizationsWithLayout(visualizations),
    });
    setActiveVisualizationTabId(visualizations[0].id);
    setRestrictedSqlErrors([]);
    setShowCreateReportModal(true);
  };

  const openEditReportModal = (reportId, focusVisualizationId = null) => {
    if (!reportId) return;
    const targetReport = reports.find((item) => item.id === reportId);
    if (!targetReport) return;
    const visualizations = normalizeVisualizationsWithLayout(targetReport.visualizations || [], targetReport.reportLevel);
    invalidateBuilderPreview();
    setReportModalMode("edit");
    setEditingReportConfigId(reportId);
    setFormState({
      reportName: targetReport.name || "",
      reportLevel: targetReport.reportLevel || "customers",
      visualizations,
    });
    setActiveVisualizationTabId(focusVisualizationId || visualizations[0]?.id || null);
    setFiltersPanelVisualizationId(null);
    setRestrictedSqlErrors([]);
    setShowCreateReportModal(true);
  };

  const closeReportModal = () => {
    invalidateBuilderPreview();
    setShowCreateReportModal(false);
    setFiltersPanelVisualizationId(null);
    setEditingReportConfigId(null);
    setActiveVisualizationTabId(null);
    setRestrictedSqlErrors([]);
  };

  const reindexManagedReports = (items) =>
    items.map((report, index) => ({ ...report, order: index + 1 }));

  const openManageReportsModal = () => {
    setIsSettingsMenuOpen(false);
    setManagedReports(reindexManagedReports(reports));
    setDraggedReportId(null);
    setReportPendingDelete(null);
    setShowManageReportsModal(true);
  };

  const moveManagedReport = (sourceIndex, targetIndex) => {
    if (
      isSavingReportOrder ||
      isDeletingReport ||
      sourceIndex === targetIndex ||
      sourceIndex < 0 ||
      targetIndex < 0 ||
      sourceIndex >= managedReports.length ||
      targetIndex >= managedReports.length
    ) {
      return;
    }
    setManagedReports((prev) => {
      const next = [...prev];
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, moved);
      return reindexManagedReports(next);
    });
  };

  const handleManagedReportDragOver = (event, targetReportId) => {
    event.preventDefault();
    if (!draggedReportId || String(draggedReportId) === String(targetReportId)) return;
    const sourceIndex = managedReports.findIndex(
      (report) => String(report.id) === String(draggedReportId)
    );
    const targetIndex = managedReports.findIndex(
      (report) => String(report.id) === String(targetReportId)
    );
    moveManagedReport(sourceIndex, targetIndex);
  };

  const handleSaveReportOrder = async () => {
    if (!isReportOrderDirty || isSavingReportOrder) return;
    setIsSavingReportOrder(true);
    try {
      const response = await server.reorder_custom_reports({
        client_id,
        service_id,
        email,
        menu: REPORTS_MENU,
        submenu: REPORTS_SUBMENU,
        report_ids: managedReports.map((report) => report.report_id || report.id),
      });
      if (response.status !== 200) {
        let payload = {};
        try {
          payload = await response.json();
        } catch (error) {
          payload = {};
        }
        if (response.status === 409) {
          const refreshedReports = await loadCustomReportsList(client_id, service_id, email);
          if (refreshedReports) setManagedReports(reindexManagedReports(refreshedReports));
        }
        showToast(payload?.message || "Unable to save the report order", "error");
        return;
      }
      const nextReports = reindexManagedReports(managedReports);
      setReports(nextReports);
      setManagedReports(nextReports);
      setShowManageReportsModal(false);
      showToast("Report order updated", "success");
    } catch (error) {
      showToast("Unable to save the report order", "error");
    } finally {
      setIsSavingReportOrder(false);
    }
  };

  const handleDeleteReport = async () => {
    if (!reportPendingDelete || isDeletingReport) return;
    setIsDeletingReport(true);
    const deletedReport = reportPendingDelete;
    const deletedIndex = reports.findIndex(
      (report) => String(report.id) === String(deletedReport.id)
    );
    try {
      const response = await server.delete_custom_report({
        client_id,
        service_id,
        email,
        menu: REPORTS_MENU,
        submenu: REPORTS_SUBMENU,
        report_id: deletedReport.report_id || deletedReport.id,
      });
      if (response.status !== 200) {
        let payload = {};
        try {
          payload = await response.json();
        } catch (error) {
          payload = {};
        }
        showToast(payload?.message || "Unable to delete the report", "error");
        return;
      }

      const remainingReports = reports.filter(
        (report) => String(report.id) !== String(deletedReport.id)
      );
      if (String(activeReportId) === String(deletedReport.id)) {
        const nextActiveReport =
          remainingReports[deletedIndex] ||
          remainingReports[deletedIndex - 1] ||
          remainingReports[0] ||
          null;
        setActiveReportId(nextActiveReport?.id || null);
        if (!nextActiveReport && /\/custom-reports\/[^/]+$/.test(location.pathname)) {
          const basePath = location.pathname.replace(
            /\/custom-reports\/[^/]+$/,
            "/custom-reports"
          );
          navigate(`${basePath}${location.search || ""}`, { replace: true });
        }
      }
      setExecutedReportsById((prev) => {
        const next = { ...prev };
        delete next[deletedReport.id];
        return next;
      });
      setReportExecutionStates((prev) => {
        const next = { ...prev };
        delete next[deletedReport.id];
        return next;
      });
      setSelectedSegment(null);
      setIsSegmentModalOpen(false);
      const refreshedReports = await loadCustomReportsList(client_id, service_id, email);
      setManagedReports(reindexManagedReports(refreshedReports || remainingReports));
      setReportPendingDelete(null);
      showToast("Report deleted", "success");
    } catch (error) {
      showToast("Unable to delete the report", "error");
    } finally {
      setIsDeletingReport(false);
    }
  };

  const handleVisualizationSegmentClick = (viz, segment) => {
    if (isEmployee) {
      showToast("Details are available through supported count cells. You can also download this report.", "info");
      return;
    }
    const segmentName = segment?.name ?? segment?.label ?? "";
    const configViz = activeReport?.visualizations?.find((item) => String(item.id) === String(viz?.id));
    const runtimeViz = reportVisualizations.find((item) => String(item.id) === String(viz?.id));
    const mergedViz = unwrapVisualizationForUi({ ...(configViz || {}), ...(runtimeViz || {}), ...viz });
    const orderedPairs = visualizationToOrderedPairs(mergedViz);
    const biKeys = getBusinessInsightKeys();
    const cmKeys = getCustomModuleKeys();
    const dataDimKeys = mergedViz?.data?.dimension_parameter_keys ?? mergedViz?.data?.dimensionParameterKeys;
    const primaryParamFromApi =
      Array.isArray(dataDimKeys) && dataDimKeys.length > 0 && String(dataDimKeys[0]).trim() !== ""
        ? String(dataDimKeys[0]).trim()
        : "";
    const baseParameter = primaryParamFromApi || orderedPairs[0]?.value || "";
    const baseModule =
      (primaryParamFromApi
        ? orderedPairs.find((pair) => pair.value === primaryParamFromApi)?.module || inferModuleForParameterKey(primaryParamFromApi, biKeys, cmKeys)
        : orderedPairs[0]?.module) ||
      viz?.baseModule ||
      viz?.base_module ||
      configViz?.baseModule ||
      configViz?.base_module ||
      "metadata";
    const segmentKey =
      (baseModule === "business_insight" ||
      baseModule === "custom_module" ||
      baseModule === "agent_feedback" ||
      baseModule === "lead_quality"
        ? baseParameter
        : segment?.id) ||
      viz?.drilldown?.segmentKey ||
      viz?.config?.aggregationParameter ||
      viz?.aggregationParameter ||
      baseParameter ||
      "crm_lead_stage";
    const filterApplied = { ...(mergedViz?.advancedFilters || {}) };
    const resolvedDashboardPayload =
      mergedViz?.advancedFiltersPayload && typeof mergedViz.advancedFiltersPayload === "object"
        ? mergedViz.advancedFiltersPayload
        : buildAdvancedFiltersPayload(mergedViz?.advancedFilters || {}, { reportLevel: activeReport?.reportLevel });

    const payloadForTime =
      extractLastInteractionForDrilldown(mergedViz?.advancedFiltersPayload) ||
      extractLastInteractionForDrilldown(resolvedDashboardPayload);
    if (payloadForTime) {
      filterApplied.lastinteraction = payloadForTime.lastinteraction;
      if (payloadForTime.lastinteraction === "Custom Range") {
        if (payloadForTime.lastInteractionStart) {
          filterApplied["Last Interaction Start"] = payloadForTime.lastInteractionStart;
          filterApplied.lastinteraction_start = payloadForTime.lastInteractionStart;
        }
        if (payloadForTime.lastInteractionEnd) {
          filterApplied["Last Interaction End"] = payloadForTime.lastInteractionEnd;
          filterApplied.lastinteraction_end = payloadForTime.lastInteractionEnd;
        }
        if (payloadForTime.lastinteraction_range_type) {
          filterApplied.lastinteraction_range_type = payloadForTime.lastinteraction_range_type;
        }
      }
    }

    const rawAdv = mergedViz?.advancedFilters || {};
    const payloadForFirst =
      extractFirstInteractionForDrilldown(mergedViz?.advancedFiltersPayload) ||
      extractFirstInteractionForDrilldown(resolvedDashboardPayload);
    if (payloadForFirst) {
      filterApplied.firstinteraction = payloadForFirst.firstinteraction;
      if (
        payloadForFirst.firstinteraction === "Custom Range" &&
        (payloadForFirst.firstInteractionStart || payloadForFirst.firstInteractionEnd)
      ) {
        filterApplied["First Interaction Start"] = payloadForFirst.firstInteractionStart || "";
        filterApplied["First Interaction End"] = payloadForFirst.firstInteractionEnd || "";
      }
    } else if (rawAdv.firstinteraction) {
      filterApplied.firstinteraction = rawAdv.firstinteraction;
      if (rawAdv.firstinteraction === "Custom Range") {
        filterApplied["First Interaction Start"] = rawAdv.firstinteraction_start || "";
        filterApplied["First Interaction End"] = rawAdv.firstinteraction_end || "";
      }
    }
    if (
      filterApplied.firstinteraction === "Custom Range" &&
      !(String(filterApplied["First Interaction Start"] || "").trim()) &&
      filterApplied.firstinteraction_start
    ) {
      filterApplied["First Interaction Start"] = filterApplied.firstinteraction_start;
      filterApplied["First Interaction End"] =
        filterApplied["First Interaction End"] || filterApplied.firstinteraction_end || "";
    }

    const payloadForCrm =
      extractCrmLeadCreatedAtForDrilldown(mergedViz?.advancedFiltersPayload) ||
      extractCrmLeadCreatedAtForDrilldown(resolvedDashboardPayload);
    if (payloadForCrm) {
      filterApplied.crm_lead_created_at = payloadForCrm.crm_lead_created_at;
      if (
        payloadForCrm.crm_lead_created_at === "Custom Range" &&
        (payloadForCrm.crm_lead_created_at_start || payloadForCrm.crm_lead_created_at_end)
      ) {
        filterApplied.crm_lead_created_at_start = payloadForCrm.crm_lead_created_at_start || "";
        filterApplied.crm_lead_created_at_end = payloadForCrm.crm_lead_created_at_end || "";
      }
    } else if (rawAdv.crm_lead_created_at) {
      filterApplied.crm_lead_created_at = rawAdv.crm_lead_created_at;
      if (rawAdv.crm_lead_created_at === "Custom Range") {
        filterApplied.crm_lead_created_at_start = rawAdv.crm_lead_created_at_start || "";
        filterApplied.crm_lead_created_at_end = rawAdv.crm_lead_created_at_end || "";
      }
    }
    const drilldownFilters = Array.isArray(segment?.filters)
      ? segment.filters
          .filter((item) => item?.key && item?.value !== undefined && item?.value !== null && item?.value !== "")
          .map((item) => ({
            module: item.module === "business_insights" ? "business_insight" : item.module,
            key: item.key,
            value: item.value,
          }))
      : [];

    const normalizeDrilldownParamKey = (s) =>
      String(s ?? "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_");
    /** Prefer visualization ordered pairs over inferModule — BI keys often fail string match against getBusinessInsightKeys() and were misrouted to flat metadata filters. */
    const moduleForDrilldownFilterKey = (filterKey) => {
      const k = String(filterKey ?? "").trim();
      if (!k) return inferModuleForParameterKey(k, biKeys, cmKeys);
      const exact = orderedPairs.find((p) => String(p.value ?? "").trim() === k);
      if (exact?.module) return exact.module;
      const nk = normalizeDrilldownParamKey(k);
      const loose = orderedPairs.find((p) => normalizeDrilldownParamKey(p.value) === nk);
      if (loose?.module) return loose.module;
      return inferModuleForParameterKey(k, biKeys, cmKeys);
    };

    const applyBusinessInsightPairs = (nextPairs) => {
      if (!nextPairs.length) return;
      const existing = Array.isArray(filterApplied.business_insights_multiple)
        ? filterApplied.business_insights_multiple.filter(
            (item) =>
              item &&
              item.business_insights_parameter &&
              String(item.business_insights_parameter).trim() !== "" &&
              !nextPairs.some((pair) => pair.business_insights_parameter === item.business_insights_parameter)
          )
        : [];
      filterApplied.business_insights_multiple = [...existing, ...nextPairs];
      filterApplied.businessInsightParameter =
        nextPairs[0]?.business_insights_parameter || filterApplied.businessInsightParameter;
      filterApplied.businessInsightValue =
        nextPairs[0]?.business_insights_parameter_value || filterApplied.businessInsightValue;
    };

    const applyCustomModulePairs = (nextPairs) => {
      if (!nextPairs.length) return;
      const existing = Array.isArray(filterApplied.custom_module_multiple)
        ? filterApplied.custom_module_multiple.filter(
            (item) =>
              item &&
              item.custom_module_parameter_name &&
              String(item.custom_module_parameter_name).trim() !== "" &&
              !nextPairs.some((pair) => pair.custom_module_parameter_name === item.custom_module_parameter_name)
          )
        : [];
      filterApplied.custom_module_multiple = [...existing, ...nextPairs];
      filterApplied.custom_module_parameter_name =
        nextPairs[0]?.custom_module_parameter_name || filterApplied.custom_module_parameter_name;
      filterApplied.custom_module_parameter_answer =
        nextPairs[0]?.custom_module_parameter_answer || filterApplied.custom_module_parameter_answer;
    };

    if (activeReport?.reportLevel === "calls") {
      Object.assign(filterApplied, conversationReportFiltersFromPayload(resolvedDashboardPayload));
    }
    if (drilldownFilters.length > 0) {
      const routed = routeNamespacedReportDrilldownFilters(
        drilldownFilters.map((item) => ({
          ...item,
          module: item.module || moduleForDrilldownFilterKey(item.key),
        })), activeReport?.reportLevel
      );
      routed.flatFilters.forEach((item) => {
        filterApplied[item.key] = item.value;
      });
      applyBusinessInsightPairs(routed.businessInsights);
      applyCustomModulePairs(routed.customModules);
      if (routed.agentFeedback.length) {
        filterApplied.agent_feedback_multiple = [
          ...(filterApplied.agent_feedback_multiple || []).filter((item) => !routed.agentFeedback.some(
            (next) => next.agent_feedback_parameter === item.agent_feedback_parameter)),
          ...routed.agentFeedback,
        ];
      }
    } else if (segmentName !== "" && segmentName !== null && segmentName !== undefined) {
      if (baseModule === "business_insight" && baseParameter) {
        applyBusinessInsightPairs([
          {
            business_insights_parameter: baseParameter,
            business_insights_parameter_value: segmentName,
          },
        ]);
      } else if (baseModule === "custom_module" && baseParameter) {
        applyCustomModulePairs([
          {
            custom_module_parameter_name: baseParameter,
            custom_module_parameter_answer: segmentName,
          },
        ]);
      } else if (baseModule === "agent_feedback" && baseParameter && activeReport?.reportLevel === "calls") {
        if (!isAgentFeedbackDrilldownScore(segmentName)) {
          showToast("Conversation drill-down is unavailable for this score", "error");
          return;
        }
        const routed = routeNamespacedReportDrilldownFilters([{ module: "agent_feedback", key: baseParameter, value: segmentName }], "calls");
        filterApplied.agent_feedback_multiple = [
          ...(filterApplied.agent_feedback_multiple || []).filter((item) => item.agent_feedback_parameter !== baseParameter),
          ...routed.agentFeedback,
        ];
      } else if (baseModule === "lead_quality" && baseParameter) {
        filterApplied[activeReport?.reportLevel === "calls" ? "conversationPotential" : "customerPotential"] = segmentName;
      } else {
        filterApplied[segmentKey] = segmentName;
      }
    }
    if (segment?.filterOverrides && typeof segment.filterOverrides === "object") {
      Object.assign(filterApplied, segment.filterOverrides);
    }
    setSelectedSegment({
      // id: segmentKey,
      // name: segmentName,
      // type: "business_insights",
      filter_applied: activeReport?.reportLevel === "calls" ? conversationReportDrilldownFilters(filterApplied) : filterApplied,
    });
    setIsSegmentModalOpen(true);
  };

  const handleExactCountCellClick = (viz, scope = {}) => {
    if (["loading", "refreshing", "processing"].includes(activeReportExecutionState?.status)) {
      showToast(REPORT_PROCESSING_MESSAGE, "info");
      return;
    }
    const context = viz?.drilldown?.context;
    const measureId = String(scope?.measureId || "").trim();
    if (!context || !measureId || !activeReportId) {
      showToast("Refresh the saved report before opening this count", "error");
      return;
    }
    const customReportDrilldown = {
      report_id: String(activeReportId),
      visualization_id: String(viz?.id),
      measure_id: measureId,
      context,
      dimensions: Array.isArray(scope?.dimensions)
        ? scope.dimensions.map((dimension) => ({
            key: dimension?.key,
            value: dimension?.value,
          }))
        : [],
      ...(scope?.periodId ? { period_id: scope.periodId } : {}),
      ...(scope?.timeScope === "total" ? { time_scope: "total" } : {}),
      ...(Number.isInteger(Number(scope?.bucketStart))
        ? { bucket_start: Number(scope.bucketStart) }
        : {}),
      ...(Number.isInteger(Number(scope?.bucketEnd))
        ? { bucket_end: Number(scope.bucketEnd) }
        : {}),
    };
    const responseConfig = viz?.data?.periodComparison || viz?.data?.period_comparison || {};
    const configuredMeasure = normalizeRestrictedSqlMeasures(viz?.measures).find(
      (measure) => String(measure.id) === measureId
    );
    const runtimeMeasure = (Array.isArray(responseConfig?.metrics) ? responseConfig.metrics : []).find(
      (measure) => String(measure?.id) === measureId
    );
    const period = (Array.isArray(responseConfig?.periods) ? responseConfig.periods : []).find(
      (item) => String(item?.id) === String(scope?.periodId || "")
    );
    const savedFilterSummary = (
      Array.isArray(viz?.advancedFiltersPayload?.filter?.and)
        ? viz.advancedFiltersPayload.filter.and
        : []
    ).map((condition) => ({
      label: humanizeFilterName(condition?.field),
      value: `${humanizeFilterName(condition?.operator)} ${formatFilterSummaryValue(condition?.value)}`.trim(),
    }));
    const clickedDimensionSummary = (Array.isArray(scope?.dimensions) ? scope.dimensions : []).map(
      (dimension) => ({
        label:
          dimension?.label ||
          resolveParameterKeyToDisplayLabel(dimension?.key) ||
          humanizeFilterName(dimension?.key),
        value: formatFilterSummaryValue(dimension?.value),
      })
    );
    setSelectedSegment({
      custom_report_drilldown: customReportDrilldown,
      applied_filter_summary: [
        {
          label: "Measure",
          value: configuredMeasure?.label || runtimeMeasure?.label || humanizeFilterName(measureId),
        },
        ...(period ? [{ label: "Period", value: period.label || humanizeFilterName(period.id) }] : []),
        ...(scope?.timeScope === "total"
          ? [{ label: "Time scope", value: "Total selected range" }]
          : []),
        ...clickedDimensionSummary,
        ...savedFilterSummary,
      ],
    });
    setIsSegmentModalOpen(true);
  };

  const handleTimeSeriesPointClick = (viz, point, measureColumn) => {
    const runtimeConfig = viz?.data?.timeSeries || viz?.data?.time_series || {};
    const startKey = runtimeConfig.bucketStartKey || "bucket_start";
    const endKey = runtimeConfig.bucketEndKey || "bucket_end";
    const isTotal = point?.__timeSeriesScope === "total";
    const bucketStart = point?.[startKey];
    const bucketEnd = point?.[endKey];
    if (measureColumn?.drilldownMode !== "exact_count") {
      showToast("This value is not an exact record count", "error");
      return;
    }
    if (isTotal) {
      handleExactCountCellClick(viz, {
        measureId:
          measureColumn?.measure_id ?? measureColumn?.measureId ?? measureColumn?.key,
        dimensions: [],
        timeScope: "total",
      });
      return;
    }
    if (
      !Number.isInteger(Number(bucketStart)) ||
      !Number.isInteger(Number(bucketEnd)) ||
      Number(bucketEnd) < Number(bucketStart)
    ) {
      showToast("This point does not include a valid drill-down range", "error");
      return;
    }
    handleExactCountCellClick(viz, {
      measureId:
        measureColumn?.measure_id ?? measureColumn?.measureId ?? measureColumn?.key,
      dimensions: [],
      bucketStart: Number(bucketStart),
      bucketEnd: Number(bucketEnd),
    });
  };

  const getBuilderValidationErrors = () => {
    const errors = [];
    formState.visualizations.forEach((visualization, visualizationIndex) => {
      if (visualization.visualizationType === "header") return;
      const orderedPairs = visualizationToOrderedPairs(visualization);
      const effectiveVisualizationType = getEffectiveVisualizationType(visualization);
      if (
        formState.reportLevel === "calls" &&
        effectiveVisualizationType !== PERIOD_COMPARISON_TABLE_VARIANT
      ) {
        const filters = visualization.advancedFilters || {};
        if (!filters.call_time || (filters.call_time === "Custom Range" &&
            (!filters.call_time_start || (filters.call_time_range_type !== "after" && !filters.call_time_end)))) {
          errors.push({ visualizationId: visualization.id, visualizationIndex,
            field: "advancedFilters", message: "Select a conversation time period and complete its date range." });
        }
      }
      if (
        visualization.visualizationType === "time_series" &&
        !isTimeSeriesReportLevel(formState.reportLevel)
      ) {
        errors.push({
          visualizationId: visualization.id,
          visualizationIndex,
          field: "visualizationType",
          message: "Time Series is available only for customer-level reports.",
        });
      }
      if (
        visualization.visualizationType !== "time_series" &&
        effectiveVisualizationType !== PERIOD_COMPARISON_TABLE_VARIANT &&
        getParameterValues(visualization).length === 0
      ) {
        errors.push({
          visualizationId: visualization.id,
          visualizationIndex,
          field: "parameters",
          message: "Select at least one parameter for this visualization.",
        });
      }
      (visualization.advancedFilters?.reportExclusions || []).forEach((condition) => {
        if (!condition.field || !String(condition.value ?? "").trim()) errors.push({
          visualizationId: visualization.id, field: "filters", message: "Complete each Not equal filter or remove it.",
        });
      });
      validateRestrictedSqlMeasures(visualization).forEach((error) =>
        errors.push({ ...error, visualizationId: visualization.id, visualizationIndex })
      );
      validateTimeSeriesVisualization(visualization).forEach((error) =>
        errors.push({ ...error, visualizationId: visualization.id, visualizationIndex })
      );
      validatePeriodComparisonVisualization(visualization, formState.reportLevel).forEach(
        (error) =>
          errors.push({ ...error, visualizationId: visualization.id, visualizationIndex })
      );
      validatePivotConfig(visualization, orderedPairs).forEach((error) =>
        errors.push({ ...error, visualizationId: visualization.id, visualizationIndex })
      );
    });
    return errors;
  };

  const buildUnsavedReportConfiguration = () => ({
    name: formState.reportName.trim() || "Untitled Report",
    reportLevel: formState.reportLevel || "customers",
    visualizations: buildPersistableVisualizations(formState.visualizations, formState.reportLevel),
  });

  const handlePreviewReport = async (isRefresh = false) => {
    const localErrors = getBuilderValidationErrors();
    if (localErrors.length > 0) {
      setRestrictedSqlErrors(localErrors);
      showToast(localErrors[0].message, "error");
      return false;
    }
    const previewGeneration = previewGenerationRef.current;
    const previewRequestId = previewRequestIdRef.current + 1;
    previewRequestIdRef.current = previewRequestId;
    const isCurrentPreviewRequest = () =>
      previewGenerationRef.current === previewGeneration &&
      previewRequestIdRef.current === previewRequestId;
    setIsPreviewing(true);
    try {
      const response = await server.custom_report_execution({
        client_id,
        service_id,
        menu: REPORTS_MENU,
        submenu: REPORTS_SUBMENU,
        email,
        preview: true,
        is_refresh: isRefresh,
        configuration: buildUnsavedReportConfiguration(),
      });
      if (!isCurrentPreviewRequest()) return false;
      if (response.status !== 200) {
        let errorPayload = {};
        try {
          errorPayload = await response.json();
        } catch (error) {
          errorPayload = {};
        }
        const apiErrors = extractRestrictedSqlApiErrors(errorPayload);
        setRestrictedSqlErrors(apiErrors);
        showToast(
          apiErrors[0]?.message || getRestrictedSqlErrorMessage(errorPayload, "Unable to preview report"),
          "error"
        );
        return false;
      }
      const payload = await response.json();
      if (!isCurrentPreviewRequest()) return false;
      const result = payload?.result;
      if (!result || !Array.isArray(result.visualizations)) {
        showToast("Preview response did not contain visualizations", "error");
        return false;
      }
      setBuilderPreview({
        result,
        refreshedAt: payload?.last_refreshed_at || new Date().toISOString(),
      });
      setRestrictedSqlErrors([]);
      if (Array.isArray(payload?.field_catalog)) {
        setServerFieldCatalog(
          payload.field_catalog
            .map((field) => ({
              value: String(field?.name || field?.logical_name || "").trim(),
              label: String(field?.label || field?.name || field?.logical_name || "").trim(),
              dataType: field?.dataType || field?.data_type || null,
            }))
            .filter((field) => field.value)
        );
      }
      showToast(isRefresh ? "Preview refreshed" : "Preview generated", "success");
      return true;
    } catch (error) {
      if (isCurrentPreviewRequest()) showToast("Unable to preview report", "error");
      return false;
    } finally {
      if (isCurrentPreviewRequest()) setIsPreviewing(false);
    }
  };

  const handleSaveReport = async () => {
    if (!formState.reportName.trim()) {
      showToast("Please enter report name", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      const localErrors = getBuilderValidationErrors();
      if (localErrors.length > 0) {
        setRestrictedSqlErrors(localErrors);
        showToast(localErrors[0].message, "error");
        setIsSubmitting(false);
        return;
      }

      const normalizedVisualizations = buildPersistableVisualizations(formState.visualizations, formState.reportLevel);
      const reportConfiguration = {
        name: formState.reportName.trim(),
        reportLevel: formState.reportLevel || "customers",
        visualizations: normalizedVisualizations,
      };

      const existingReport = reports.find((item) => item.id === editingReportConfigId);
      const response = await server.post_custom_report_details({
        client_id,
        service_id,
        report_id:
          reportModalMode === "edit"
            ? existingReport?.report_id || editingReportConfigId
            : undefined,
        report_configuration: reportConfiguration,
        menu: REPORTS_MENU,
        submenu: REPORTS_SUBMENU,
        email: email,
      });

      if (response.status !== 200) {
        let errorPayload = {};
        try {
          errorPayload = await response.json();
        } catch (error) {
          errorPayload = {};
        }
        const apiErrors = extractRestrictedSqlApiErrors(errorPayload);
        setRestrictedSqlErrors(apiErrors);
        showToast(
          apiErrors[0]?.message || getRestrictedSqlErrorMessage(errorPayload, "Unable to save report"),
          "error"
        );
        return;
      }

      const payload = await response.json();
      const savedReportId = payload?.report_id || existingReport?.report_id || editingReportConfigId;

      if (savedReportId) {
        setExecutedReportsById((prev) => {
          const next = { ...prev };
          delete next[savedReportId];
          return next;
        });
        setReportExecutionStates((prev) => ({
          ...prev,
          [savedReportId]: { status: "loading", message: "Loading report..." },
        }));
        setSelectedSegment(null);
        setIsSegmentModalOpen(false);
        setActiveReportId(savedReportId);
      }
      await loadCustomReportsList(client_id, service_id, email);
      if (savedReportId) {
        await triggerReportExecution(savedReportId);
      }
      showToast(
        reportModalMode === "edit" ? "Report updated successfully" : "Report created successfully",
        "success"
      );

      setShowCreateReportModal(false);
      setFiltersPanelVisualizationId(null);
      setEditingReportConfigId(null);
      setFormState({
        reportName: "",
        reportLevel: "customers",
        visualizations: [getDefaultVisualization(1)],
      });
      setBuilderPreview(null);
      setRestrictedSqlErrors([]);
    } catch (error) {
      showToast("Could not save report. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = (data, title) => {
    const arrData = Array.isArray(data?.series) ? data.series : Array.isArray(data?.rows) ? data.rows : [];
    if (!arrData.length) {
      showToast("No data available to download", "error");
      return;
    }

    const escapeCsvValue = (value) => {
      const str = value === null || value === undefined ? "" : String(value);
      if (/[",\n]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const columnDefs = Array.isArray(data?.columns) ? data.columns : [];
    const headerKeys = columnDefs.length
      ? columnDefs.map((col) => col?.key || col?.label).filter(Boolean)
      : Object.keys(arrData[0] || {});
    const headerLabels = columnDefs.length
      ? columnDefs.map((col) => col?.label || col?.key).filter(Boolean)
      : headerKeys;

    const headerRow = headerLabels.map(escapeCsvValue).join(",");
    const dataRows = arrData.map((row) =>
      headerKeys
        .map((key) => escapeCsvValue(Array.isArray(row) ? row[key] : row?.[key]))
        .join(",")
    );
    const csvContent = "data:text/csv;charset=utf-8," + [headerRow, ...dataRows].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${title}_data.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const handlePivotDownload = (viz) => {
    const {
      rows,
      dimensionColumns,
      countColumn,
      getDisplayColumnLabel,
    } = getReportTableModel(viz);
    const [columnDimension, rowDimension] = dimensionColumns;
    const countKey = countColumn?.key;
    if (!columnDimension || !rowDimension || !countKey || !rows.length) {
      showToast("No data available to download", "error");
      return;
    }

    const toDimensionKey = (value) =>
      value === null || value === undefined || value === "" ? "__blank__" : String(value);
    const formatDimensionValue = (value) =>
      value === null || value === undefined || value === "" ? "-" : String(value);

    const columnValues = [];
    const columnValueByKey = new Map();
    const rowValues = [];
    const rowValueByKey = new Map();
    const pivotRowsByKey = new Map();
    const columnTotals = new Map();
    let grandTotal = 0;

    rows.forEach((row) => {
      const colVal = row?.[columnDimension.key];
      const rowVal = row?.[rowDimension.key];
      const colKey = toDimensionKey(colVal);
      const rowKey = toDimensionKey(rowVal);
      const count = parseReportTableCount(row?.[countKey]);
      if (!columnValueByKey.has(colKey)) {
        columnValueByKey.set(colKey, colVal);
        columnValues.push({ key: colKey, value: colVal });
      }
      if (!rowValueByKey.has(rowKey)) {
        rowValueByKey.set(rowKey, rowVal);
        rowValues.push({ key: rowKey, value: rowVal });
      }
      if (!pivotRowsByKey.has(rowKey)) {
        pivotRowsByKey.set(rowKey, { cells: new Map(), total: 0 });
      }
      const pivotRow = pivotRowsByKey.get(rowKey);
      pivotRow.cells.set(colKey, (pivotRow.cells.get(colKey) || 0) + count);
      pivotRow.total += count;
      columnTotals.set(colKey, (columnTotals.get(colKey) || 0) + count);
      grandTotal += count;
    });

    rowValues.sort(
      (a, b) => (pivotRowsByKey.get(b.key)?.total || 0) - (pivotRowsByKey.get(a.key)?.total || 0)
    );
    columnValues.sort((a, b) => (columnTotals.get(b.key) || 0) - (columnTotals.get(a.key) || 0));

    const escapeCsvValue = (value) => {
      const str = value === null || value === undefined ? "" : String(value);
      if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
      return str;
    };

    const headerRow = [
      escapeCsvValue(getDisplayColumnLabel(rowDimension)),
      ...columnValues.map((c) => escapeCsvValue(formatDimensionValue(c.value))),
      escapeCsvValue("Grand Total"),
    ].join(",");

    const dataRows = rowValues.map((rv) => {
      const pivotRow = pivotRowsByKey.get(rv.key);
      return [
        escapeCsvValue(formatDimensionValue(rv.value)),
        ...columnValues.map((cv) => pivotRow?.cells.get(cv.key) || 0),
        pivotRow?.total || 0,
      ].join(",");
    });

    const totalRow = [
      escapeCsvValue("Grand Total"),
      ...columnValues.map((cv) => columnTotals.get(cv.key) || 0),
      grandTotal,
    ].join(",");

    const csvContent =
      "data:text/csv;charset=utf-8," + [headerRow, ...dataRows, totalRow].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${viz.title || "pivot_table"}_data.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const REPORT_TABLE_ROW_LABEL_KEY = "__report_row_label__";
  const REPORT_PIVOT_ROW_TOTAL_KEY = "__report_pivot_row_total__";

  const getAriaSort = (sortConfig, field) =>
    sortConfig?.field === field
      ? sortConfig?.direction === "asc"
        ? "ascending"
        : "descending"
      : "none";
  const cycleSort = (currentSort, field) => {
    if (currentSort?.field !== field) return { field, direction: "asc" };
    if (currentSort?.direction === "asc") return { field, direction: "desc" };
    return { field: null, direction: null };
  };
  const updateReportTableSort = (vizId, field) => {
    setReportTableSortSettings((prev) => ({
      ...prev,
      [vizId]: cycleSort(prev?.[vizId], field),
    }));
  };
  const updatePivotSort = (vizId, field) => {
    setPivotSortSettings((prev) => ({
      ...prev,
      [vizId]: cycleSort(prev?.[vizId], field),
    }));
  };
  const renderSortArrows = (sortConfig, field) => {
    if (sortConfig?.field === field && sortConfig?.direction === "asc") {
      return <ArrowUp className="h-3 w-3 text-indigo-600" />;
    }
    if (sortConfig?.field === field && sortConfig?.direction === "desc") {
      return <ArrowDown className="h-3 w-3 text-indigo-600" />;
    }
    return <ChevronsUpDown className="h-3 w-3 text-gray-300 group-hover:text-gray-400" />;
  };
  const normalizeSortValue = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "number") return value;
    const asNumber = Number(String(value).replace(/,/g, ""));
    if (Number.isFinite(asNumber) && String(value).trim() !== "") return asNumber;
    return String(value).toLowerCase();
  };
  const compareSortValues = (leftRaw, rightRaw, direction) => {
    const left = normalizeSortValue(leftRaw);
    const right = normalizeSortValue(rightRaw);
    if (left === null && right === null) return 0;
    if (left === null) return direction === "asc" ? 1 : -1;
    if (right === null) return direction === "asc" ? -1 : 1;
    if (left < right) return direction === "asc" ? -1 : 1;
    if (left > right) return direction === "asc" ? 1 : -1;
    return 0;
  };

  const parseReportTableCount = (raw) => {
    if (raw === null || raw === undefined || raw === "") return 0;
    if (typeof raw === "number" && Number.isFinite(raw)) return raw;
    const n = Number(String(raw).replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
  };

  const getReportTableModel = (viz) => {
    const rows = Array.isArray(viz?.data?.rows) ? viz.data.rows : [];
    const rawColumns = Array.isArray(viz?.data?.columns)
      ? viz.data.columns
      : [
          { key: "lead_stage", label: "Lead Stage" },
          { key: "lead_source", label: "Lead Source" },
          { key: "value", label: "Count" },
        ];
    const visibleColumns = rawColumns.filter(
      (column) => !["metric_id", "metric_format"].includes(String(column?.key || ""))
    );
    const sqlMeasures = normalizeRestrictedSqlMeasures(viz?.measures);
    const sqlMeasureById = new Map(sqlMeasures.map((measure) => [String(measure.id), measure]));
    const hasSqlMeasures = sqlMeasures.length > 0;
    const legacyAggregation = getLegacyStandardAggregationSemantics(viz);
    const isCountColumnDef = (column) =>
      column?.role === "measure" ||
      column?.kind === "measure" ||
      column?.type === "measure" ||
      sqlMeasureById.has(String(column?.measure_id ?? column?.measureId ?? column?.key)) ||
      (!hasSqlMeasures &&
        (column?.key === "value" || String(column?.label || "").toLowerCase().includes("count")));
    const dimensionColumns = visibleColumns.filter((column) => !isCountColumnDef(column));
    const rawMeasureColumns = visibleColumns.filter((column) => isCountColumnDef(column));
    const responseMeasureColumns = Array.isArray(viz?.data?.measureColumns)
      ? viz.data.measureColumns
      : Array.isArray(viz?.data?.measure_columns)
        ? viz.data.measure_columns
        : [];
    const hasExplicitPercentageEligibility = (column) => {
      const measureId = String(column?.measure_id ?? column?.measureId ?? column?.key ?? "");
      const responseColumn = responseMeasureColumns.find(
        (item) => String(item?.measure_id ?? item?.measureId ?? item?.key ?? "") === measureId
      );
      return [column, responseColumn].some(
        (item) => item && Object.prototype.hasOwnProperty.call(item, "percentageEligible")
      );
    };
    const measureColumns = mergeMeasureColumnMetadata(
      viz?.data,
      rawMeasureColumns,
      sqlMeasures
    ).map((column, index) => {
      if (hasSqlMeasures) return column;
      const hasExplicitEligibility = hasExplicitPercentageEligibility(
        rawMeasureColumns[index]
      );
      return {
        ...column,
        percentageEligible:
          column.format !== "percentage" &&
          legacyAggregation.percentageEligible &&
          (!hasExplicitEligibility || column.percentageEligible === true),
      };
    });
    const measureColumnByKey = new Map(
      measureColumns.map((column) => [String(column.key), column])
    );
    const columns = visibleColumns.map(
      (column) => measureColumnByKey.get(String(column.key)) || column
    );
    const countColumn = measureColumns[0];
    const orderedPairs = visualizationToOrderedPairs(viz);
    const fallbackParameterIds = orderedPairs.map((p) => p.value);
    const resolvedParameterIds = resolveDimensionParameterIds(
      viz?.data,
      dimensionColumns,
      fallbackParameterIds
    );
    const mappedDimensions = dimensionColumns.map((column, index) => ({
      module: orderedPairs[index]?.module,
      columnKey: column?.key,
      filterKey: resolvedParameterIds[index] || column?.key,
    }));
    const mappedDimensionByColumnKey = new Map(
      mappedDimensions.map((item) => [String(item.columnKey), item.filterKey])
    );
    const getDisplayColumnLabel = (column) => {
      if (isCountColumnDef(column)) {
        const configuredMeasure = sqlMeasureById.get(
          String(column?.measure_id ?? column?.measureId ?? column?.key)
        );
        return (
          column?.display_label ||
          column?.displayLabel ||
          column?.label ||
          configuredMeasure?.label ||
          "Count"
        );
      }
      const apiHdr =
        column?.parameter_label ??
        column?.parameterLabel ??
        column?.display_label ??
        column?.displayLabel;
      if (String(apiHdr || "").trim()) return String(apiHdr).trim();
      const key = String(column?.key || "");
      const rawLabel = String(column?.label || "").trim();
      const isGenericLabel =
        rawLabel === "" ||
        /^col\s*\d+$/i.test(rawLabel) ||
        /^col_?\d+$/i.test(rawLabel.replace(/\s+/g, "_"));
      if (!isGenericLabel && rawLabel) return column.label;
      const looksGenerated = /^col_?\d+$/i.test(key) || /^col\s+\d+$/i.test(rawLabel);
      if (looksGenerated) {
        const mapped = mappedDimensionByColumnKey.get(key);
        if (mapped) {
          const human = resolveParameterKeyToDisplayLabel(mapped);
          if (human) return human;
          return mapped;
        }
      }
      return column?.label || column?.key;
    };

    return {
      rows,
      columns,
      dimensionColumns,
      countColumn,
      measureColumns,
      hasSqlMeasures,
      mappedDimensions,
      isCountColumnDef,
      getDisplayColumnLabel,
      legacyAggregation,
    };
  };

  const renderStandardTable = (viz, tableControls = null) => {
    const {
      rows,
      columns,
      dimensionColumns,
      measureColumns,
      hasSqlMeasures,
      mappedDimensions,
      isCountColumnDef,
      getDisplayColumnLabel,
      legacyAggregation,
    } = getReportTableModel(viz);
    const getRowDrilldown = (row) => {
      const mappedFilters = mappedDimensions.map((item) => ({
        module: item.module,
        key: item.filterKey,
        value: row?.[item.columnKey],
      }));
      return mappedFilters.length
        ? { key: mappedFilters[0].key, value: mappedFilters[0].value, filters: mappedFilters }
        : null;
    };
    const measureByKey = new Map(measureColumns.map((column) => [String(column.key), column]));
    const grandTotalLabelKey = columns.find(
      (column) => !measureByKey.has(String(column.key))
    )?.key;
    const getTotal = (column) => {
      const backendTotal = getGrandTotal(viz?.data, column);
      if (backendTotal !== undefined) return backendTotal;
      return tableControls || hasSqlMeasures || !legacyAggregation.rowDerivedTotalEligible
        ? undefined
        : sumRawMeasure(rows, column);
    };
    const hasPercentage = hasPercentageEligibleMeasure(measureColumns);
    const requestedSettings = getDisplayModeSettings(pivotDisplaySettings[viz.id]);
    const displayMode = hasPercentage ? requestedSettings.mode : DISPLAY_VALUES;
    const hasDisplayableTotal = measureColumns.some(
      (column) => getTotal(column) !== undefined
    );
    const tableSort = tableControls?.sort || reportTableSortSettings[viz.id] || { field: null, direction: null };
    const sortedRows =
      !tableControls && tableSort.field && tableSort.direction
        ? [...rows].sort((leftRow, rightRow) => {
            const measureColumn = measureByKey.get(String(tableSort.field));
            const getSortValue = (row) =>
              measureColumn
                ? getDisplayNumericValue({
                    value: row?.[measureColumn.key],
                    column: measureColumn,
                    mode: displayMode,
                    denominator: getTotal(measureColumn),
                  })
                : row?.[tableSort.field];
            return compareSortValues(
              getSortValue(leftRow),
              getSortValue(rightRow),
              tableSort.direction
            );
          })
        : rows;

    return (
      <div className="h-full min-h-0 overflow-auto px-3 pb-3" style={{ position: "relative" }}>
        <div className="flex flex-wrap items-start justify-between gap-3 sticky top-0 left-0 w-full bg-white z-10">
          <div className="text-xl font-semibold ml-3 mt-3 mb-3">{viz.title || `Visualization ${viz.id}`}</div>
          <div className="mr-3 mt-3 mb-3 flex items-start gap-2">
            {hasPercentage ? (
              <MeasureDisplayModeControls
                mode={displayMode}
                percentageOf={PERCENTAGE_OF_COLUMN}
                onChange={(patch) => updatePivotDisplaySetting(viz.id, patch)}
              />
            ) : null}
            <div className="relative" onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) setTableDownloadMenuId(null);
            }} onKeyDown={(event) => {
              if (event.key === "Escape") {
                setTableDownloadMenuId(null);
                event.currentTarget.querySelector("button")?.focus();
              }
            }}>
              <button type="button" aria-label="Download table" title="Download table" aria-expanded={tableDownloadMenuId === viz.id}
                className="rounded p-1 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                onClick={() => setTableDownloadMenuId((current) => current === viz.id ? null : viz.id)}>
                <Download size={20} />
              </button>
              {tableDownloadMenuId === viz.id ? <div className="absolute right-0 top-8 z-20 min-w-[240px] rounded-md border border-gray-200 bg-white p-2 shadow-lg">
                <button type="button" className="w-full rounded px-2 py-1.5 text-left text-sm text-gray-600 hover:bg-gray-100"
                  onClick={() => {
                    setTableDownloadMenuId(null);
                    handleDownload({ ...viz.data, rows: sortedRows }, viz.title);
                  }}>Download Displayed Table</button>
                {tableControls ? <button type="button" disabled={tableControls.exporting}
                  className="w-full rounded px-2 py-1.5 text-left text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                  title="Receive a download link by email" onClick={() => {
                    setTableDownloadMenuId(null);
                    tableControls.downloadFull();
                  }}>{tableControls.exporting ? "Queueing…" : "Download Full Data (Email)"}</button> : null}
              </div> : null}
            </div>
          </div>
        </div>
        <table className="min-w-full">
          <thead>
            <tr className="bg-gray-50 text-left">
              {columns.map((column) => (
                <th
                  key={`${viz.id}-${column.key}`}
                  aria-sort={getAriaSort(tableSort, column.key)}
                  className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  <button
                    type="button"
                    className="flex items-center gap-1 group"
                    onClick={() => tableControls ? tableControls.onSort(column.key) : updateReportTableSort(viz.id, column.key)}
                  >
                    <span>{getDisplayColumnLabel(column)}</span>
                    {renderSortArrows(tableSort, column.key)}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedRows.length === 0 ? (
              <tr>
                <td className="px-4 py-3 text-sm text-gray-500" colSpan={columns.length}>
                  No data available
                </td>
              </tr>
            ) : (
              <>
                {sortedRows.map((row, rowIndex) => {
                  const rowDrilldown = getRowDrilldown(row);
                  return (
                    <tr key={`${viz.id}-${rowIndex}`} className="hover:bg-gray-50">
                      {columns.map((column) => {
                        const value = row?.[column.key];
                        const measureColumn = measureByKey.get(String(column.key));
                        const isExactCountDrillable =
                          measureColumn?.drilldownMode === "exact_count" &&
                          Boolean(viz?.drilldown?.context) &&
                          Number(value) > 0;
                        const isLegacyDrillable =
                          !hasSqlMeasures &&
                          (!measureColumn?.drilldownMode || isConversationCountDrilldown(viz, activeReport?.reportLevel)) &&
                          isCountColumnDef(column) &&
                          rowDrilldown?.value !== undefined &&
                          rowDrilldown?.value !== null &&
                          (activeReport?.reportLevel !== "calls" || rowDrilldown.filters.every(
                            (filter) => filter.value !== null && filter.value !== undefined && filter.value !== "" &&
                              (filter.module !== "agent_feedback" || isAgentFeedbackDrilldownScore(filter.value))
                          ));
                        const isDrillable = isExactCountDrillable || isLegacyDrillable;
                        return (
                          <td
                            key={`${viz.id}-${rowIndex}-${column.key}`}
                            title={activeReport?.reportLevel === "calls" && isCountColumnDef(column) && !isDrillable
                              ? "Conversation drill-down is unavailable for this value" : undefined}
                            onClick={
                              isDrillable
                                ? () => {
                                    if (isExactCountDrillable) {
                                      handleExactCountCellClick(viz, {
                                        measureId:
                                          measureColumn?.measure_id ??
                                          measureColumn?.measureId ??
                                          measureColumn?.key,
                                        dimensions: dimensionColumns.map((dimension) => ({
                                          key: dimension.key,
                                          label: getDisplayColumnLabel(dimension),
                                          value: row?.[dimension.key],
                                        })),
                                      });
                                      return;
                                    }
                                    handleVisualizationSegmentClick(viz, {
                                      id: rowDrilldown.key,
                                      name: rowDrilldown.value,
                                      filters: rowDrilldown.filters,
                                    });
                                  }
                                : undefined
                            }
                            className={`px-4 py-3 text-sm text-gray-900 ${
                              isDrillable
                                ? "text-indigo-500 hover:text-indigo-700 underline cursor-pointer"
                                : ""
                            }`}
                          >
                            {measureColumn
                              ? formatDisplayedMeasure({
                                  value,
                                  column: measureColumn,
                                  mode: displayMode,
                                  denominator: getTotal(measureColumn),
                                })
                              : value === null || value === undefined || value === ""
                                ? "-"
                                : String(value)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {hasDisplayableTotal ? (
                  <tr className="bg-gray-100 font-semibold border-t border-gray-200">
                    {columns.map((column) => {
                      const measureColumn = measureByKey.get(String(column.key));
                      return String(column.key) === String(grandTotalLabelKey) ? (
                        <th key={`${viz.id}-grand-total-label`} className="px-4 py-3 text-left text-sm text-gray-900">
                          Grand Total
                        </th>
                      ) : (
                        <td key={`${viz.id}-grand-total-${column.key}`} className="px-4 py-3 text-left text-sm tabular-nums text-gray-900">
                          {measureColumn
                            ? formatDisplayedMeasure({
                                value: getTotal(measureColumn),
                                column: measureColumn,
                                mode: displayMode,
                                denominator: getTotal(measureColumn),
                              })
                            : ""}
                        </td>
                      );
                    })}
                  </tr>
                ) : null}
              </>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderRestrictedSqlPivotTable = (viz, tableModel) => {
    const { rows, dimensionColumns, measureColumns, getDisplayColumnLabel } = tableModel;
    const [columnDimension, rowDimension] = dimensionColumns;
    if (!columnDimension || !rowDimension || measureColumns.length === 0) {
      return (
        <div className="flex h-full min-h-0 items-center justify-center text-sm text-gray-500">
          Pivot table needs exactly two dimensions and at least one SQL measure.
        </div>
      );
    }

    const toKey = (value) =>
      value === null || value === undefined || value === "" ? "__blank__" : String(value);
    const toLabel = (value) =>
      value === null || value === undefined || value === "" ? "-" : String(value);
    const columnValues = [];
    const rowValues = [];
    const seenColumns = new Set();
    const seenRows = new Set();
    const valuesByCell = new Map();
    const hasPercentage = hasPercentageEligibleMeasure(measureColumns);
    const requestedSettings = getDisplayModeSettings(pivotDisplaySettings[viz.id]);
    const displayMode = hasPercentage ? requestedSettings.mode : DISPLAY_VALUES;
    const percentageOf = requestedSettings.percentageOf;
    const pivotSort = pivotSortSettings[viz.id] || { field: null, direction: null };
    const showRowTotal = measureColumns.length === 1;
    const getRowTotal = (rowValue, measureColumn) =>
      getAxisTotal(viz?.data, "rows", [rowValue], measureColumn);
    const getColumnTotal = (columnValue, measureColumn) =>
      getAxisTotal(viz?.data, "columns", [columnValue], measureColumn);
    const getCellDenominator = (rowValue, columnValue, measureColumn) =>
      percentageOf === PERCENTAGE_OF_ROW
        ? getRowTotal(rowValue, measureColumn)
        : getColumnTotal(columnValue, measureColumn);

    rows.forEach((row) => {
      const columnValue = row?.[columnDimension.key];
      const rowValue = row?.[rowDimension.key];
      const columnKey = toKey(columnValue);
      const rowKey = toKey(rowValue);
      if (!seenColumns.has(columnKey)) {
        seenColumns.add(columnKey);
        columnValues.push({ key: columnKey, value: columnValue });
      }
      if (!seenRows.has(rowKey)) {
        seenRows.add(rowKey);
        rowValues.push({ key: rowKey, value: rowValue });
      }
      valuesByCell.set(`${rowKey}\u0000${columnKey}`, row);
    });

    const restrictedCellSortKey = (columnKey, measureKey) =>
      `cell:${columnKey}\u0000${measureKey}`;
    const sortedRowValues = [...rowValues];
    if (pivotSort.field && pivotSort.direction) {
      if (pivotSort.field === REPORT_TABLE_ROW_LABEL_KEY) {
        sortedRowValues.sort((left, right) =>
          compareSortValues(left?.value, right?.value, pivotSort.direction)
        );
      } else if (pivotSort.field === REPORT_PIVOT_ROW_TOTAL_KEY && showRowTotal) {
        const measureColumn = measureColumns[0];
        sortedRowValues.sort((left, right) => {
          const getSortValue = (rowValue) => {
            const value = getRowTotal(rowValue.value, measureColumn);
            return getDisplayNumericValue({
              value,
              column: measureColumn,
              mode: displayMode,
              denominator:
                percentageOf === PERCENTAGE_OF_ROW
                  ? value
                  : getGrandTotal(viz?.data, measureColumn),
            });
          };
          return compareSortValues(
            getSortValue(left),
            getSortValue(right),
            pivotSort.direction
          );
        });
      } else if (String(pivotSort.field).startsWith("cell:")) {
        const target = columnValues
          .flatMap((columnValue) =>
            measureColumns.map((measureColumn) => ({
              columnValue,
              measureColumn,
              sortKey: restrictedCellSortKey(columnValue.key, measureColumn.key),
            }))
          )
          .find((item) => item.sortKey === pivotSort.field);
        if (target) {
          sortedRowValues.sort((left, right) => {
            const getSortValue = (rowValue) => {
              const sourceRow = valuesByCell.get(
                `${rowValue.key}\u0000${target.columnValue.key}`
              );
              return getDisplayNumericValue({
                value: sourceRow?.[target.measureColumn.key],
                column: target.measureColumn,
                mode: displayMode,
                denominator: getCellDenominator(
                  rowValue.value,
                  target.columnValue.value,
                  target.measureColumn
                ),
              });
            };
            return compareSortValues(
              getSortValue(left),
              getSortValue(right),
              pivotSort.direction
            );
          });
        }
      }
    }

    return (
      <div className="h-full min-h-0 overflow-auto px-3 pb-3" style={{ position: "relative" }}>
        <div className="sticky left-0 top-0 z-10 flex flex-wrap items-start justify-between gap-3 bg-white">
          <div>
            <div className="ml-3 mt-3 text-xl font-semibold">{viz.title || `Visualization ${viz.id}`}</div>
            <div className="mb-3 ml-3 text-xs text-gray-500">
              Rows: {getDisplayColumnLabel(rowDimension)} / Columns: {getDisplayColumnLabel(columnDimension)}
            </div>
          </div>
          <div className="mr-3 mt-3 flex items-start gap-2">
            {hasPercentage ? (
              <MeasureDisplayModeControls
                mode={displayMode}
                percentageOf={percentageOf}
                allowRow
                onChange={(patch) => updatePivotDisplaySetting(viz.id, patch)}
              />
            ) : null}
            <button
              type="button"
              aria-label={`Download ${viz.title || "pivot"}`}
              onClick={() => handleDownload(viz.data, viz.title)}
              className="rounded p-1 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
            >
              <Download size={20} />
            </button>
          </div>
        </div>
        <table className="min-w-full border-separate border-spacing-0">
          <thead>
            <tr className="bg-gray-50">
              <th
                rowSpan={2}
                aria-sort={getAriaSort(pivotSort, REPORT_TABLE_ROW_LABEL_KEY)}
                className="sticky left-0 z-[3] border-b border-gray-200 bg-gray-50 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                <button
                  type="button"
                  onClick={() => updatePivotSort(viz.id, REPORT_TABLE_ROW_LABEL_KEY)}
                  className="inline-flex items-center gap-1 text-left"
                >
                  {getDisplayColumnLabel(rowDimension)}
                  {renderSortArrows(pivotSort, REPORT_TABLE_ROW_LABEL_KEY)}
                </button>
              </th>
              {columnValues.map((columnValue) => (
                <th
                  key={`${viz.id}-restricted-pivot-column-${columnValue.key}`}
                  colSpan={measureColumns.length}
                  className="border-b border-l border-gray-200 px-4 py-2 text-center text-xs font-semibold text-gray-600"
                >
                  {toLabel(columnValue.value)}
                </th>
              ))}
              {showRowTotal ? (
                <th
                  rowSpan={2}
                  aria-sort={getAriaSort(pivotSort, REPORT_PIVOT_ROW_TOTAL_KEY)}
                  className="border-b border-l border-gray-200 bg-gray-100 px-4 py-2 text-right text-xs font-semibold text-gray-600"
                >
                  <button
                    type="button"
                    onClick={() => updatePivotSort(viz.id, REPORT_PIVOT_ROW_TOTAL_KEY)}
                    className="ml-auto inline-flex items-center gap-1"
                  >
                    Grand Total
                    {renderSortArrows(pivotSort, REPORT_PIVOT_ROW_TOTAL_KEY)}
                  </button>
                </th>
              ) : null}
            </tr>
            <tr className="bg-gray-50">
              {columnValues.flatMap((columnValue) =>
                measureColumns.map((measureColumn) => {
                  const sortKey = restrictedCellSortKey(columnValue.key, measureColumn.key);
                  return (
                    <th
                      key={`${viz.id}-${columnValue.key}-${measureColumn.key}`}
                      aria-sort={getAriaSort(pivotSort, sortKey)}
                      className="border-b border-l border-gray-200 px-4 py-2 text-right text-[11px] font-medium text-gray-500"
                    >
                      <button
                        type="button"
                        onClick={() => updatePivotSort(viz.id, sortKey)}
                        className="ml-auto inline-flex items-center gap-1 text-right"
                      >
                        {getDisplayColumnLabel(measureColumn)}
                        {renderSortArrows(pivotSort, sortKey)}
                      </button>
                    </th>
                  );
                })
              )}
            </tr>
          </thead>
          <tbody>
            {sortedRowValues.length === 0 ? (
              <tr>
                <td className="px-4 py-3 text-sm text-gray-500" colSpan={1 + columnValues.length * measureColumns.length + (showRowTotal ? 1 : 0)}>
                  No data available
                </td>
              </tr>
            ) : (
              <>
                {sortedRowValues.map((rowValue) => (
                  <tr key={`${viz.id}-restricted-pivot-row-${rowValue.key}`} className="hover:bg-gray-50">
                    <th className="sticky left-0 z-[2] border-b border-gray-100 bg-white px-4 py-3 text-left text-sm font-medium text-gray-900">
                      {toLabel(rowValue.value)}
                    </th>
                    {columnValues.flatMap((columnValue) => {
                      const sourceRow = valuesByCell.get(`${rowValue.key}\u0000${columnValue.key}`);
                      return measureColumns.map((measureColumn) => {
                        const rawValue = sourceRow?.[measureColumn.key];
                        const isDrillable =
                          measureColumn?.drilldownMode === "exact_count" &&
                          Boolean(viz?.drilldown?.context) &&
                          Number(rawValue) > 0;
                        return (
                          <td
                            key={`${viz.id}-${rowValue.key}-${columnValue.key}-${measureColumn.key}`}
                            onClick={
                              isDrillable
                                ? () =>
                                    handleExactCountCellClick(viz, {
                                      measureId:
                                        measureColumn?.measure_id ??
                                        measureColumn?.measureId ??
                                        measureColumn?.key,
                                      dimensions: [
                                        {
                                          key: columnDimension.key,
                                          label: getDisplayColumnLabel(columnDimension),
                                          value: columnValue.value,
                                        },
                                        {
                                          key: rowDimension.key,
                                          label: getDisplayColumnLabel(rowDimension),
                                          value: rowValue.value,
                                        },
                                      ],
                                    })
                                : undefined
                            }
                            className={`border-b border-l border-gray-100 px-4 py-3 text-right text-sm tabular-nums ${
                              isDrillable
                                ? "cursor-pointer text-indigo-600 underline hover:text-indigo-700"
                                : "text-gray-800"
                            }`}
                          >
                            {formatDisplayedMeasure({
                              value: rawValue,
                              column: measureColumn,
                              mode: displayMode,
                              denominator: getCellDenominator(
                                rowValue.value,
                                columnValue.value,
                                measureColumn
                              ),
                            })}
                          </td>
                        );
                      });
                    })}
                    {showRowTotal ? (
                      <td className="border-b border-l border-gray-100 bg-gray-50 px-4 py-3 text-right text-sm font-semibold tabular-nums text-gray-900">
                        {formatDisplayedMeasure({
                          value: getRowTotal(rowValue.value, measureColumns[0]),
                          column: measureColumns[0],
                          mode: displayMode,
                          denominator:
                            percentageOf === PERCENTAGE_OF_ROW
                              ? getRowTotal(rowValue.value, measureColumns[0])
                              : getGrandTotal(viz?.data, measureColumns[0]),
                        })}
                      </td>
                    ) : null}
                  </tr>
                ))}
                <tr className="bg-gray-100 font-semibold">
                  <th className="sticky left-0 bg-gray-100 px-4 py-3 text-left text-sm text-gray-900">
                    Grand Total
                  </th>
                  {columnValues.flatMap((columnValue) =>
                    measureColumns.map((measureColumn) => {
                      const columnTotal = getColumnTotal(columnValue.value, measureColumn);
                      return (
                        <td
                          key={`${viz.id}-restricted-total-${columnValue.key}-${measureColumn.key}`}
                          className="px-4 py-3 text-right text-sm tabular-nums text-gray-900"
                        >
                          {formatDisplayedMeasure({
                            value: columnTotal,
                            column: measureColumn,
                            mode: displayMode,
                            denominator:
                              percentageOf === PERCENTAGE_OF_COLUMN
                                ? columnTotal
                                : getGrandTotal(viz?.data, measureColumn),
                          })}
                        </td>
                      );
                    })
                  )}
                  {showRowTotal ? (
                    <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-900">
                      {formatDisplayedMeasure({
                        value: getGrandTotal(viz?.data, measureColumns[0]),
                        column: measureColumns[0],
                        mode: displayMode,
                        denominator: getGrandTotal(viz?.data, measureColumns[0]),
                      })}
                    </td>
                  ) : null}
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPivotTable = (viz) => {
    const tableModel = getReportTableModel(viz);
    if (usesMultidimensionalPivotRenderer(viz)) {
      return (
        <MultidimensionalPivotTable
          visualization={viz}
          reportLevel={activeReport?.reportLevel}
          tableModel={tableModel}
          displaySettings={pivotDisplaySettings[viz.id]}
          onDisplaySettingsChange={(patch) => updatePivotDisplaySetting(viz.id, patch)}
          onCellClick={(cell) => {
            if (cell?.exactCount) {
              handleExactCountCellClick(viz, cell);
              return;
            }
            handleVisualizationSegmentClick(viz, { filters: cell?.filters || cell });
          }}
        />
      );
    }
    if (tableModel.hasSqlMeasures) return renderRestrictedSqlPivotTable(viz, tableModel);
    const {
      rows,
      dimensionColumns,
      countColumn,
      mappedDimensions,
      getDisplayColumnLabel,
    } = tableModel;
    const [columnDimension, rowDimension] = dimensionColumns;
    const countKey = countColumn?.key;
    const hasPercentage = hasPercentageEligibleMeasure([countColumn]);
    const settings = getDisplayModeSettings(pivotDisplaySettings[viz.id]);
    const pivotSort = pivotSortSettings[viz.id] || { field: null, direction: null };
    const displayMode = hasPercentage ? settings.mode : DISPLAY_VALUES;
    const percentageOf = settings.percentageOf;
    const toDimensionKey = (value) =>
      value === null || value === undefined || value === "" ? "__blank__" : String(value);
    const formatDimensionValue = (value) =>
      value === null || value === undefined || value === "" ? "-" : String(value);
    if (!columnDimension || !rowDimension || !countKey) {
      return (
        <div className="flex h-full min-h-0 items-center justify-center text-sm text-gray-500">
          Pivot table needs exactly two dimensions and a count column.
        </div>
      );
    }

    const columnValues = [];
    const rowValues = [];
    const columnValueByKey = new Map();
    const rowValueByKey = new Map();
    const pivotRowsByKey = new Map();
    const columnTotals = new Map();
    let grandTotal = 0;

    rows.forEach((row) => {
      const columnValue = row?.[columnDimension.key];
      const rowValue = row?.[rowDimension.key];
      const columnKey = toDimensionKey(columnValue);
      const rowKey = toDimensionKey(rowValue);
      const count = parseReportTableCount(row?.[countKey]);

      if (!columnValueByKey.has(columnKey)) {
        columnValueByKey.set(columnKey, columnValue);
        columnValues.push({ key: columnKey, value: columnValue });
      }
      if (!rowValueByKey.has(rowKey)) {
        rowValueByKey.set(rowKey, rowValue);
        rowValues.push({ key: rowKey, value: rowValue });
      }
      if (!pivotRowsByKey.has(rowKey)) {
        pivotRowsByKey.set(rowKey, { key: rowKey, value: rowValue, cells: new Map(), total: 0 });
      }

      const pivotRow = pivotRowsByKey.get(rowKey);
      pivotRow.cells.set(columnKey, (pivotRow.cells.get(columnKey) || 0) + count);
      pivotRow.total += count;
      columnTotals.set(columnKey, (columnTotals.get(columnKey) || 0) + count);
      grandTotal += count;
    });

    const valueOrFallback = (value, fallback) => (value === undefined ? fallback : value);
    const getPivotRowTotal = (rowValue) =>
      valueOrFallback(
        getAxisTotal(viz?.data, "rows", [rowValue.value], countColumn),
        pivotRowsByKey.get(rowValue.key)?.total ?? 0
      );
    const getPivotColumnTotal = (columnValue) =>
      valueOrFallback(
        getAxisTotal(viz?.data, "columns", [columnValue.value], countColumn),
        columnTotals.get(columnValue.key) ?? 0
      );
    const pivotGrandTotal = valueOrFallback(getGrandTotal(viz?.data, countColumn), grandTotal);
    const getCellDenominator = (rowValue, columnValue) =>
      percentageOf === PERCENTAGE_OF_ROW
        ? getPivotRowTotal(rowValue)
        : getPivotColumnTotal(columnValue);
    const formatPivotMeasure = (value, denominator) =>
      formatDisplayedMeasure({
        value,
        column: countColumn,
        mode: displayMode,
        denominator,
      });

    if (!pivotSort.field || !pivotSort.direction) {
      rowValues.sort(
        (a, b) => Number(getPivotRowTotal(b) || 0) - Number(getPivotRowTotal(a) || 0)
      );
      columnValues.sort(
        (a, b) => Number(getPivotColumnTotal(b) || 0) - Number(getPivotColumnTotal(a) || 0)
      );
    } else if (pivotSort.field === REPORT_TABLE_ROW_LABEL_KEY) {
      rowValues.sort((a, b) => compareSortValues(a?.value, b?.value, pivotSort.direction));
    } else if (pivotSort.field === REPORT_PIVOT_ROW_TOTAL_KEY) {
      rowValues.sort((a, b) => {
        const aTotal = getPivotRowTotal(a);
        const bTotal = getPivotRowTotal(b);
        return compareSortValues(
          getDisplayNumericValue({
            value: aTotal,
            column: countColumn,
            mode: displayMode,
            denominator:
              percentageOf === PERCENTAGE_OF_ROW ? aTotal : pivotGrandTotal,
          }),
          getDisplayNumericValue({
            value: bTotal,
            column: countColumn,
            mode: displayMode,
            denominator:
              percentageOf === PERCENTAGE_OF_ROW ? bTotal : pivotGrandTotal,
          }),
          pivotSort.direction
        );
      });
    } else if (String(pivotSort.field).startsWith("col:")) {
      const sortColumnKey = String(pivotSort.field).slice(4);
      rowValues.sort((a, b) => {
        const aRow = pivotRowsByKey.get(a.key);
        const bRow = pivotRowsByKey.get(b.key);
        const sortColumn = columnValues.find((item) => item.key === sortColumnKey);
        const aVal = aRow?.cells.get(sortColumnKey) ?? 0;
        const bVal = bRow?.cells.get(sortColumnKey) ?? 0;
        return compareSortValues(
          getDisplayNumericValue({
            value: aVal,
            column: countColumn,
            mode: displayMode,
            denominator: sortColumn ? getCellDenominator(a, sortColumn) : undefined,
          }),
          getDisplayNumericValue({
            value: bVal,
            column: countColumn,
            mode: displayMode,
            denominator: sortColumn ? getCellDenominator(b, sortColumn) : undefined,
          }),
          pivotSort.direction
        );
      });
    }

    const getCellDrilldown = (rowValue, columnValue) => ({
      key: mappedDimensions[1]?.filterKey || rowDimension.key,
      value: rowValue,
      filters: [
        { module: mappedDimensions[0]?.module, key: mappedDimensions[0]?.filterKey || columnDimension.key, value: columnValue },
        { module: mappedDimensions[1]?.module, key: mappedDimensions[1]?.filterKey || rowDimension.key, value: rowValue },
      ],
    });

    return (
      <div className="h-full min-h-0 overflow-auto px-3 pb-3" style={{ position: "relative" }}>
        <div className="flex flex-wrap justify-between gap-3 sticky top-0 left-0 w-full bg-white z-10">
          <div>
            <div className="text-xl font-semibold ml-3 mt-3">{viz.title || `Visualization ${viz.id}`}</div>
            <div className="text-xs text-gray-500 ml-3 mb-3">
              Rows: {getDisplayColumnLabel(rowDimension)} / Columns: {getDisplayColumnLabel(columnDimension)}
            </div>
          </div>
          <div className="flex items-start gap-2 mr-3 mt-3 mb-3">
            {hasPercentage ? (
              <MeasureDisplayModeControls
                mode={displayMode}
                percentageOf={percentageOf}
                allowRow
                onChange={(patch) => updatePivotDisplaySetting(viz.id, patch)}
              />
            ) : null}
            <button
              type="button"
              aria-label={`Download ${viz.title || "pivot"}`}
              onClick={() => handlePivotDownload(viz)}
              className="rounded p-1 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
            >
              <Download size={20} />
            </button>
          </div>
        </div>

        <table className="min-w-full border-separate border-spacing-0">
          <thead>
            <tr className="bg-gray-50 text-left">
              <th className="sticky left-0 z-[2] bg-gray-50 px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                <button
                  type="button"
                  className="flex items-center gap-1 group"
                  onClick={() => updatePivotSort(viz.id, REPORT_TABLE_ROW_LABEL_KEY)}
                >
                  <span>{getDisplayColumnLabel(rowDimension)}</span>
                  {renderSortArrows(pivotSort, REPORT_TABLE_ROW_LABEL_KEY)}
                </button>
              </th>
              {columnValues.map((columnValue) => (
                <th
                  key={`${viz.id}-pivot-col-${columnValue.key}`}
                  className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200 text-right"
                >
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 group"
                    onClick={() => updatePivotSort(viz.id, `col:${columnValue.key}`)}
                  >
                    <span>{formatDimensionValue(columnValue.value)}</span>
                    {renderSortArrows(pivotSort, `col:${columnValue.key}`)}
                  </button>
                </th>
              ))}
              <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 text-right bg-gray-100">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 group"
                  onClick={() => updatePivotSort(viz.id, REPORT_PIVOT_ROW_TOTAL_KEY)}
                >
                  <span>Grand Total</span>
                  {renderSortArrows(pivotSort, REPORT_PIVOT_ROW_TOTAL_KEY)}
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {rowValues.length === 0 ? (
              <tr>
                <td className="px-4 py-3 text-sm text-gray-500" colSpan={columnValues.length + 2}>
                  No data available
                </td>
              </tr>
            ) : (
              <>
                {rowValues.map((rowValue) => {
                  const pivotRow = pivotRowsByKey.get(rowValue.key);
                  const rowTotal = getPivotRowTotal(rowValue);
                  return (
                    <tr key={`${viz.id}-pivot-row-${rowValue.key}`} className="hover:bg-gray-50">
                      <th className="sticky left-0 bg-white px-4 py-3 text-sm font-medium text-gray-900 text-left border-b border-gray-100">
                        {formatDimensionValue(rowValue.value)}
                      </th>
                      {columnValues.map((columnValue) => {
                        const value = pivotRow?.cells.get(columnValue.key) ?? 0;
                        const drilldown = getCellDrilldown(rowValue.value, columnValue.value);
                        const isDrillable = activeReport?.reportLevel !== "calls" || (
                          countColumn?.drilldownMode !== "exact_count" && Number(value) > 0 && drilldown.filters.every((filter) =>
                            filter.value !== null && filter.value !== undefined && filter.value !== "" &&
                            (filter.module !== "agent_feedback" || isAgentFeedbackDrilldownScore(filter.value)))
                        );
                        return (
                          <td
                            key={`${viz.id}-pivot-cell-${rowValue.key}-${columnValue.key}`}
                            onClick={isDrillable ? () => handleVisualizationSegmentClick(viz, drilldown) : undefined}
                            title={!isDrillable ? "Conversation drill-down is unavailable for this value" : undefined}
                            className={`px-4 py-3 text-sm text-right tabular-nums border-b border-gray-100 ${isDrillable ? "text-indigo-500 hover:text-indigo-700 underline cursor-pointer" : "text-gray-900"}`}
                          >
                            {formatPivotMeasure(value, getCellDenominator(rowValue, columnValue))}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-sm text-right tabular-nums font-semibold bg-gray-50 border-b border-gray-100">
                        {formatPivotMeasure(
                          rowTotal,
                          percentageOf === PERCENTAGE_OF_ROW ? rowTotal : pivotGrandTotal
                        )}
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-gray-100 font-semibold">
                  <th className="sticky left-0 bg-gray-100 px-4 py-3 text-sm text-gray-900 text-left">
                    Grand Total
                  </th>
                  {columnValues.map((columnValue) => {
                    const columnTotal = getPivotColumnTotal(columnValue);
                    return (
                      <td
                        key={`${viz.id}-pivot-total-${columnValue.key}`}
                        className="px-4 py-3 text-sm text-right tabular-nums text-gray-900"
                      >
                        {formatPivotMeasure(
                          columnTotal,
                          percentageOf === PERCENTAGE_OF_COLUMN
                            ? columnTotal
                            : pivotGrandTotal
                        )}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-sm text-right tabular-nums text-gray-900">
                    {formatPivotMeasure(pivotGrandTotal, pivotGrandTotal)}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderVisualizationPreview = (viz) => {
    const effectiveVisualizationType = getEffectiveVisualizationType(viz);
    if (effectiveVisualizationType === "header") {
      return (
        <div className="h-full min-h-0 w-full flex items-center px-4 py-3 bg-gray-100 overflow-auto">
          <div className="text-2xl font-semibold text-gray-900 break-words">
            {viz.title || "Header"}
          </div>
        </div>
      );
    }
    if (effectiveVisualizationType === "time_series") {
      return (
        <TimeSeriesVisualization
          visualization={viz}
          onDownload={() => handleDownload(viz.data, viz.title)}
          onPointClick={(point, measureColumn) =>
            handleTimeSeriesPointClick(viz, point, measureColumn)
          }
        />
      );
    }
    if (effectiveVisualizationType === PERIOD_COMPARISON_TABLE_VARIANT) {
      return (
        <PeriodComparisonTable
          visualization={viz}
          reportLevel={activeReport?.reportLevel}
          onCellClick={(scope) => handleExactCountCellClick(viz, scope)}
        />
      );
    }
    if (effectiveVisualizationType === "pie_chart") {
      const configuredMeasures = normalizeRestrictedSqlMeasures(viz?.measures);
      const displayMeasureId =
        viz?.displayMeasureId || viz?.data?.displayMeasureId || configuredMeasures[0]?.id;
      const displayMeasureFormat = configuredMeasures.find(
        (measure) => String(measure.id) === String(displayMeasureId)
      )?.format;
      const responseMeasureColumns = Array.isArray(viz?.data?.measureColumns)
        ? viz.data.measureColumns
        : [];
      const selectedMeasureColumn =
        responseMeasureColumns.find(
          (column) =>
            String(column?.measure_id ?? column?.measureId ?? column?.key) ===
            String(displayMeasureId)
        ) ||
        (Array.isArray(viz?.data?.columns)
          ? viz.data.columns.find(
              (column) => column?.role === "measure" || column?.kind === "measure"
            )
          : null);
      const isExactCountPie =
        selectedMeasureColumn?.drilldownMode === "exact_count" &&
        Boolean(viz?.drilldown?.context);
      const pieData = Array.isArray(viz?.data?.series)
        ? viz.data.series.map((item, index) => ({
            name: item?.name ?? "Unknown",
            value: Number(item?.value || 0),
            dimensionValue: item?.name,
            fill: item?.color || PIE_COLORS[index % PIE_COLORS.length],
          }))
        : PIE_SAMPLE.map((item) => ({
            name: item.label,
            value: item.value,
            fill: item.color,
          }));
      return (
        <div className="h-full min-h-0 overflow-y-auto">
          <PieChart2
            title={viz.title || `Visualization ${viz.id}`}
            pieData={pieData}
            colors={PIE_COLORS}
            handleDownload={() => {handleDownload(viz.data, viz.title)}}
            onSegmentClick={
              isExactCountPie
                ? (segment) => {
                    const dimensionKey = viz?.data?.primaryDimension?.key;
                    const payload = segment?.payload || {};
                    const dimensionValue = Object.prototype.hasOwnProperty.call(
                      payload,
                      "dimensionValue"
                    )
                      ? payload.dimensionValue
                      : Object.prototype.hasOwnProperty.call(
                            segment || {},
                            "dimensionValue"
                          )
                        ? segment.dimensionValue
                        : payload.name ?? segment?.name;
                    if (!dimensionKey || Number(segment?.value ?? segment?.payload?.value) <= 0) {
                      return;
                    }
                    handleExactCountCellClick(viz, {
                      measureId:
                        selectedMeasureColumn?.measure_id ??
                        selectedMeasureColumn?.measureId ??
                        displayMeasureId ??
                        selectedMeasureColumn?.key,
                      dimensions: [
                        {
                          key: dimensionKey,
                          label:
                            viz?.data?.primaryDimension?.label ||
                            resolveParameterKeyToDisplayLabel(dimensionKey) ||
                            humanizeFilterName(dimensionKey),
                          value: dimensionValue,
                        },
                      ],
                    });
                  }
                : configuredMeasures.length
                  ? undefined
                  : (segment) => handleVisualizationSegmentClick(viz, segment)
            }
            valueFormatter={
              configuredMeasures.length
                ? (value) => formatRestrictedSqlValue(value, displayMeasureFormat)
                : undefined
            }
          />
        </div>
      );
    }
    if (effectiveVisualizationType === PIVOT_TABLE_VARIANT) {
      return renderPivotTable(viz);
    }
    if (showCreateReportModal || !activeReport?.id) return renderStandardTable(viz);
    if (!viz.data) return <div role="status">Loading report…</div>;
    const scope = JSON.stringify([client_id, service_id, activeReport.id, activeExecutedReport?.generatedAt, viz.id,
      activeReport.visualizations]);
    return <PaginatedReportTable key={scope} visualization={viz} requestScope={scope}
      renderTable={renderStandardTable} loadPage={async (table, download, signal) => {
        const response = await server.customReportTable({
          client_id, service_id, report_id: activeReport.report_id || activeReport.id,
          visualization_id: viz.id, table, download, email, menu: REPORTS_MENU, submenu: REPORTS_SUBMENU,
        }, signal);
        const payload = await response.json();
        if (!response.ok) {
          const error = new Error(payload.message || "Unable to load table");
          error.status = response.status;
          throw error;
        }
        return payload;
      }} />;
  };

  useEffect(() => {
    const next = reportVisualizations.map((viz, index) => ({
      i: String(viz.id),
      ...normalizeVisualizationLayout(viz, index),
    }));
    setReportLayouts((prev) => {
      const nextLg = next;
      const nextMd = next;
    
      if (
        areLayoutItemsEqual(prev?.lg || [], nextLg) &&
        areLayoutItemsEqual(prev?.md || [], nextMd)
      ) {
        return prev;
      }
    
      return {
        ...prev,
        lg: nextLg,
        md: nextMd,
      };
    });
  }, [activeReportId, reportVisualizations]);

  useEffect(() => {
    setIsLayoutEditMode(false);
    setPendingReportLayouts({
      lg: reportLayouts?.lg || [],
      md: reportLayouts?.md || reportLayouts?.lg || [],
    });
  }, [activeReportId, reportLayouts]);

  const displayedReportLayouts = useMemo(() => {
    const base = isLayoutEditMode ? pendingReportLayouts : reportLayouts;
    if (isLayoutEditMode) return base;
    return {
      lg: (base?.lg || []).map((item) => ({
        ...item,
        static: true,
      })),
      md: (base?.md || base?.lg || []).map((item) => ({ ...item, static: true })),
    };
  }, [isLayoutEditMode, pendingReportLayouts, reportLayouts]);

  const handleReportLayoutChange = (layout, layouts) => {
    if (!isLayoutEditMode) return;
    const raw = Array.isArray(layout) ? layout : Array.isArray(layouts?.lg) ? layouts.lg : [];
    if (!Array.isArray(raw) || !activeReport?.id) return;
    const nextLayout = raw.map((item) => {
      const viz = reportVisualizations.find((v) => String(v.id) === String(item.i));
      if (viz?.visualizationType === "header") {
        return {
          ...item,
          x: 0,
          w: LAYOUT_COLS.lg,
          minW: LAYOUT_COLS.lg,
          maxW: LAYOUT_COLS.lg,
          resizeHandles: ["s"],
        };
      }
      return item;
    });
    const lgSame = areLayoutItemsEqual(pendingReportLayouts?.lg || [], nextLayout);
    const mdSame = areLayoutItemsEqual(pendingReportLayouts?.md || [], nextLayout);
    if (lgSame && mdSame) return;
    setPendingReportLayouts((prev) => ({ ...prev, lg: nextLayout, md: nextLayout }));
  };

  const startLayoutEdit = () => {
    if (!activeReport?.id) return;
    setPendingReportLayouts({ lg: reportLayouts?.lg || [], md: reportLayouts?.md || reportLayouts?.lg || [] });
    setIsLayoutEditMode(true);
  };

  const cancelLayoutEdit = () => {
    setPendingReportLayouts({ lg: reportLayouts?.lg || [], md: reportLayouts?.md || reportLayouts?.lg || [] });
    setIsLayoutEditMode(false);
  };

  const saveLayoutEdit = async () => {
    const chosenLayout = pendingReportLayouts?.lg || pendingReportLayouts?.md || [];
    if (!Array.isArray(chosenLayout) || !activeReport?.id || !client_id || !service_id) return;
    const updatedVisualizations = applyLayoutToVisualizations(activeReport.visualizations || [], chosenLayout, activeReport.reportLevel);
    setReportLayouts((prev) => ({ ...prev, lg: chosenLayout, md: chosenLayout }));
    setReports((prev) =>
      prev.map((report) =>
        report.id === activeReport.id ? { ...report, visualizations: updatedVisualizations } : report
      )
    );
    setExecutedReportsById((prev) => {
      if (!prev?.[activeReport.id]) return prev;
      return {
        ...prev,
        [activeReport.id]: {
          ...prev[activeReport.id],
          visualizations: applyLayoutToVisualizations(prev[activeReport.id].visualizations || [], chosenLayout, activeReport.reportLevel),
        },
      };
    });
    try {
      await server.post_custom_report_details({
        client_id,
        service_id,
        report_id: activeReport.report_id || activeReport.id,
        report_configuration: {
          name: activeReport.name || "Untitled Report",
          reportLevel: activeReport.reportLevel || "customers",
          visualizations: buildPersistableVisualizations(updatedVisualizations, activeReport.reportLevel),
        },
        menu: REPORTS_MENU,
        submenu: REPORTS_SUBMENU,
        email,
      });
      showToast("Report layout updated", "success");
      setIsLayoutEditMode(false);
    } catch (error) {
      showToast("Unable to persist visualization layout update", "error");
    }
  };

  const handleRefreshNow = async () => {
    if (!activeReport?.id || isHardRefreshing) return;
    setIsHardRefreshing(true);
    try {
      const refreshed = await triggerReportExecution(activeReport.id, true);
      if (refreshed) showToast("Report refreshed", "success");
    } finally {
      setIsHardRefreshing(false);
    }
  };

  const handleStaleDrilldown = async () => {
    const reportToRefresh = activeReport?.id;
    setIsSegmentModalOpen(false);
    setSelectedSegment(null);
    if (!reportToRefresh) return;
    setExecutedReportsById((prev) => {
      const next = { ...prev };
      delete next[reportToRefresh];
      return next;
    });
    const refreshed = await triggerReportExecution(reportToRefresh, true);
    if (refreshed) {
      showToast("The report changed and has been refreshed. Please click the updated count again.", "info");
    }
  };

  const openSettingsMenu = () => {
    if (settingsHideTimeoutRef.current) {
      clearTimeout(settingsHideTimeoutRef.current);
      settingsHideTimeoutRef.current = null;
    }
    setIsSettingsMenuOpen(true);
  };

  const closeSettingsMenuWithDelay = () => {
    if (settingsHideTimeoutRef.current) {
      clearTimeout(settingsHideTimeoutRef.current);
    }
    settingsHideTimeoutRef.current = setTimeout(() => {
      setIsSettingsMenuOpen(false);
    }, 1000);
  };

  const openDownloadMenu = () => {
    if (downloadMenuHideTimeoutRef.current) {
      clearTimeout(downloadMenuHideTimeoutRef.current);
      downloadMenuHideTimeoutRef.current = null;
    }
    setIsDownloadMenuOpen(true);
  };

  const closeDownloadMenuWithDelay = () => {
    if (downloadMenuHideTimeoutRef.current) {
      clearTimeout(downloadMenuHideTimeoutRef.current);
    }
    downloadMenuHideTimeoutRef.current = setTimeout(() => {
      setIsDownloadMenuOpen(false);
    }, 1000);
  };

  const handleDownloadReportScreenshot = async () => {
    const targetNode = reportGridContainerRef?.current;
    if (!targetNode) return;
    try {
      const gridNode = targetNode.querySelector(".react-grid-layout") || targetNode;
      const dataUrl = await toPng(gridNode, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#ffffff",
      });
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `${(activeReport?.name || "report").replace(/\s+/g, "_")}.png`;
      link.click();
    } catch (error) {
      showToast("Unable to download report screenshot", "error");
    }
  };

  const handleDownloadReportExcel = async () => {
    setIsDownloadMenuOpen(false);
    if (!activeReport?.id || !client_id || !service_id || !email) {
      showToast("Report not ready for download", "error");
      return;
    }
    try {
      const response = await server.downloadCustomReportExcel(client_id, service_id, email, activeReport.report_id || activeReport.id, REPORTS_MENU, REPORTS_SUBMENU);

      if (!response.ok) {
        throw new Error("Excel download failed");
      }
      const blob = await response.blob();
      const disposition = response.headers.get("Content-Disposition");
      let filename = `${(activeReport?.name || "report").replace(/\s+/g, "_")}.xlsx`;
      if (disposition && disposition.includes("filename=")) {
        const match = /filename\*?=(?:UTF-8'')?([^;\n]+)/i.exec(disposition);
        if (match && match[1]) {
          filename = decodeURIComponent(match[1].trim().replace(/^["']|["']$/g, ""));
        }
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
      showToast("Excel report downloaded", "success");
    } catch (error) {
      showToast("Unable to download Excel report", "error");
    }
  };

  const convertToLocalTime = (time) => {
    if (!time) return "NA";
    return new Date(time).toLocaleString();
  }

  const sendReportNow = async () => {
    if (!activeReport?.id || isSendingReportEmail) return;
    setIsSendingReportEmail(true);
    try {
      const response = await server.saveCustomReportConfiguration({
        client_id, service_id, report_id: activeReport.id,
        email, menu: REPORTS_MENU, submenu: REPORTS_SUBMENU, action: "send_now",
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Unable to queue report email");
      showToast("Report email queued for sending", "success");
    } catch (error) {
      showToast(error.message || "Unable to queue report email", "error");
    } finally {
      setIsSendingReportEmail(false);
    }
  };

  const saveReportConfiguration = async () => {
    if (!activeReport?.id) return;
    let res = await server.saveCustomReportConfiguration({
      client_id,
      service_id,
      report_id: activeReport.id,
      report_configuration: {
        emails: reportShareEmail,
        frequency: reportShareFrequency,
        status: reportShareStatus,
      },
      menu: REPORTS_MENU,
      submenu: REPORTS_SUBMENU,
      email: email,
    });
    if(res.status === 200){
      showToast("Report configuration saved successfully", "success");
    }else{
      let resp = await res.json();
      showToast(resp.message || "Error saving report configuration", "error");
    }
    setShowEmailModal(false);
  }
  const getReportConfiguration = async () => {
    setLoadingEmailConfiguration(true);
    let res = await server.getCustomReportConfiguration(client_id, service_id, email, REPORTS_MENU, REPORTS_SUBMENU, activeReport.id);
    if(res.status === 200){
      let resp = await res.json();
      setReportShareEmail(resp?.data?.emails);
      setReportShareFrequency(resp?.data?.frequency);
      setReportShareStatus(resp?.data?.status);
    }
    setLoadingEmailConfiguration(false);
  }

  const activeExecutionStatus = activeReportExecutionState?.status;
  const shouldShowExecutionPlaceholder = Boolean(
    activeReport &&
      !activeExecutedReport &&
      activeExecutionStatus !== "error"
  );
  const activeExecutionMessage =
    activeExecutionStatus === "processing"
      ? REPORT_PROCESSING_MESSAGE
      : activeReportExecutionState?.message || "Loading report...";

  return (
    <div className="flex flex-col font-sans bg-white h-full">
      <main className="flex-1 py-3 px-5 bg-gray-50">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 mb-1">Reports</h1>
            <p className="text-sm text-gray-500">
              Build and manage custom reports with reusable visualizations
            </p>
          </div>
          {canManageReports && <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openManageReportsModal}
              disabled={!reports.length}
              className="inline-flex h-9 items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Settings className="h-4 w-4" />
              Manage Reports
            </button>
            <button
              type="button"
              onClick={openCreateReportModal}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-white bg-indigo-600 hover:bg-indigo-700 h-9"
            >
              <Plus className="h-4 w-4" />
              Create Report
            </button>
          </div>}
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="border-b border-gray-200 pb-3 flex items-center justify-between gap-3">
            <div className="flex items-center overflow-x-auto gap-2">
              {reports.length > 0 && reports.map((report) => (
                <div
                  key={report.id}
                  className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap ${
                    activeReportId === report.id
                      ? "bg-indigo-100 text-indigo-700"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setActiveReportId(report.id)}>
                      {report.order}. {report.name}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {!isLayoutEditMode ? (
                <button
                  type="button"
                  disabled={!activeReport?.id || isHardRefreshing}
                  onClick={handleRefreshNow}
                  className="inline-flex h-8 items-center justify-center gap-1.5 rounded-md bg-gray-100 px-3 text-xs font-medium text-gray-700 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="Hard refresh report"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${isHardRefreshing ? "animate-spin" : ""}`} />
                  {isHardRefreshing ? "Refreshing..." : "Refresh"}
                </button>
              ) : null}
              {!isLayoutEditMode ? (
                <div
                  className="relative"
                  onMouseEnter={openDownloadMenu}
                  onMouseLeave={closeDownloadMenuWithDelay}
                >
                  <button
                    type="button"
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200"
                    aria-label="Download report"
                    disabled={!activeReport?.id}
                    onClick={() => setIsDownloadMenuOpen((prev) => !prev)}
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  {isDownloadMenuOpen && (
                    <div className="absolute right-0 top-8 z-20 min-w-[200px] bg-white border border-gray-200 rounded-md shadow-lg p-2">
                      <button
                        type="button"
                        disabled={!activeReport?.id}
                        onClick={() => {
                          setIsDownloadMenuOpen(false);
                          handleDownloadReportScreenshot();
                        }}
                        className="w-full text-left px-2 py-1.5 text-sm text-gray-600 rounded hover:bg-gray-100 disabled:opacity-50"
                      >
                        PNG format
                      </button>
                      <button
                        type="button"
                        disabled={!activeReport?.id}
                        onClick={handleDownloadReportExcel}
                        className="w-full text-left px-2 py-1.5 text-sm text-gray-600 rounded hover:bg-gray-100 disabled:opacity-50"
                      >
                        Excel format
                      </button>
                    </div>
                  )}
                </div>
              ) : null}
              {canManageReports && <button
                aria-label="Schedule report"
                className="bg-gray-100 rounded-md h-8 w-8 flex items-center justify-center text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                disabled={!activeReport?.id || isLayoutEditMode}
                onClick={() => {
                  setShowEmailModal(true);
                  getReportConfiguration();
                }}
              >
                <Mail className="h-4 w-4" />
              </button>}
              {canManageReports && (!isLayoutEditMode ? (
                <div
                  className="relative"
                  onMouseEnter={openSettingsMenu}
                  onMouseLeave={closeSettingsMenuWithDelay}
                >
                  <button
                    type="button"
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200"
                    aria-label="Edit report"
                    onClick={() => setIsSettingsMenuOpen((prev) => !prev)}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  {isSettingsMenuOpen && (
                  <div className="absolute right-0 top-8 z-20 min-w-[220px] bg-white border border-gray-200 rounded-md shadow-lg p-2">
                    <button
                      type="button"
                      disabled={!activeReport?.id}
                      onClick={() => openEditReportModal(activeReport.id)}
                      className="w-full text-left px-2 py-1.5 text-sm text-gray-600 rounded hover:bg-gray-100 disabled:opacity-50"
                    >
                      Edit Visualization
                    </button>
                    <button
                      type="button"
                      disabled={!activeReport?.id}
                      onClick={startLayoutEdit}
                      className="w-full text-left px-2 py-1.5 text-sm text-gray-600 rounded hover:bg-gray-100 disabled:opacity-50"
                    >
                      Edit Layout
                    </button>
                  </div>
                  )}
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={cancelLayoutEdit}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveLayoutEdit}
                    className="px-3 py-1.5 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                  >
                    Save
                  </button>
                </>
              ))}
            </div>
          </div>
          {
                isLayoutEditMode && (
                  <p className="text-sm text-gray-500 text-end" style={{fontSize: "10px"}}>You can drag and resize the visualizations to your desired layout from bottom right corner of the visualization.</p>
                )
              }

          <div className="pt-4">
            {reportDataLoading || shouldShowExecutionPlaceholder ? (
              <div className="h-64 flex flex-col items-center justify-center gap-3 text-gray-500">
                <RefreshCw className="h-5 w-5 animate-spin" />
                <span>{reportDataLoading ? "Loading report..." : activeExecutionMessage}</span>
              </div>
            ) : activeExecutionStatus === "error" && !activeExecutedReport ? (
              <div className="h-64 flex items-center justify-center text-red-600">
                {activeReportExecutionState?.message || "Unable to execute report"}
              </div>
            ) : (
              <div>
               
                {!reportVisualizations.length ? (
                  <div className="h-44 rounded-md bg-gray-50 flex items-center justify-center text-gray-500 text-sm">
                    No visualizations yet. Create a report to test.
                  </div>
                ) : (
                  <div ref={reportGridContainerRef} className="w-full min-w-0">
                    {reportGridMounted && (
                      <ResponsiveReactGridLayout
                        className="layout"
                        width={reportGridWidth}
                        breakpoints={LAYOUT_BREAKPOINTS}
                        cols={LAYOUT_COLS}
                        useCSSTransforms={false}
                        compactType={null}
                        preventCollision={true}
                        rowHeight={18}
                        margin={[4, 4]}
                        containerPadding={[0, 0]}
                        isDraggable={isLayoutEditMode}
                        isResizable={isLayoutEditMode}
                        onBreakpointChange={setCurrentBreakpoint}
                        onLayoutChange={handleReportLayoutChange}
                        layouts={displayedReportLayouts}
                      >
                        {reportVisualizations.map((viz) => (
                          <div key={String(viz.id)} className="h-full min-h-0 min-w-0 overflow-hidden rounded-md border border-gray-100 bg-white">
                            {renderVisualizationPreview(viz)}
                          </div>
                        ))}
                      </ResponsiveReactGridLayout>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {showManageReportsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div className="flex max-h-[82vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border bg-white shadow-xl">
            <div className="flex items-start justify-between border-b bg-gray-50 px-5 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Manage Reports</h2>
                <p className="mt-1 text-sm text-gray-500">
                  This order is shared by everyone using this client and project.
                </p>
              </div>
              <button
                type="button"
                className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
                disabled={isSavingReportOrder || isDeletingReport}
                onClick={() => setShowManageReportsModal(false)}
                aria-label="Close manage reports"
              >
                <FaTimes />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-5">
              <p className="mb-3 text-sm text-gray-600">
                Drag reports into position, or use the arrow buttons. Save Order applies the
                arrangement for this client and project.
              </p>
              <div className="space-y-2">
                {managedReports.map((report, index) => (
                  <div
                    key={report.id}
                    draggable={!isSavingReportOrder && !isDeletingReport}
                    onDragStart={(event) => {
                      event.dataTransfer.effectAllowed = "move";
                      event.dataTransfer.setData("text/plain", String(report.id));
                      setDraggedReportId(report.id);
                    }}
                    onDragOver={(event) => handleManagedReportDragOver(event, report.id)}
                    onDragEnd={() => setDraggedReportId(null)}
                    className={`flex items-center gap-3 rounded-md border px-3 py-3 transition-colors ${
                      String(draggedReportId) === String(report.id)
                        ? "border-indigo-300 bg-indigo-50"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <GripVertical
                      className="h-5 w-5 shrink-0 cursor-grab text-gray-400"
                      aria-hidden
                    />
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-gray-100 text-xs font-semibold text-gray-600">
                      {index + 1}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-800">
                      {report.name}
                    </span>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        disabled={index === 0 || isSavingReportOrder || isDeletingReport}
                        onClick={() => moveManagedReport(index, index - 1)}
                        className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-30"
                        aria-label={`Move ${report.name} up`}
                        title="Move up"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        disabled={
                          index === managedReports.length - 1 ||
                          isSavingReportOrder ||
                          isDeletingReport
                        }
                        onClick={() => moveManagedReport(index, index + 1)}
                        className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-30"
                        aria-label={`Move ${report.name} down`}
                        title="Move down"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        disabled={isSavingReportOrder || isDeletingReport}
                        onClick={() => setReportPendingDelete(report)}
                        className="ml-1 rounded-md p-1.5 text-red-500 hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-40"
                        aria-label={`Delete ${report.name}`}
                        title="Delete report"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t bg-gray-50 px-5 py-4">
              <button
                type="button"
                disabled={isSavingReportOrder || isDeletingReport}
                onClick={() => setShowManageReportsModal(false)}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!isReportOrderDirty || isSavingReportOrder || isDeletingReport}
                onClick={handleSaveReportOrder}
                className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSavingReportOrder && <RefreshCw className="h-4 w-4 animate-spin" />}
                {isSavingReportOrder ? "Saving..." : "Save Order"}
              </button>
            </div>
          </div>
        </div>
      )}

      {reportPendingDelete && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 px-4"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div className="w-full max-w-md rounded-lg border bg-white p-5 shadow-xl">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600">
                <Trash2 className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete report?</h3>
                <p className="mt-2 text-sm text-gray-600">
                  <span className="font-medium text-gray-800">{reportPendingDelete.name}</span>{" "}
                  and its email schedule will be permanently deleted. This cannot be undone.
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                disabled={isDeletingReport}
                onClick={() => setReportPendingDelete(null)}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isDeletingReport}
                onClick={handleDeleteReport}
                className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isDeletingReport && <RefreshCw className="h-4 w-4 animate-spin" />}
                {isDeletingReport ? "Deleting..." : "Delete Report"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateReportModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex items-start justify-center pt-8 z-50"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div className="bg-white w-[95vw] max-w-8xl max-h-[95vh] overflow-y-auto rounded-lg shadow-lg border">
            <div className="flex items-center justify-between px-5 py-4 border-b bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-900">
                {reportModalMode === "edit" ? "Edit Report" : "Create Report"}
              </h2>
              <button type="button" className="p-2" onClick={closeReportModal}>
                <FaTimes />
              </button>
            </div>

            <div className="p-5 space-y-5 h-[75vh] overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Report Name</label>
                  <input
                    type="text"
                    value={formState.reportName}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, reportName: event.target.value }))
                    }
                    placeholder="Enter report name"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Report Level</label>
                  <select
                    aria-label="Report Level"
                    value={formState.reportLevel}
                    disabled={reportModalMode === "edit"}
                    onChange={(event) => {
                      const reportLevel = event.target.value;
                      invalidateBuilderPreview();
                      fieldCatalogRequestRef.current += 1;
                      setServerFieldCatalog([]);
                      setRestrictedSqlErrors([]);
                      setFiltersPanelVisualizationId(null);
                      setFormState((prev) => ({ ...prev, reportLevel,
                        visualizations: prev.visualizations.map((viz, index) => viz.visualizationType === "header" ? viz : ({
                          ...getDefaultVisualization(viz.id, index, reportLevel),
                          title: viz.title, layout: viz.layout,
                        })),
                      }));
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md disabled:bg-gray-100"
                  >
                    <option value="customers">Customer</option>
                    <option value="calls">Conversation</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {reportModalMode === "edit" ? "Create another report to use a different level." : "Applies to every section. Changing level resets section fields and filters."}
                  </p>
                </div>
              </div>

              <div className="">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-gray-800">Add New Section</div>
                  <button
                    type="button"
                    onClick={addVisualization}
                    className="inline-flex items-center gap-2 text-xs px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Section
                  </button>
                </div>
                <div className="mb-2">
                  <div className="flex items-center gap-2 overflow-x-auto">
                    {formState.visualizations.map((visualization, vizIndex) => (
                      <button
                        key={`viz-tab-${visualization.id}`}
                        type="button"
                        onClick={() => setActiveVisualizationTabId(visualization.id)}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs whitespace-nowrap ${
                          activeVisualizationTabId === visualization.id
                            ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                            : "border-gray-200 text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <span>{visualization.title}</span>
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(event) => {
                            event.stopPropagation();
                            duplicateVisualization(visualization.id);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              duplicateVisualization(visualization.id);
                            }
                          }}
                          className="opacity-70 hover:opacity-100"
                          title="Duplicate visualization"
                        >
                          <Copy className="h-3 w-3" />
                        </span>
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(event) => {
                            event.stopPropagation();
                            removeVisualization(visualization.id);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              removeVisualization(visualization.id);
                            }
                          }}
                          className="opacity-70 hover:opacity-100"
                          title="Remove visualization"
                        >
                          <Trash2 className="h-3 w-3" />
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {(() => {
                  const activeVisualization =
                    formState.visualizations.find((item) => item.id === activeVisualizationTabId) ||
                    formState.visualizations[0];
                  if (!activeVisualization) return null;
                  const activeVizIndex = formState.visualizations.findIndex(
                    (item) => item.id === activeVisualization.id
                  );
                  const selectedPairsForForm = visualizationToOrderedPairs(activeVisualization);
                  const aggregationParam =
                    selectedPairsForForm[0]?.value || activeVisualization.aggregationParameter || "";
                  const aggregationOptions = getAggregationOptions(aggregationParam);
                  const appliedFilterCount = getAppliedFilterCount(activeVisualization.advancedFilters);
                  const activeEffectiveVisualizationType = getEffectiveVisualizationType(activeVisualization);
                  const contentType = activeEffectiveVisualizationType === "header" ? "Heading" : "Visualization";
                  const usesRestrictedSql = hasRestrictedSqlMeasures(activeVisualization);
                  const isPeriodComparison =
                    activeEffectiveVisualizationType === PERIOD_COMPARISON_TABLE_VARIANT;
                  const isLegacyPeriodComparison =
                    isPeriodComparison &&
                    isLegacyPeriodComparisonConfig(activeVisualization?.periodComparison);
                  const isGenericPeriodComparison =
                    isPeriodComparison && !isLegacyPeriodComparison;
                  const activePeriodComparisonAxes = isGenericPeriodComparison
                    ? getPeriodComparisonAxes(
                        activeVisualization?.periodComparison,
                        selectedPairsForForm
                      )
                    : { rows: [], columns: [] };
                  const isBoundedStandardVariant =
                    !usesRestrictedSql &&
                    (activeEffectiveVisualizationType === "time_series" ||
                      isGenericPeriodComparison ||
                      (activeEffectiveVisualizationType === PIVOT_TABLE_VARIANT &&
                        activeVisualization?.pivotConfig &&
                        typeof activeVisualization.pivotConfig === "object"));
                  const metadataParameterOptions = METADATA_BASE_PARAMETER_OPTIONS.filter(
                    (item) =>
                      !Array.isArray(item.reportLevels) ||
                      item.reportLevels.includes(formState.reportLevel)
                  );
                  const fixedAgentFeedbackParameters = formState.reportLevel === "calls" ? [
                    { value: "fatal", label: "Fatal" },
                    ...(dummyAdvancedFilterData.agent_feedback_parameters || []).filter((item) =>
                      !["fatal", "overall_score"].includes(normalizeLogicalFieldName(item?.value ?? item))
                    ),
                  ] : [];
                  const unifiedParameterOptions = [
                    ...fixedAgentFeedbackParameters.map((item) => ({
                      label: `${item?.label ?? item} (Agent Feedback)`, module: "agent_feedback", paramValue: item?.value ?? item,
                    })),
                    ...metadataParameterOptions.map((item) => ({
                      label: `${item.label} (Metadata)`,
                      module: "metadata",
                      paramValue: item.value,
                    })),
                    ...(formState.reportLevel === "calls" ? [{ label: "Conversation Potential", value: "conversation_potential" }] : LEAD_QUALITY_BASE_PARAMETER_OPTIONS).map((item) => ({
                      label: `${item.label} (Lead Quality)`,
                      module: "lead_quality",
                      paramValue: item.value,
                    })),
                    ...getBusinessInsightKeys().map((key) => ({
                      label: `${key} (Business Insight)`,
                      module: "business_insight",
                      paramValue: key,
                    })),
                    ...getCustomModuleKeys().map((key) => ({
                      label: `${key} (Custom Module)`,
                      module: "custom_module",
                      paramValue: key,
                    })),
                  ];
                  const selectedPairCount = selectedPairsForForm.length;
                  const fallbackRestrictedSqlFieldOptions = buildRestrictedSqlFieldOptions({
                    agentFeedback: formState.reportLevel === "calls" ? [
                      { value: "overall_score", label: "Overall Score" },
                      ...fixedAgentFeedbackParameters,
                    ] : [],
                    metadata: [
                      { value: "customer_id", label: "Customer ID" },
                      ...metadataParameterOptions.filter((item) => item.value !== "customer_id"),
                      { value: formState.reportLevel === "calls" ? "conversation_potential" : "customer_potential", label: "Lead Quality" },
                      ...(formState.reportLevel === "calls" ? [{ value: "audio_duration", label: "Audio Duration" }] : []),
                    ],
                    businessInsights: getBusinessInsightKeys().map((key) => ({
                      value: normalizeLogicalFieldName(key),
                      label: key,
                    })),
                    customModules: getCustomModuleKeys().map((key) => ({
                      value: normalizeLogicalFieldName(key),
                      label: key,
                    })),
                  });
                  const restrictedSqlFieldOptions = serverFieldCatalog.length
                    ? serverFieldCatalog.map((field) => ({
                        value: field.value,
                        label: field.dataType ? `${field.label} (${field.dataType})` : field.label,
                        dataType: field.dataType,
                      }))
                    : fallbackRestrictedSqlFieldOptions;
                  const activeRestrictedSqlErrors = restrictedSqlErrors.filter((error) => {
                    if (error?.visualizationId !== null && error?.visualizationId !== undefined) {
                      return String(error.visualizationId) === String(activeVisualization.id);
                    }
                    if (Number.isInteger(error?.visualizationIndex)) {
                      return error.visualizationIndex === activeVizIndex;
                    }
                    return true;
                  });
                  const activeTimeSeriesErrors = activeRestrictedSqlErrors.filter((error) =>
                    String(error?.field || "").startsWith("timeSeries")
                  );
                  const activePivotErrors = activeRestrictedSqlErrors.filter((error) =>
                    String(error?.field || "").startsWith("pivotConfig") || error?.field === "parameters"
                  );
                  const activePeriodComparisonErrors = activeRestrictedSqlErrors.filter((error) =>
                    String(error?.field || "").startsWith("periodComparison")
                  );
                  const visualizationOptions = getCustomReportVisualizationOptions(
                    selectedPairCount,
                    formState.reportLevel
                  );
                  const activePivotConfig = getPivotConfig(activeVisualization, selectedPairsForForm);

                  return (
                    <div className=" space-y-4">

                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Section type</label>
                        <ModernTimeDropdown
                          width="w-full"
                          placeholder="Select type"
                          value={contentType}
                          options={["Visualization", "Heading"]}
                          onChange={(event) => {
                            const nextType = event.target.value;
                            if (nextType === "Heading") {
                              updateVisualization(activeVisualization.id, {
                                visualizationType: "header",
                                variant: undefined,
                                pivotConfig: undefined,
                                timeSeries: undefined,
                                periodComparison: undefined,
                                measures: undefined,
                                displayMeasureId: undefined,
                                layout: getHeaderLayout(
                                  activeVizIndex,
                                  activeVisualization?.layout?.y,
                                  activeVisualization?.layout?.h
                                ),
                              });
                              return;
                            }
                            const nextVisualizationType = getVisualizationTypeForPairCount(
                              visualizationToOrderedPairs(activeVisualization).length
                            );
                            updateVisualization(activeVisualization.id, {
                              visualizationType: nextVisualizationType,
                              variant: undefined,
                              pivotConfig: undefined,
                              timeSeries: undefined,
                              periodComparison: undefined,
                              layout: getDefaultLayout(activeVizIndex),
                            });
                          }}
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          {contentType === "Heading" ? "Header text" : "Visualization name"}
                        </label>
                        <input
                          type="text"
                          value={activeVisualization.title || ""}
                          onChange={(event) =>
                            updateVisualization(activeVisualization.id, {
                              title: event.target.value,
                            })
                          }
                          placeholder={`Visualization ${activeVizIndex + 1}`}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                        />
                      </div>

                      {contentType === "Visualization" && (
                        <>
                          <div className="">
                            <div className="text-sm font-medium text-gray-700 mb-2">1. Filters</div>
                            {formState.reportLevel === "calls" && (
                              <div className="mb-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                                <ModernTimeDropdown placeholder="Conversation Time" value={activeVisualization.advancedFilters.call_time}
                                  options={CONVERSATION_TIME_FILTER_OPTIONS}
                                  onChange={(event) => updateAdvancedFilters(activeVisualization.id, { ...activeVisualization.advancedFilters, call_time: event.target.value })} />
                                <ModernTimeDropdown placeholder="Employee" value={activeVisualization.advancedFilters.employee_id}
                                  options={dummyAdvancedFilterData.employees || []}
                                  onChange={(event) => updateAdvancedFilters(activeVisualization.id, { ...activeVisualization.advancedFilters, employee_id: event.target.value })} />
                                <ModernTimeDropdown placeholder="Team" value={activeVisualization.advancedFilters.team_name}
                                  options={dummyAdvancedFilterData.team_names || []}
                                  onChange={(event) => updateAdvancedFilters(activeVisualization.id, { ...activeVisualization.advancedFilters, team_name: event.target.value })} />
                                <ModernTimeDropdown placeholder="Record Type" value={activeVisualization.advancedFilters.record_type}
                                  options={CONVERSATION_RECORD_TYPE_OPTIONS}
                                  onChange={(event) => updateAdvancedFilters(activeVisualization.id, { ...activeVisualization.advancedFilters, record_type: event.target.value })} />
                                <input
                                  type="text"
                                  aria-label="Conversation ID filter"
                                  placeholder="Conversation ID"
                                  value={activeVisualization.advancedFilters.conversation_id || ""}
                                  onChange={(event) => updateAdvancedFilters(activeVisualization.id, { ...activeVisualization.advancedFilters, conversation_id: event.target.value })}
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                                />
                                {activeVisualization.advancedFilters.call_time === "Custom Range" && (
                                  <ModernDateRangePicker startDate={activeVisualization.advancedFilters.call_time_start}
                                    endDate={activeVisualization.advancedFilters.call_time_end}
                                    rangeType={activeVisualization.advancedFilters.call_time_range_type || "between"}
                                    maxRangeDays={90} placeholder="Conversation Time Range"
                                    onChange={({ startDate, endDate, rangeType }) => updateAdvancedFilters(activeVisualization.id, {
                                      ...activeVisualization.advancedFilters, call_time_start: startDate, call_time_end: endDate,
                                      call_time_range_type: rangeType || "between",
                                    })} />
                                )}
                              </div>
                            )}
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-xs text-gray-500">Applied filters: {appliedFilterCount}</div>
                              <button
                                type="button"
                                onClick={() => setFiltersPanelVisualizationId(activeVisualization.id)}
                                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100"
                              >
                                <Filter className="h-3.5 w-3.5" />
                                Choose Filters
                              </button>
                            </div>
                          </div>

                          <div className="">
                            <div className="text-sm font-medium text-gray-700 mb-2">
                              {isPeriodComparison
                                ? "2. Period comparison"
                                : activeEffectiveVisualizationType === "time_series"
                                  ? "2. Time series"
                                  : "2. Parameters"}
                            </div>
                            {isPeriodComparison ? (
                              <>
                                <PeriodComparisonSettings
                                  config={activeVisualization.periodComparison}
                                  reportLevel={formState.reportLevel}
                                  fieldOptions={restrictedSqlFieldOptions}
                                  errors={activePeriodComparisonErrors}
                                  showMeasurePlacement={
                                    isGenericPeriodComparison &&
                                    ((Array.isArray(activeVisualization?.measures) &&
                                      activeVisualization.measures.length > 1) ||
                                      normalizePeriodComparisonConfig(
                                        activeVisualization?.periodComparison,
                                        formState.reportLevel
                                      ).measurePlacement === "rows")
                                  }
                                  onChange={(periodComparison) =>
                                    updateVisualization(activeVisualization.id, {
                                      periodComparison,
                                    })
                                  }
                                />
                                {isGenericPeriodComparison ? (
                                  <div className="mt-3">
                                    <label className="block text-xs text-gray-500 mb-1">
                                      Grouping parameters (optional, up to {MAX_RESTRICTED_SQL_GROUPING_PARAMETERS})
                                    </label>
                                    <ModernTimeDropdown
                                      width="w-full"
                                      isMulti
                                      placeholder="Select optional grouping parameters"
                                      value={unifiedParameterOptions
                                        .filter((opt) =>
                                          selectedPairsForForm.some(
                                            (pair) =>
                                              pair.module === opt.module &&
                                              pair.value === String(opt.paramValue)
                                          )
                                        )
                                        .map((opt) => opt.label)}
                                      options={unifiedParameterOptions.map((opt) => opt.label)}
                                      onChange={(event) => {
                                        const rawValue = event.target.value;
                                        const nextLabels = Array.isArray(rawValue)
                                          ? rawValue
                                          : [rawValue].filter(Boolean);
                                        const nextPairs = unifiedParameterOptions
                                          .filter((opt) => nextLabels.includes(opt.label))
                                          .map((opt) => ({
                                            module: opt.module,
                                            value: String(opt.paramValue),
                                          }));
                                        if (
                                          nextPairs.length >
                                          MAX_RESTRICTED_SQL_GROUPING_PARAMETERS
                                        ) {
                                          showToast(
                                            `Visualizations support up to ${MAX_RESTRICTED_SQL_GROUPING_PARAMETERS} grouping parameters`,
                                            "error"
                                          );
                                          return;
                                        }
                                        updateVisualization(activeVisualization.id, {
                                          periodComparison: {
                                            ...normalizePeriodComparisonConfig(
                                              activeVisualization?.periodComparison,
                                              formState.reportLevel
                                            ),
                                            columns: getPeriodComparisonAxes(
                                              activeVisualization?.periodComparison,
                                              nextPairs
                                            ).columns,
                                          },
                                          orderedParameterPairs: nextPairs,
                                          parameters: orderedPairsToParametersObject(nextPairs),
                                          ...deriveLegacyBaseFieldsFromPairs(nextPairs),
                                        });
                                      }}
                                    />
                                    <PivotShelvesEditor
                                      pivotConfig={activePeriodComparisonAxes}
                                      fieldOptions={unifiedParameterOptions}
                                      errors={activePeriodComparisonErrors.filter((error) =>
                                        String(error?.field || "").startsWith(
                                          "periodComparison.columns"
                                        )
                                      )}
                                      movePair={movePeriodComparisonPair}
                                      rowLimit={MAX_PERIOD_COMPARISON_DIMENSIONS}
                                      columnLimit={MAX_PERIOD_COMPARISON_DIMENSIONS}
                                      title="Period comparison layout"
                                      description={`Place every selected parameter on Rows or Columns. Use at most ${MAX_PERIOD_COMPARISON_DIMENSIONS} parameters in total. Time periods remain the top header.`}
                                      onChange={(axes) => {
                                        const canonicalPairs = [
                                          ...axes.rows,
                                          ...axes.columns,
                                        ];
                                        updateVisualization(activeVisualization.id, {
                                          periodComparison: {
                                            ...normalizePeriodComparisonConfig(
                                              activeVisualization?.periodComparison,
                                              formState.reportLevel
                                            ),
                                            columns: axes.columns,
                                          },
                                          orderedParameterPairs: canonicalPairs,
                                          parameters:
                                            orderedPairsToParametersObject(canonicalPairs),
                                          ...deriveLegacyBaseFieldsFromPairs(canonicalPairs),
                                        });
                                      }}
                                    />
                                  </div>
                                ) : null}
                              </>
                            ) : activeEffectiveVisualizationType === "time_series" ? (
                              <TimeSeriesSettings
                                visualization={activeVisualization}
                                errors={activeTimeSeriesErrors}
                                onChange={(patch) => updateVisualization(activeVisualization.id, patch)}
                              />
                            ) : (
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">
                                  Grouping parameters (up to {getGroupingParameterLimit(activeVisualization)})
                                </label>
                                <ModernTimeDropdown
                                  width="w-full"
                                  isMulti
                                  placeholder="Select one or more parameters"
                                  value={unifiedParameterOptions
                                    .filter((opt) =>
                                      selectedPairsForForm.some(
                                        (p) => p.module === opt.module && p.value === String(opt.paramValue)
                                      )
                                    )
                                    .map((opt) => opt.label)}
                                  options={unifiedParameterOptions.map((opt) => opt.label)}
                                  onChange={(event) => {
                                    const rawValue = event.target.value;
                                    const nextLabels = Array.isArray(rawValue) ? rawValue : [rawValue].filter(Boolean);
                                    const nextPairs = unifiedParameterOptions
                                      .filter((opt) => nextLabels.includes(opt.label))
                                      .map((opt) => ({ module: opt.module, value: String(opt.paramValue) }));
                                    if (nextPairs.length > getGroupingParameterLimit(activeVisualization)) {
                                      showToast(
                                        `Visualizations support up to ${getGroupingParameterLimit(activeVisualization)} grouping parameters`,
                                        "error"
                                      );
                                      return;
                                    }
                                    const derived = deriveLegacyBaseFieldsFromPairs(nextPairs);
                                    const remainsPivot =
                                      activeEffectiveVisualizationType === PIVOT_TABLE_VARIANT && nextPairs.length >= 2;
                                    const nextPivotConfig = remainsPivot
                                      ? reconcilePivotConfig(activePivotConfig, nextPairs)
                                      : undefined;
                                    updateVisualization(activeVisualization.id, {
                                      orderedParameterPairs: nextPairs,
                                      parameters: orderedPairsToParametersObject(nextPairs),
                                      ...derived,
                                      visualizationType: remainsPivot
                                        ? "table"
                                        : getVisualizationTypeForPairCount(nextPairs.length),
                                      variant: remainsPivot ? PIVOT_TABLE_VARIANT : undefined,
                                      pivotConfig: nextPivotConfig,
                                    });
                                  }}
                                />
                              </div>
                            )}
                          </div>

                          {!isLegacyPeriodComparison ? (
                            <div className="">
                            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                              <div>
                                <div className="text-sm font-medium text-gray-700">3. Values</div>
                                <div className="mt-0.5 text-[11px] text-gray-500">
                                  Use the existing aggregation or opt in to validated SQL measures.
                                </div>
                              </div>
                              <div className="inline-flex overflow-hidden rounded-md border border-gray-200">
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateVisualization(activeVisualization.id, {
                                      measures: undefined,
                                      displayMeasureId: undefined,
                                      ...(activeEffectiveVisualizationType === "time_series" ||
                                      isGenericPeriodComparison ||
                                      (activeEffectiveVisualizationType === PIVOT_TABLE_VARIANT &&
                                        activeVisualization?.pivotConfig)
                                        ? {
                                            aggregationParameter: "customer_id",
                                            aggregationOperator: "count",
                                          }
                                        : {}),
                                    })
                                  }
                                  className={`px-3 py-1.5 text-xs ${
                                    !usesRestrictedSql
                                      ? "bg-indigo-50 text-indigo-700"
                                      : "bg-white text-gray-600 hover:bg-gray-50"
                                  }`}
                                >
                                  Standard
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!usesRestrictedSql) {
                                      const measure = createRestrictedSqlMeasure(1, formState.reportLevel);
                                      updateVisualization(activeVisualization.id, {
                                        measures: [measure],
                                        displayMeasureId: measure.id,
                                      });
                                    }
                                    if (!serverFieldCatalog.length) {
                                      loadRestrictedSqlFieldCatalog(formState.reportLevel || "customers");
                                    }
                                  }}
                                  className={`px-3 py-1.5 text-xs ${
                                    usesRestrictedSql
                                      ? "bg-indigo-50 text-indigo-700"
                                      : "bg-white text-gray-600 hover:bg-gray-50"
                                  }`}
                                >
                                  SQL measures
                                </button>
                              </div>
                            </div>

                            {usesRestrictedSql ? (
                              <RestrictedSqlMeasuresEditor
                                reportLevel={formState.reportLevel}
                                visualizationId={activeVisualization.id}
                                measures={activeVisualization.measures}
                                displayMeasureId={activeVisualization.displayMeasureId}
                                visualizationType={activeEffectiveVisualizationType}
                                fieldOptions={restrictedSqlFieldOptions}
                                errors={activeRestrictedSqlErrors}
                                onChange={(measures, displayMeasureId) =>
                                  updateVisualization(activeVisualization.id, {
                                    measures,
                                    displayMeasureId,
                                  })
                                }
                              />
                            ) : (
                              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Parameter name</label>
                                  <ModernTimeDropdown width="w-full" value={formState.reportLevel === "calls" ? "Conversation ID" : "Customer ID"} isDisabled={true} />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Operator</label>
                                  <ModernTimeDropdown
                                    width="w-full"
                                    placeholder="Select operator"
                                    value={isBoundedStandardVariant
                                      ? "COUNT"
                                      : String(activeVisualization.aggregationOperator || "").toUpperCase()}
                                    options={(isBoundedStandardVariant || formState.reportLevel === "calls" ? ["count"] : aggregationOptions).map((option) =>
                                      option.toUpperCase()
                                    )}
                                    isDisabled={isBoundedStandardVariant}
                                    onChange={(event) =>
                                      updateVisualization(activeVisualization.id, {
                                        aggregationOperator: String(event.target.value || "").toLowerCase(),
                                      })
                                    }
                                  />
                                </div>
                              </div>
                            )}
                            </div>
                          ) : null}

                          <div className="">
                            <div className="text-sm font-medium text-gray-700 mb-2">
                              {isLegacyPeriodComparison ? "3. Visualization" : "4. Visualization"}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {visualizationOptions.map((option) => {
                                const isActive = option.value === activeEffectiveVisualizationType;
                                const Icon =
                                  option.value === "pie_chart"
                                    ? PieChart
                                    : option.value === "time_series"
                                      ? LineChart
                                      : Table;
                                return (
                                  <button
                                    key={`${activeVisualization.id}-${option.value}`}
                                    type="button"
                                    onClick={() => {
                                      if (option.value === "time_series") {
                                        const timeSeries = normalizeTimeSeriesConfig(activeVisualization?.timeSeries);
                                        const advancedFilters = ensureTimeSeriesRangeFilter(
                                          activeVisualization?.advancedFilters,
                                          timeSeries.field,
                                          { clearImplicitLastInteraction: true }
                                        );
                                        updateVisualization(activeVisualization.id, {
                                          visualizationType: "time_series",
                                          variant: undefined,
                                          pivotConfig: undefined,
                                          periodComparison: undefined,
                                          timeSeries,
                                          advancedFilters,
                                          orderedParameterPairs: [],
                                          parameters: {},
                                          aggregationParameter: "customer_id",
                                          aggregationOperator: "count",
                                          ...deriveLegacyBaseFieldsFromPairs([]),
                                        });
                                        return;
                                      }
                                      if (option.value === PIVOT_TABLE_VARIANT) {
                                        const pivotConfig = activeVisualization?.pivotConfig
                                          ? reconcilePivotConfig(activePivotConfig, selectedPairsForForm)
                                          : buildDefaultPivotConfig(selectedPairsForForm);
                                        updateVisualization(activeVisualization.id, {
                                          visualizationType: "table",
                                          variant: PIVOT_TABLE_VARIANT,
                                          pivotConfig,
                                          periodComparison: undefined,
                                          timeSeries: undefined,
                                          ...(!usesRestrictedSql
                                            ? {
                                                aggregationParameter: "customer_id",
                                                aggregationOperator: "count",
                                              }
                                            : {}),
                                        });
                                        return;
                                      }
                                      if (option.value === PERIOD_COMPARISON_TABLE_VARIANT) {
                                        const periodComparison = normalizePeriodComparisonConfig(
                                          activeVisualization?.periodComparison ||
                                            getDefaultPeriodComparisonConfig(formState.reportLevel),
                                          formState.reportLevel
                                        );
                                        const useLegacyPeriodComparison =
                                          isLegacyPeriodComparisonConfig(periodComparison);
                                        const periodPairs = useLegacyPeriodComparison
                                          ? []
                                          : selectedPairsForForm;
                                        const periodAxes = getPeriodComparisonAxes(
                                          periodComparison,
                                          periodPairs
                                        );
                                        const advancedFilters = {
                                          ...(activeVisualization?.advancedFilters || {}),
                                        };
                                        if (
                                          advancedFilters.lastinteraction === "Last 7 Days" &&
                                          !advancedFilters.lastinteraction_start &&
                                          !advancedFilters.lastinteraction_end &&
                                          !advancedFilters.lastinteraction_range_type
                                        ) {
                                          advancedFilters.lastinteraction = "";
                                        }
                                        if (formState.reportLevel === "calls") {
                                          delete advancedFilters.call_time;
                                          delete advancedFilters.call_time_start;
                                          delete advancedFilters.call_time_end;
                                          delete advancedFilters.call_time_range_type;
                                        }
                                        updateVisualization(activeVisualization.id, {
                                          visualizationType: "table",
                                          variant: PERIOD_COMPARISON_TABLE_VARIANT,
                                          periodComparison: useLegacyPeriodComparison
                                            ? periodComparison
                                            : {
                                                ...periodComparison,
                                                columns: periodAxes.columns,
                                              },
                                          pivotConfig: undefined,
                                          timeSeries: undefined,
                                          ...(useLegacyPeriodComparison
                                            ? {
                                                measures: undefined,
                                                displayMeasureId: undefined,
                                                aggregationParameter: undefined,
                                                aggregationOperator: undefined,
                                              }
                                            : usesRestrictedSql
                                              ? {
                                                  aggregationParameter: undefined,
                                                  aggregationOperator: undefined,
                                                }
                                              : {
                                                  aggregationParameter: reportCountField(formState.reportLevel),
                                                  aggregationOperator: "count",
                                                }),
                                          orderedParameterPairs: periodPairs,
                                          parameters: orderedPairsToParametersObject(periodPairs),
                                          advancedFilters,
                                          ...deriveLegacyBaseFieldsFromPairs(periodPairs),
                                        });
                                        if (!serverFieldCatalog.length) {
                                          loadRestrictedSqlFieldCatalog(
                                            formState.reportLevel || "customers"
                                          );
                                        }
                                        return;
                                      }
                                      updateVisualization(activeVisualization.id, {
                                        visualizationType: option.value,
                                        variant: undefined,
                                        pivotConfig: undefined,
                                        timeSeries: undefined,
                                        periodComparison: undefined,
                                      });
                                    }}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${
                                      isActive
                                        ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                                        : "border-gray-200 text-gray-700 hover:bg-gray-50"
                                    }`}
                                  >
                                    <Icon className="h-4 w-4" />
                                    {option.label}
                                  </button>
                                );
                              })}
                            </div>
                            <ReportExclusionFilters
                              value={activeVisualization.advancedFilters?.reportExclusions || []}
                              fields={restrictedSqlFieldOptions}
                              onChange={(reportExclusions) => updateVisualization(activeVisualization.id, {
                                advancedFilters: { ...activeVisualization.advancedFilters, reportExclusions },
                              })}
                            />
                            {activeEffectiveVisualizationType === PIVOT_TABLE_VARIANT ? (
                              <PivotShelvesEditor
                                pivotConfig={activePivotConfig}
                                fieldOptions={unifiedParameterOptions}
                                errors={activePivotErrors}
                                onChange={(pivotConfig) => {
                                  const canonicalPairs = [...pivotConfig.rows, ...pivotConfig.columns];
                                  updateVisualization(activeVisualization.id, {
                                    pivotConfig,
                                    orderedParameterPairs: canonicalPairs,
                                    parameters: orderedPairsToParametersObject(canonicalPairs),
                                    ...deriveLegacyBaseFieldsFromPairs(canonicalPairs),
                                  });
                                }}
                              />
                            ) : null}
                            {activeEffectiveVisualizationType === "table" && selectedPairsForForm.length > 1 ? (
                              <div className="mt-3">
                                <label className="block text-xs text-gray-500 mb-2">
                                  Column order (drag to set column order — left to right)
                                </label>
                                <ul className="space-y-1.5 rounded-md border border-gray-200 bg-gray-50/80 p-2">
                                  {selectedPairsForForm.map((pair, pairIndex) => {
                                    const pairLabel =
                                      unifiedParameterOptions.find(
                                        (opt) => opt.module === pair.module && String(opt.paramValue) === pair.value
                                      )?.label || pair.value;
                                    return (
                                      <li
                                        key={`${activeVisualization.id}-${pair.module}-${pair.value}-${pairIndex}`}
                                        draggable
                                        onDragStart={(e) => {
                                          e.dataTransfer.setData("text/plain", String(pairIndex));
                                          e.dataTransfer.effectAllowed = "move";
                                        }}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => {
                                          e.preventDefault();
                                          const from = Number(e.dataTransfer.getData("text/plain"), 10);
                                          if (Number.isNaN(from)) return;
                                          const next = reorderParameterPairsAt(selectedPairsForForm, from, pairIndex);
                                          const derived = deriveLegacyBaseFieldsFromPairs(next);
                                          updateVisualization(activeVisualization.id, {
                                            orderedParameterPairs: next,
                                            parameters: orderedPairsToParametersObject(next),
                                            ...derived,
                                          });
                                        }}
                                        className="flex items-center gap-2 rounded border border-transparent bg-white px-2 py-1.5 text-xs text-gray-800 shadow-sm cursor-grab active:cursor-grabbing"
                                      >
                                        <GripVertical className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
                                        <span className="truncate">{pairLabel}</span>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            ) : null}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>

              {builderPreview?.result?.visualizations?.length ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-semibold text-gray-800">Unsaved report preview</div>
                      <div className="text-[11px] text-gray-500">
                        Limited preview data. Refreshed {convertToLocalTime(builderPreview.refreshedAt)}
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={isPreviewing}
                      onClick={() => handlePreviewReport(true)}
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 ${isPreviewing ? "animate-spin" : ""}`} />
                      {isPreviewing ? "Refreshing..." : "Refresh Preview"}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                    {builderPreview.result.visualizations.map((previewVisualization, index) => {
                      const configured = formState.visualizations[index] || {};
                      const preview = unwrapVisualizationForUi({
                        ...configured,
                        ...previewVisualization,
                        id: previewVisualization?.id ?? configured.id ?? `preview-${index + 1}`,
                        title: previewVisualization?.title ?? configured.title,
                        measures: previewVisualization?.measures ?? configured.measures,
                        displayMeasureId:
                          previewVisualization?.displayMeasureId ?? configured.displayMeasureId,
                      });
                      return (
                        <div key={String(preview.id)} className="h-[25rem] overflow-hidden rounded-md border border-gray-200 bg-white">
                          {renderVisualizationPreview(preview)}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="px-5 py-3 border-t bg-white flex items-center justify-end gap-2 rounded-b-lg">
              <button
                type="button"
                className="px-4 py-2 text-sm border border-gray-300 rounded-md"
                onClick={closeReportModal}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isPreviewing || isSubmitting}
                className="inline-flex items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
                onClick={() => handlePreviewReport(Boolean(builderPreview))}
              >
                <RefreshCw className={`h-4 w-4 ${isPreviewing ? "animate-spin" : ""}`} />
                {isPreviewing ? "Generating preview..." : builderPreview ? "Refresh Preview" : "Preview Report"}
              </button>
              <button
                type="button"
                disabled={isSubmitting || isPreviewing}
                className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-70"
                onClick={handleSaveReport}
              >
                {isSubmitting ? "Saving..." : reportModalMode === "edit" ? "Update Report" : "Save Report"}
              </button>
            </div>
          </div>
        </div>
      )}
      {
        showEmailModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style={{background: "rgba(0,0,0,0.5)"}}>
            <div className="bg-white rounded-lg shadow-lg p-4 w-full max-w-md" style={{ maxHeight: "90vh", overflowY: "auto", maxWidth: "500px" }}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">Schedule Custom Report</h3>
                <button onClick={() => {setShowEmailModal(false)}} className="text-gray-500 hover:text-gray-700">
                  <FaTimes style={{color: "slategray"}}/>
                </button>
              </div>
              {!loadingEmailConfiguration && <div className="mb-4" style={{height: "40vh"}}>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Report configuration for <span className="font-bold text-gray-700">{activeReport?.name}</span></h3>
                <div className="mb-4">
                  <label className="block mb-1 font-medium text-gray-700 text-sm">Email</label>
                  <input type="email" value={reportShareEmail} onChange={(e)=>setReportShareEmail(e.target.value)} placeholder="Enter comma separated emails" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md" />
                </div>
                <div className="mb-4">
                  <label className="block mb-1 font-medium text-gray-700 text-sm">Frequency <p style={{fontSize: "10px", color: "gray"}}>Report will be sent between 8AM to 9AM on the selected day</p></label>
                  <ModernTimeDropdown width='w-full' placeholder="Select Frequency" value={reportShareFrequency} onChange={(e)=>setReportShareFrequency(e.target.value)} options={['Daily', 'Weekly', 'Fortnightly', 'Monthly']}/>
                </div>
                <div className="mb-4">
                  <label className="block mb-1 font-medium text-gray-700 text-sm">Status</label>
                  <ModernTimeDropdown width='w-full' placeholder="Select Status" value={reportShareStatus} onChange={(e)=>setReportShareStatus(e.target.value)} options={['Active', 'Inactive']}/>
                </div>
              </div>}
              {loadingEmailConfiguration && (
                <div className="flex justify-center items-center" style={{ height: "25vh", flexDirection: "column" }}>
                  <div className="loader"></div>
                  <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                </div>
              )}
              <p className="mb-3 text-xs text-gray-500">Send now uses saved recipients and report filters. Save any changes first. Scheduled delivery is unchanged.</p>
              <div className="flex space-x-3 justify-end">
                <button
                  type="button"
                  onClick={sendReportNow}
                  disabled={loadingEmailConfiguration || isSendingReportEmail}
                  className="px-4 py-2 border border-indigo-300 rounded text-indigo-700 text-base disabled:opacity-50"
                >
                  {isSendingReportEmail ? "Sending…" : "Send Test Email Now"}
                </button>
                <button 
                  onClick={() => {setShowEmailModal(false)}}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 text-base"
                >
                  Cancel
                </button>
                <button 
                  className="px-4 py-2 rounded text-white font-medium text-base disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: 'rgb(79 70 229)' }}
                  onClick={saveReportConfiguration}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )
      }

      {isSegmentModalOpen && (
        <div
          className="fixed inset-0 z-[1001]"
          style={{ background: "rgba(0, 0, 0, 0.5)" }}
        >
          <div
            className="fixed left-[2%] top-[3.5%] flex h-[93%] w-[96%] flex-col overflow-hidden rounded-lg bg-white p-1 shadow-sm"
            style={{ zIndex: 999999999 }}
          >
            <button
              type="button"
              aria-label={activeReport?.reportLevel === "calls" ? "Close conversation records" : "Close customer records"}
              className="absolute right-4 top-4 z-10 rounded-md p-1 text-slate-500 hover:bg-gray-100 hover:text-slate-700"
              onClick={() => setIsSegmentModalOpen(false)}
            >
              <FaTimes style={{ color: "slategray" }} />
            </button>
            <div className="min-h-0 flex-1 overflow-hidden">
              <ReportRecords
                segment={selectedSegment}
                serviceID={service_id}
                clientID={client_id}
                type="modal-open"
                hide_filters={false}
                onStaleDrilldown={handleStaleDrilldown}
              />
            </div>
          </div>
        </div>
      )}

      {filtersPanelVisualizationId !== null && (
        <ReportFiltersPanel
          isOpen={true}
          onClose={() => setFiltersPanelVisualizationId(null)}
          multiSelectFields={multiSelectFields}
          advancedFilters={
            formState.visualizations.find((item) => item.id === filtersPanelVisualizationId)
              ?.advancedFilters || getDefaultAdvancedFilters(formState.reportLevel)
          }
          setAdvancedFilters={(next) => {
            const current = formState.visualizations.find(
              (item) => item.id === filtersPanelVisualizationId
            )?.advancedFilters;
            updateAdvancedFilters(
              filtersPanelVisualizationId,
              formState.reportLevel === "calls"
                ? preserveConversationReportQuickFilters(current, next)
                : next
            );
          }}
          handleAdvancedInputChange={() => {}}
          dummyAdvancedFilterData={dummyAdvancedFilterData}
          expandedSections={expandedSections}
          setExpandedSections={setExpandedSections}
          updateBusinessInsights={() => {}}
          getBusinessInsightKeys={getBusinessInsightKeys}
          getBusinessInsightValues={getBusinessInsightValues}
          handleConcernCategoryChange={() => {}}
          getConcernCategoryKeys={getConcernCategoryKeys}
          getConcernSubcategoriesForCategory={getConcernSubcategoriesForCategory}
          handleQueryCategoryChange={() => {}}
          getQueryCategoryKeys={getQueryCategoryKeys}
          getQuerySubcategoriesForCategory={getQuerySubcategoriesForCategory}
          handleCustomModuleParameterChange={() => {}}
          getCustomModuleKeys={getCustomModuleKeys}
          getCustomModuleValues={getCustomModuleValues}
          handleDispositionChange={() => {}}
          getDispositionKeys={getDispositionKeys}
          getSubdispositionsForDisposition={getSubdispositionsForDisposition}
          crmStageKey="crm_lead_stage"
          crmSubStageKey="crm_lead_sub_stage"
          showDispositionSection={true}
          showCrmLeadCreatedAt={true}
          showBaseFiltersTab={true}
          showCampaignBatchId={formState.reportLevel === "calls"}
          showNeedsReview={formState.reportLevel === "calls"}
        />
      )}
    </div>
  );
};

export {
  buildAdvancedFiltersPayload,
  buildPersistableVisualizations,
  getDefaultAdvancedFilters,
  normalizeVisualizationsWithLayout,
  visualizationToOrderedPairs,
};

export default ReportsDashboard;
